export const WAEC_CHEMISTRY_LEARN_SS2 = [
  {
    topic: 'Separation of Mixtures',
    topicCode: 'SS2-CHEM-01',
    module: 'Physical Chemistry',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro">A <span class="learn-keyword">mixture</span> contains two or more substances combined physically, each retaining its properties. Separation techniques exploit differences in physical properties such as solubility, boiling point, particle size, density, and magnetism.</div><h3 class="learn-subheading">1. Filtration</h3><p class="learn-p">Separates an insoluble solid from a liquid. Liquid (filtrate) passes through filter paper; solid (residue) is trapped. Example: sand from water.</p><h3 class="learn-subheading">2. Evaporation and Crystallisation</h3><p class="learn-p">Evaporation removes solvent to leave dissolved solid. Crystallisation is preferred — controlled cooling forms pure crystals without decomposition. Example: CuSO₄ crystals from solution.</p><h3 class="learn-subheading">3. Distillation and Fractional Distillation</h3><p class="learn-p">Distillation separates a soluble solid from its solvent or a liquid from a non-volatile dissolved substance (e.g., salt water → pure water). Fractional distillation separates miscible liquids with different boiling points using a fractionating column. Example: ethanol/water separation; crude oil fractions.</p><h3 class="learn-subheading">4. Chromatography</h3><p class="learn-p">Separates components based on differential affinity for stationary vs mobile phase. Rf = distance moved by substance ÷ distance moved by solvent front (value 0–1).</p><h3 class="learn-subheading">5. Other Techniques</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Technique</th><th>Basis</th><th>Example</th></tr></thead><tbody><tr><td>Magnetic separation</td><td>Magnetism</td><td>Iron from sand</td></tr><tr><td>Sublimation</td><td>Sublimation property</td><td>Iodine or NH₄Cl from mixture</td></tr><tr><td>Solvent extraction</td><td>Differential solubility</td><td>Iodine from water using hexane</td></tr><tr><td>Centrifugation</td><td>Density (spinning)</td><td>Blood cells from plasma</td></tr></tbody></table></div><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Rf Formula:</strong> Rf = distance by spot ÷ distance by solvent front. Always 0–1.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Filtration (insoluble solid). Crystallisation (pure solid). Distillation (solid dissolved in liquid). Fractional distillation (miscible liquids). Chromatography (Rf values). Sublimation (sublimable solid). Magnetic separation (magnetic component).</div>',
    questions: [
      {
        q: 'Which technique separates an insoluble solid from a liquid?',
        o: ['Distillation', 'Filtration', 'Crystallisation', 'Chromatography'],
        a: 1,
        e: 'Filtration separates insoluble solids from liquids. The liquid (filtrate) passes through filter paper; the solid (residue) stays on top. Example: filtering sand from water.',
        h: 'Insoluble solid from liquid → Filtration.',
        yr: 'SS2',
      },
      {
        q: 'The Rf value in paper chromatography is calculated as:',
        o: [
          'Solvent distance ÷ spot distance',
          'Spot distance ÷ solvent front distance',
          'Spot distance × solvent distance',
          'Mass of spot ÷ mass of solvent',
        ],
        a: 1,
        e: 'Rf = distance moved by the spot ÷ distance moved by the solvent front. Rf is always between 0 and 1.',
        h: 'Rf = spot distance ÷ solvent front distance.',
        yr: 'SS2',
      },
      {
        q: 'Fractional distillation differs from simple distillation in that it:',
        o: [
          'Uses a higher temperature',
          'Uses a fractionating column to separate liquids with close but different boiling points',
          'Only collects one fraction',
          'Works for immiscible liquids only',
        ],
        a: 1,
        e: 'Fractional distillation uses a fractionating column — vapours condense and re-vaporise repeatedly, each time becoming richer in the lower-boiling component, allowing separation of close-boiling liquids.',
        h: 'Fractional distillation = fractionating column for liquids with close boiling points.',
        yr: 'SS2',
      },
      {
        q: 'Crystallisation is preferred over evaporation to dryness because:',
        o: [
          'It is faster',
          'Slow cooling allows pure crystals to form without thermal decomposition',
          'It needs less equipment',
          'It works only for coloured salts',
        ],
        a: 1,
        e: 'Evaporation to dryness uses high heat that can decompose heat-sensitive compounds. Crystallisation involves partial evaporation then slow cooling — pure crystals form without decomposition.',
        h: 'Crystallisation = slow cooling → pure crystals without decomposition.',
        yr: 'SS2',
      },
      {
        q: 'Which technique separates iron filings from sand?',
        o: ['Filtration', 'Magnetic separation', 'Distillation', 'Chromatography'],
        a: 1,
        e: 'Iron is magnetic; sand is not. A magnet attracts iron filings, leaving sand behind. Magnetic separation exploits the difference in magnetic properties.',
        h: 'Iron (magnetic) from sand (non-magnetic) → Magnetic separation.',
        yr: 'SS2',
      },
      {
        q: 'Sublimation separates iodine from sand because:',
        o: [
          'Iodine dissolves in sand',
          'Iodine converts directly from solid to vapour on heating while sand does not',
          'Sand dissolves in iodine vapour',
          'Both sublime at different rates',
        ],
        a: 1,
        e: 'Iodine sublimes — converts directly from solid to purple vapour without passing through liquid phase. The vapour condenses on a cold surface above, leaving non-sublimable sand behind.',
        h: 'Iodine sublimes (solid→vapour); sand does not → Sublimation.',
        yr: 'SS2',
      },
      {
        q: 'In paper chromatography, a component that travels furthest has:',
        o: [
          'Smallest Rf',
          'Largest Rf (most soluble in mobile phase, least attracted to stationary phase)',
          'Rf greater than 1',
          'Same Rf as all others',
        ],
        a: 1,
        e: 'Travelling furthest gives highest Rf (closest to 1). The component is most soluble in the mobile phase (solvent) and least retained by the stationary phase (paper).',
        h: 'Furthest travel = highest Rf value.',
        yr: 'SS2',
      },
      {
        q: 'The apparatus used to condense vapour to liquid in distillation is:',
        o: ['The flask', 'The Liebig condenser', 'The fractionating column', 'The thermometer'],
        a: 1,
        e: 'The Liebig condenser has cold water flowing around an outer jacket, cooling the vapour inside back to liquid which drips into the collection flask.',
        h: 'Condenser in distillation = Liebig condenser.',
        yr: 'SS2',
      },
      {
        q: 'To separate ethanol (bp 78°C) from water (bp 100°C), the best method is:',
        o: ['Filtration', 'Crystallisation', 'Fractional distillation', 'Magnetic separation'],
        a: 2,
        e: 'Ethanol and water are miscible with close boiling points. Fractional distillation using a fractionating column separates them — ethanol vapour condenses first at ~78°C.',
        h: 'Miscible liquids with different boiling points → Fractional distillation.',
        yr: 'SS2',
      },
      {
        q: 'The filtrate in a filtration experiment is:',
        o: [
          'Solid on filter paper',
          'Liquid that passes through the filter paper',
          'The filter paper itself',
          'The funnel',
        ],
        a: 1,
        e: 'Filtrate = the liquid that passes through the filter paper. Residue = solid trapped on the filter paper.',
        h: 'Filtrate = liquid through filter paper. Residue = solid on paper.',
        yr: 'SS2',
      },
      {
        q: 'Solvent extraction uses a separating funnel when:',
        o: [
          'Two liquids are miscible',
          'A solute is more soluble in one immiscible liquid than another',
          'The mixture is magnetic',
          'Two liquids have very different boiling points',
        ],
        a: 1,
        e: 'Solvent extraction exploits differential solubility. Example: iodine is more soluble in hexane than water — shaking aqueous iodine with hexane transfers iodine to the hexane layer.',
        h: 'Solvent extraction: differential solubility; separating funnel for immiscible liquids.',
        yr: 'SS2',
      },
      {
        q: 'Which method separates crude oil into petrol, kerosene, diesel?',
        o: ['Simple distillation', 'Filtration', 'Fractional distillation', 'Crystallisation'],
        a: 2,
        e: 'Crude oil is a mixture of hydrocarbons with different boiling points. Industrial fractional distillation in a fractionating tower separates them into fractions.',
        h: 'Crude oil separation = industrial fractional distillation.',
        yr: 'SS2',
      },
      {
        q: 'In a chromatography experiment, a spot moves 4.5 cm and the solvent front moves 9 cm. Rf = ?',
        o: ['2.0', '0.25', '4.5', '0.5'],
        a: 3,
        e: 'Rf = 4.5 ÷ 9 = 0.5.',
        h: 'Rf = 4.5 ÷ 9 = 0.5.',
        yr: 'SS2',
      },
      {
        q: 'Decantation involves:',
        o: [
          'Filtering through paper',
          'Carefully pouring off liquid from above a settled solid',
          'Spinning at high speed',
          'Evaporating solvent completely',
        ],
        a: 1,
        e: 'Decantation is carefully pouring liquid off from above a settled solid sediment — a simple preliminary separation before filtration.',
        h: 'Decantation = carefully pouring liquid from above settled solid.',
        yr: 'SS2',
      },
      {
        q: 'Centrifugation is used instead of filtration when:',
        o: [
          'Particles are too large',
          'Particles are too fine to settle or filter quickly — spinning forces denser particles outward',
          'The mixture is homogeneous',
          'Temperature is very high',
        ],
        a: 1,
        e: 'Centrifugation spins at high speed, generating centrifugal force that pushes denser particles to the bottom faster than gravity — used for fine colloids and biological separations.',
        h: 'Centrifugation = spinning separates fine particles too small for normal filtration.',
        yr: 'SS2',
      },
      {
        q: 'Which mixture is BEST separated by simple distillation?',
        o: [
          'Ethanol and water',
          'Salt dissolved in water',
          'Iron filings and sand',
          'Coloured dyes in ink',
        ],
        a: 1,
        e: 'Simple distillation is ideal for separating a dissolved solid from its liquid solvent. Salt water is distilled to collect pure water — water vaporises and is condensed; salt remains.',
        h: 'Simple distillation = solid dissolved in liquid (e.g., salt water → pure water).',
        yr: 'SS2',
      },
      {
        q: 'Ammonium chloride can be separated from sand by sublimation because:',
        o: [
          'NH₄Cl is magnetic',
          'NH₄Cl sublimes on heating while sand remains solid',
          'Sand dissolves in NH₄Cl',
          'Both sublime at the same temperature',
        ],
        a: 1,
        e: 'NH₄Cl sublimes on gentle heating — converts directly from white solid to vapour, which condenses as white crystals on a cooler surface. Sand does not sublime.',
        h: 'NH₄Cl sublimes; sand does not → Sublimation.',
        yr: 'SS2',
      },
      {
        q: 'In chromatography, the stationary phase is:',
        o: [
          'The moving solvent',
          'The fixed medium (paper or silica plate)',
          'The spots of mixture',
          'The baseline pencil line',
        ],
        a: 1,
        e: 'The stationary phase is the fixed medium — silica gel on TLC plate or cellulose in paper chromatography. The mobile phase is the moving solvent.',
        h: 'Stationary phase = fixed medium (paper/silica). Mobile phase = moving solvent.',
        yr: 'SS2',
      },
      {
        q: 'Why must chromatography spots be placed ABOVE the solvent level?',
        o: [
          'To make spots more visible',
          'To prevent spots dissolving directly into bulk solvent before separation occurs',
          'To make solvent evaporate faster',
          'To increase Rf',
        ],
        a: 1,
        e: 'If spots are below solvent level, they dissolve directly into the solvent and wash away before separation can occur. Spots above the solvent are carried by capillary action at different rates.',
        h: 'Spots above solvent: prevents direct dissolution; allows capillary separation.',
        yr: 'SS2',
      },
      {
        q: 'What property must a substance have for sublimation to be useful?',
        o: [
          'Soluble in water',
          'Converts directly from solid to vapour without passing through liquid state',
          'Denser than other components',
          'Magnetic',
        ],
        a: 1,
        e: 'A sublimable substance converts directly from solid to gas when heated (and back to solid when cooled) without passing through liquid. Examples: iodine, NH₄Cl, naphthalene.',
        h: 'Sublimation requires: solid → vapour without liquid phase (sublimable property).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Water',
    topicCode: 'SS2-CHEM-02',
    module: 'Physical Chemistry',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro">Water (H₂O) is the most important liquid on Earth. This topic covers its composition, properties, hardness, and treatment.</div><h3 class="learn-subheading">1. Composition of Water</h3><p class="learn-p">Electrolysis of water: 2H₂O → 2H₂ + O₂. H₂:O₂ volume ratio = 2:1. Synthesis: 2H₂ + O₂ → 2H₂O.</p><h3 class="learn-subheading">2. Properties</h3><ul class="learn-list"><li>Boiling point 100°C; freezing point 0°C</li><li>Maximum density at 4°C</li><li>Universal solvent; amphoteric</li><li>High specific heat capacity; extensive hydrogen bonding</li></ul><h3 class="learn-subheading">3. Hardness of Water</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Type</th><th>Cause</th><th>Removal</th></tr></thead><tbody><tr><td>Temporary</td><td>Ca(HCO₃)₂ and Mg(HCO₃)₂</td><td>Boiling; Clark\'s method (Ca(OH)₂)</td></tr><tr><td>Permanent</td><td>CaSO₄ and MgSO₄</td><td>Washing soda (Na₂CO₃); ion exchange; distillation</td></tr></tbody></table></div><h3 class="learn-subheading">4. Water Treatment</h3><p class="learn-p">Steps: Sedimentation → Coagulation (alum, Al₂(SO₄)₃) → Filtration (sand/gravel) → Chlorination (kills pathogens).</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Key:</strong> Temporary hardness removed by boiling (bicarbonate decomposes). Permanent cannot be boiled away — needs washing soda, ion exchange, or distillation.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> H₂:O₂ = 2:1 from electrolysis. Temporary = Ca(HCO₃)₂, removed by boiling/lime. Permanent = CaSO₄, removed by Na₂CO₃ or ion exchange. Treatment: sedimentation→coagulation (alum)→filtration→chlorination.</div>',
    questions: [
      {
        q: 'Electrolysis of water produces hydrogen and oxygen in what volume ratio?',
        o: ['1:2', '2:1', '1:1', '3:1'],
        a: 1,
        e: '2H₂O → 2H₂ + O₂. Two moles H₂ produced per mole O₂. H₂:O₂ = 2:1 by volume. Hydrogen at cathode; oxygen at anode.',
        h: '2H₂O → 2H₂ + O₂ → H₂:O₂ = 2:1 by volume.',
        yr: 'SS2',
      },
      {
        q: 'What causes temporary hardness in water?',
        o: [
          'Dissolved CaSO₄',
          'Dissolved Ca(HCO₃)₂ and Mg(HCO₃)₂',
          'Dissolved MgCl₂',
          'Dissolved NaCl',
        ],
        a: 1,
        e: 'Temporary hardness is caused by dissolved calcium and magnesium bicarbonates, formed when acidic rainwater dissolves limestone: CaCO₃ + H₂O + CO₂ → Ca(HCO₃)₂.',
        h: 'Temporary hardness = Ca(HCO₃)₂ and Mg(HCO₃)₂.',
        yr: 'SS2',
      },
      {
        q: 'Temporary hardness is removed by boiling because:',
        o: [
          'Heat dissolves calcium ions',
          'Ca(HCO₃)₂ decomposes: Ca(HCO₃)₂ → CaCO₃↓ + H₂O + CO₂, removing Ca²⁺',
          'Boiling increases CaSO₄ solubility',
          'Steam carries away Ca²⁺',
        ],
        a: 1,
        e: 'Boiling decomposes calcium bicarbonate: Ca(HCO₃)₂ → CaCO₃(s)↓ + H₂O + CO₂. CaCO₃ precipitates as limescale; water loses Ca²⁺ and becomes soft.',
        h: 'Boiling: Ca(HCO₃)₂ → CaCO₃↓ + H₂O + CO₂.',
        yr: 'SS2',
      },
      {
        q: 'Which method removes BOTH temporary and permanent hardness?',
        o: ['Boiling', "Clark's method", 'Ion exchange resin', 'Filtration'],
        a: 2,
        e: "Ion exchange resins exchange Ca²⁺ and Mg²⁺ for Na⁺ or H⁺, removing all hardness (both types). Boiling and Clark's method only remove temporary hardness.",
        h: 'Ion exchange removes BOTH types of hardness.',
        yr: 'SS2',
      },
      {
        q: 'What chemical causes coagulation during water treatment?',
        o: ['NaCl', 'Alum (Al₂(SO₄)₃)', 'CaCO₃', 'NaOH'],
        a: 1,
        e: 'Alum (aluminium sulphate) dissolves and forms gelatinous Al(OH)₃ that traps and clumps fine suspended particles, making them settle (sedimentation).',
        h: 'Coagulation = alum (Al₂(SO₄)₃) traps fine particles.',
        yr: 'SS2',
      },
      {
        q: 'Permanent hardness is caused by:',
        o: [
          'Ca(HCO₃)₂',
          'Dissolved CaSO₄ and MgSO₄',
          'Dissolved sodium salts',
          'Rainwater dissolving gypsum only',
        ],
        a: 1,
        e: "Permanent hardness is caused by CaSO₄, MgSO₄, CaCl₂, MgCl₂ — sulphates and chlorides of Ca and Mg that don't decompose on boiling.",
        h: 'Permanent hardness = CaSO₄ and MgSO₄ (stable to boiling).',
        yr: 'SS2',
      },
      {
        q: "Clark's method softens water by adding:",
        o: ['Na₂CO₃', 'Slaked lime Ca(OH)₂', 'Chlorine gas', 'Alum'],
        a: 1,
        e: "Clark's method adds Ca(OH)₂: Ca(OH)₂ + Ca(HCO₃)₂ → 2CaCO₃↓ + 2H₂O. CaCO₃ precipitates, removing Ca²⁺. Removes temporary hardness only.",
        h: "Clark's method = Ca(OH)₂ → precipitates CaCO₃ → removes temporary hardness.",
        yr: 'SS2',
      },
      {
        q: 'Why is chlorine added during water treatment?',
        o: [
          'To remove hardness ions',
          'To kill bacteria and harmful microorganisms (disinfection)',
          'To improve taste',
          'To acidify water',
        ],
        a: 1,
        e: 'Chlorination adds small amounts of Cl₂ or chlorine compounds to kill bacteria, viruses, and pathogens, making treated water safe to drink.',
        h: 'Chlorination = kills bacteria/pathogens for safe drinking water.',
        yr: 'SS2',
      },
      {
        q: 'Water has an unusually high boiling point (100°C) for such a small molecule because:',
        o: [
          'It is very dense',
          'Extensive hydrogen bonding between water molecules requires significant energy to break',
          'It contains oxygen atoms',
          'It has covalent double bonds',
        ],
        a: 1,
        e: 'Water forms strong O–H···O hydrogen bonds between molecules. These intermolecular forces require much energy to overcome, resulting in the unusually high boiling point of 100°C for such a small molecule.',
        h: "Water's high boiling point = extensive hydrogen bonding between molecules.",
        yr: 'SS2',
      },
      {
        q: 'Washing soda (Na₂CO₃) softens water by:',
        o: [
          'Evaporating Ca²⁺ ions',
          'Precipitating Ca²⁺ as CaCO₃↓: Ca²⁺ + CO₃²⁻ → CaCO₃↓',
          'Neutralising acidity',
          'Dissolving all salts',
        ],
        a: 1,
        e: 'Na₂CO₃ provides CO₃²⁻ ions that react with Ca²⁺ and Mg²⁺ to form insoluble carbonates that settle out, removing hardness. Works for both temporary and permanent hardness.',
        h: 'Washing soda: Ca²⁺ + CO₃²⁻ → CaCO₃↓. Removes both hardness types.',
        yr: 'SS2',
      },
      {
        q: "Water's maximum density occurs at:",
        o: ['0°C', '4°C', '100°C', '25°C'],
        a: 1,
        e: 'Water reaches maximum density (~1.0 g/cm³) at 4°C. Above and below 4°C, water is less dense. Ice (0°C) is less dense than liquid water — explaining why ice floats.',
        h: 'Water maximum density = ~1.0 g/cm³ at 4°C. Ice floats because less dense than liquid water.',
        yr: 'SS2',
      },
      {
        q: 'Water is described as amphoteric. This means it can:',
        o: [
          'Dissolve both acids and bases',
          'Act as acid (donate H⁺) or base (accept H⁺) depending on what it reacts with',
          'Conduct electricity in both AC and DC',
          'Both freeze and boil at unusual temperatures',
        ],
        a: 1,
        e: 'Amphoteric: can donate or accept protons. As acid: H₂O → H⁺ + OH⁻. As base: H₂O + H⁺ → H₃O⁺. Example: HCl + H₂O → H₃O⁺ + Cl⁻ (water acts as base).',
        h: 'Amphoteric water: can donate H⁺ (acid) or accept H⁺ (base) depending on reactant.',
        yr: 'SS2',
      },
      {
        q: 'The correct order for water treatment is:',
        o: [
          'Chlorination→Sedimentation→Filtration→Coagulation',
          'Sedimentation→Coagulation→Filtration→Chlorination',
          'Filtration→Sedimentation→Coagulation→Chlorination',
          'Coagulation→Chlorination→Sedimentation→Filtration',
        ],
        a: 1,
        e: 'Correct order: (1) Sedimentation — large particles settle. (2) Coagulation — alum clumps fine particles. (3) Filtration — sand/gravel removes remaining particles. (4) Chlorination — disinfection.',
        h: 'Water treatment: Sedimentation → Coagulation (alum) → Filtration → Chlorination.',
        yr: 'SS2',
      },
      {
        q: 'Hard water forms scum with soap. The scum is:',
        o: [
          'Undissolved soap',
          'Insoluble calcium stearate formed when soap reacts with Ca²⁺',
          'Bacteria and dirt',
          'Undissolved carbonate',
        ],
        a: 1,
        e: 'Soap (sodium stearate) + Ca²⁺ → calcium stearate (insoluble scum). 2C₁₇H₃₅COONa + Ca²⁺ → (C₁₇H₃₅COO)₂Ca↓ + 2Na⁺. The scum wastes soap and is ineffective for cleaning.',
        h: 'Scum = insoluble calcium stearate (soap + Ca²⁺ in hard water).',
        yr: 'SS2',
      },
      {
        q: 'A sample of water cannot be softened by boiling. What type of hardness does it contain?',
        o: [
          'Temporary only',
          "Permanent hardness — CaSO₄/MgSO₄ that don't decompose on heating",
          'Both types',
          'No hardness',
        ],
        a: 1,
        e: "If water remains hard after boiling, it contains permanent hardness (CaSO₄, MgSO₄). These salts don't decompose on heating unlike bicarbonates (temporary). Needs washing soda, ion exchange, or distillation.",
        h: 'Cannot be softened by boiling = permanent hardness.',
        yr: 'SS2',
      },
      {
        q: 'The formula for calcium bicarbonate — responsible for temporary hardness — is:',
        o: ['CaCO₃', 'CaSO₄', 'Ca(HCO₃)₂', 'CaCl₂'],
        a: 2,
        e: 'Calcium bicarbonate = Ca(HCO₃)₂. Forms when acidic rainwater (CO₂ + H₂O = H₂CO₃) dissolves limestone: CaCO₃ + H₂CO₃ → Ca(HCO₃)₂.',
        h: 'Calcium bicarbonate = Ca(HCO₃)₂ — causes temporary hardness.',
        yr: 'SS2',
      },
      {
        q: 'Which is an advantage of hard water?',
        o: [
          'It lathers easily with soap',
          'Ca²⁺ ions beneficial for bone/teeth development; may protect against cardiovascular disease',
          "It doesn't form scale in pipes",
          'Cheaper to treat than soft water',
        ],
        a: 1,
        e: 'Hard water contains Ca²⁺ ions important for bone and teeth development. Studies suggest people in hard water areas have lower rates of cardiovascular disease — a health advantage despite soap-wasting and scale-forming disadvantages.',
        h: 'Advantage of hard water: Ca²⁺ beneficial for bones, teeth, and cardiovascular health.',
        yr: 'SS2',
      },
      {
        q: 'The reaction 2H₂ + O₂ → 2H₂O: volumes of H₂ and O₂ that react are in ratio:',
        o: ['1:2', '2:1', '1:1', '3:1'],
        a: 1,
        e: "By Avogadro's Law: 2H₂ + O₂ → 2H₂O. 2 volumes H₂ react with 1 volume O₂. H₂:O₂ = 2:1.",
        h: '2H₂ + O₂ → 2H₂O: H₂:O₂ volume ratio = 2:1.',
        yr: 'SS2',
      },
      {
        q: 'Distillation removes both types of hardness because:',
        o: [
          'It kills all bacteria',
          'Only pure water vaporises and condenses — all dissolved ions remain in flask',
          'It adds fresh minerals',
          'It changes pH',
        ],
        a: 1,
        e: 'During distillation, pure water evaporates and condenses as distillate, leaving all dissolved salts (CaSO₄, Ca(HCO₃)₂) behind. Distilled water contains no dissolved ions — both hardness types completely removed.',
        h: 'Distillation: only pure water vaporises; all ions remain behind → removes all hardness.',
        yr: 'SS2',
      },
      {
        q: "Which of the following best explains why water is called the 'universal solvent'?",
        o: [
          'It dissolves all substances without exception',
          'Its polarity and hydrogen-bonding ability allow it to dissolve a wider variety of ionic and polar compounds than almost any other liquid',
          'It has the highest boiling point of all solvents',
          'It does not react chemically with solutes',
        ],
        a: 1,
        e: 'Water is polar (δ+ on H, δ− on O) and forms hydrogen bonds. This allows it to surround and solvate ionic compounds (like NaCl) and polar molecules (like sugars and alcohols). No solvent dissolves everything, but water dissolves more substances than any other common liquid.',
        h: 'Universal solvent: water polarity + hydrogen bonding dissolves ionic and polar compounds.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Acids, Bases and Salts',
    topicCode: 'SS2-CHEM-03',
    module: 'Physical Chemistry',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Acids</span>, <span class="learn-keyword">bases</span>, and <span class="learn-keyword">salts</span> are fundamental classes of chemical compounds. They react in neutralisation reactions to form salts and water.</div><h3 class="learn-subheading">1. Definitions</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Theory</th><th>Acid</th><th>Base</th></tr></thead><tbody><tr><td>Arrhenius</td><td>Produces H⁺ in water</td><td>Produces OH⁻ in water</td></tr><tr><td>Brønsted–Lowry</td><td>H⁺ donor</td><td>H⁺ acceptor</td></tr><tr><td>Lewis</td><td>Electron pair acceptor</td><td>Electron pair donor</td></tr></tbody></table></div><h3 class="learn-subheading">2. Properties</h3><ul class="learn-list"><li>Acids: pH &lt; 7; turn blue litmus red; react with metals (H₂↑), carbonates (CO₂↑), bases (neutralisation)</li><li>Strong acids (HCl, H₂SO₄, HNO₃) fully dissociate; weak acids (CH₃COOH) partially dissociate</li><li>Bases: pH &gt; 7; turn red litmus blue; alkali = soluble base</li></ul><h3 class="learn-subheading">3. pH Scale</h3><p class="learn-p">pH = −log[H⁺]. Scale 0–14: 0–6 acidic; 7 neutral; 8–14 alkaline.</p><h3 class="learn-subheading">4. Salt Preparation</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Method</th><th>Example</th></tr></thead><tbody><tr><td>Acid + Metal</td><td>Zn + H₂SO₄ → ZnSO₄ + H₂</td></tr><tr><td>Acid + Metal oxide</td><td>CuO + H₂SO₄ → CuSO₄ + H₂O</td></tr><tr><td>Acid + Carbonate</td><td>CaCO₃ + 2HCl → CaCl₂ + H₂O + CO₂</td></tr><tr><td>Neutralisation</td><td>NaOH + HCl → NaCl + H₂O</td></tr><tr><td>Precipitation</td><td>BaCl₂ + Na₂SO₄ → BaSO₄↓ + 2NaCl</td></tr></tbody></table></div><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Key:</strong> Strong acid ≠ concentrated acid. Strong = fully dissociates. Weak = partially. Insoluble salts made by precipitation.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Arrhenius: H⁺/OH⁻. Brønsted–Lowry: H⁺ donor/acceptor. Lewis: e⁻ pair acceptor/donor. pH&lt;7=acid; 7=neutral; &gt;7=alkaline. Insoluble salts by precipitation; soluble by neutralisation.</div>',
    questions: [
      {
        q: 'According to Brønsted–Lowry theory, an acid is:',
        o: [
          'Produces OH⁻ in water',
          'A proton (H⁺) donor',
          'An electron pair acceptor',
          'A substance with pH < 7',
        ],
        a: 1,
        e: 'Brønsted–Lowry (1923): acid = proton (H⁺) donor; base = proton acceptor. Broader than Arrhenius — works in non-aqueous solvents. Example: HCl donates H⁺ to H₂O → HCl is the acid.',
        h: 'Brønsted–Lowry acid = proton (H⁺) donor.',
        yr: 'SS2',
      },
      {
        q: 'According to Lewis theory, a base is:',
        o: [
          'A proton donor',
          'A proton acceptor',
          'An electron pair donor',
          'An electron pair acceptor',
        ],
        a: 2,
        e: 'Lewis (1923): base = electron pair donor; acid = electron pair acceptor. Broadest definition — includes reactions without proton transfer. Example: NH₃ donates electron pair to BF₃.',
        h: 'Lewis base = electron pair donor; Lewis acid = electron pair acceptor.',
        yr: 'SS2',
      },
      {
        q: 'The difference between a base and an alkali is:',
        o: [
          'Bases have higher pH',
          'Alkali = soluble base. All alkalis are bases; not all bases dissolve in water',
          'Alkalis are gases; bases are solids',
          'Bases react with acids; alkalis do not',
        ],
        a: 1,
        e: 'Base = neutralises acid (accepts H⁺ or produces OH⁻). Alkali = soluble base. CuO is a base (insoluble); NaOH is both base and alkali (dissolves to give OH⁻).',
        h: 'Alkali = soluble base. All alkalis are bases; not all bases are alkalis.',
        yr: 'SS2',
      },
      {
        q: 'Strong acids differ from weak acids in that strong acids:',
        o: [
          'Are more concentrated',
          'Fully dissociate into ions in water; weak acids only partially dissociate',
          'React faster with everything',
          'Have lower molecular masses',
        ],
        a: 1,
        e: 'Strong acids (HCl, H₂SO₄, HNO₃) fully dissociate (~100%). Weak acids (CH₃COOH) partially dissociate, establishing an equilibrium. This is about dissociation, not concentration.',
        h: 'Strong acid = 100% dissociation. Weak acid = partial dissociation (equilibrium).',
        yr: 'SS2',
      },
      {
        q: 'The pH of a neutral solution at 25°C is:',
        o: ['0', '7', '14', '1'],
        a: 1,
        e: 'At 25°C, neutral: [H⁺]=[OH⁻]=10⁻⁷ mol/L. pH=−log(10⁻⁷)=7. Acidic: pH<7. Alkaline: pH>7.',
        h: 'Neutral pH = 7 at 25°C.',
        yr: 'SS2',
      },
      {
        q: 'The reaction between an acid and a base producing salt and water is called:',
        o: ['Decomposition', 'Precipitation', 'Neutralisation', 'Hydrolysis'],
        a: 2,
        e: 'Neutralisation: Acid + Base → Salt + Water. Example: HCl + NaOH → NaCl + H₂O. H⁺ combines with OH⁻ to form water; remaining ions form the salt.',
        h: 'Acid + Base → Salt + Water = Neutralisation.',
        yr: 'SS2',
      },
      {
        q: 'The best method to prepare insoluble salt BaSO₄ is:',
        o: [
          'Neutralisation',
          'Acid + metal',
          'Precipitation: mixing two solutions containing the required ions',
          'Crystallisation',
        ],
        a: 2,
        e: 'Insoluble salts made by precipitation: BaCl₂(aq) + Na₂SO₄(aq) → BaSO₄(s)↓ + 2NaCl(aq). The precipitate is filtered, washed, and dried.',
        h: 'Insoluble salts = precipitation (mix two solutions; product precipitates).',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is a weak acid?',
        o: ['HCl', 'H₂SO₄', 'HNO₃', 'CH₃COOH (ethanoic acid)'],
        a: 3,
        e: 'Ethanoic acid (CH₃COOH) partially dissociates: CH₃COOH ⇌ CH₃COO⁻ + H⁺ — weak acid. HCl, H₂SO₄, HNO₃ are strong acids (fully dissociate).',
        h: 'Weak acids: CH₃COOH, H₂CO₃. Strong: HCl, H₂SO₄, HNO₃.',
        yr: 'SS2',
      },
      {
        q: 'When zinc reacts with dilute H₂SO₄, products are:',
        o: ['ZnSO₄ and O₂', 'ZnSO₄ and H₂', 'ZnO and H₂O', 'ZnS and H₂O'],
        a: 1,
        e: 'Zn + H₂SO₄ → ZnSO₄ + H₂. Active metals above hydrogen in reactivity series displace hydrogen from dilute acids. H₂ causes a squeaky pop with a lit splint.',
        h: 'Zn + H₂SO₄ → ZnSO₄ + H₂. Metal + acid → salt + hydrogen.',
        yr: 'SS2',
      },
      {
        q: 'Which observation correctly identifies an acidic solution using litmus?',
        o: [
          'Red litmus turns blue',
          'Blue litmus turns red',
          'Litmus becomes colourless',
          'Yellow litmus turns green',
        ],
        a: 1,
        e: 'Acids turn blue litmus red. Alkalis turn red litmus blue. Classic litmus test.',
        h: 'Acid: turns blue litmus red. Alkali: turns red litmus blue.',
        yr: 'SS2',
      },
      {
        q: 'NaOH + NH₄Cl on heating gives:',
        o: ['NaCl + H₂ + O₂', 'NaCl + H₂O + NH₃', 'Na₂CO₃ + H₂ + N₂', 'NaCl + H₂SO₄'],
        a: 1,
        e: 'NaOH + NH₄Cl → NaCl + H₂O + NH₃. Alkalis react with ammonium salts on warming to produce ammonia — the test for NH₄⁺ ions.',
        h: 'NaOH + NH₄Cl → NaCl + H₂O + NH₃ (ammonia test for NH₄⁺).',
        yr: 'SS2',
      },
      {
        q: 'Excess CuO is added when preparing CuSO₄ from CuO + H₂SO₄ because:',
        o: [
          'CuO is cheap',
          'Excess CuO ensures all acid is neutralised, preventing acid contamination of crystals',
          'Excess CuO improves crystal colour',
          'CuO makes the reaction faster',
        ],
        a: 1,
        e: 'CuO + H₂SO₄ → CuSO₄ + H₂O. Excess CuO ensures all H₂SO₄ is neutralised. Unreacted CuO (insoluble) is filtered off before crystallisation.',
        h: 'Excess CuO = ensures all acid neutralised; excess filtered off.',
        yr: 'SS2',
      },
      {
        q: 'The conjugate base of HNO₃ is:',
        o: ['H₂NO₃⁺', 'NO₃⁻', 'OH⁻', 'H₃O⁺'],
        a: 1,
        e: 'Conjugate base = acid minus H⁺. HNO₃ → H⁺ + NO₃⁻. Conjugate base of HNO₃ = NO₃⁻.',
        h: 'HNO₃ − H⁺ = NO₃⁻ (conjugate base).',
        yr: 'SS2',
      },
      {
        q: 'CaCO₃ + 2HCl → ? The salt formed is:',
        o: ['CaSO₄', 'CaCl₂', 'Ca(NO₃)₂', 'CaCO₃'],
        a: 1,
        e: 'CaCO₃ + 2HCl → CaCl₂ + H₂O + CO₂. The salt is calcium chloride (CaCl₂). CO₂ gas is evolved (bubbles).',
        h: 'CaCO₃ + 2HCl → CaCl₂ + H₂O + CO₂. Salt = calcium chloride.',
        yr: 'SS2',
      },
      {
        q: 'At pH 1, universal indicator shows:',
        o: ['Purple/violet', 'Green', 'Red/dark red (strongly acidic)', 'Yellow'],
        a: 2,
        e: 'Universal indicator: pH 1–3 = red/dark red (strongly acidic); pH 4–6 = orange/yellow (weakly acidic); pH 7 = green (neutral); pH 8–10 = blue; pH 11–14 = purple.',
        h: 'pH 1 = red/dark red. pH 7 = green. pH 14 = purple.',
        yr: 'SS2',
      },
      {
        q: 'A substance with pH = 12 is:',
        o: ['Strongly acidic', 'Weakly acidic', 'Neutral', 'Strongly alkaline'],
        a: 3,
        e: 'pH 12 is well above 7 — strongly alkaline. At pH 12, [H⁺] = 10⁻¹² mol/L; [OH⁻] = 10⁻² mol/L.',
        h: 'pH 12 = strongly alkaline.',
        yr: 'SS2',
      },
      {
        q: 'Gas produced when a carbonate reacts with acid, and its test:',
        o: [
          'H₂ — squeaky pop',
          'O₂ — relights glowing splint',
          'CO₂ — turns limewater milky',
          'NH₃ — turns damp red litmus blue',
        ],
        a: 2,
        e: 'Carbonates + acid → CO₂. Test: CO₂ bubbled through limewater (Ca(OH)₂) → milky (CaCO₃ precipitates): CO₂ + Ca(OH)₂ → CaCO₃↓ + H₂O.',
        h: 'Carbonate + acid → CO₂. CO₂ test: turns limewater milky.',
        yr: 'SS2',
      },
      {
        q: '25.0 cm³ of 0.1 mol/L NaOH neutralised by 12.5 cm³ HCl. Concentration of HCl?',
        o: ['0.05 mol/L', '0.1 mol/L', '0.2 mol/L', '0.4 mol/L'],
        a: 2,
        e: 'n(NaOH)=0.025×0.1=0.0025 mol. NaOH:HCl=1:1. n(HCl)=0.0025 mol. c(HCl)=0.0025/0.0125=0.2 mol/L.',
        h: 'n(NaOH)=0.0025 mol → n(HCl)=0.0025 mol → c=0.0025/0.0125=0.2 mol/L.',
        yr: 'SS2',
      },
      {
        q: 'H₂SO₄ + 2NaOH → ? The salt is:',
        o: ['NaCl', 'Na₂SO₄', 'NaNO₃', 'Na₂SO₃'],
        a: 1,
        e: 'H₂SO₄ + 2NaOH → Na₂SO₄ + 2H₂O. Sulphuric acid + sodium hydroxide → sodium sulphate. Cation from base (Na⁺), anion from acid (SO₄²⁻).',
        h: 'H₂SO₄ + NaOH → Na₂SO₄ (sodium sulphate).',
        yr: 'SS2',
      },
      {
        q: "Arrhenius theory's limitation vs Brønsted–Lowry is that Arrhenius:",
        o: [
          'Requires electron pairs',
          'Only applies to aqueous solutions — cannot explain acid-base reactions in non-aqueous solvents',
          'Does not apply to strong acids',
          'Requires all acids to be organic',
        ],
        a: 1,
        e: 'Arrhenius requires water — acid must produce H⁺ in water; base must produce OH⁻ in water. Brønsted–Lowry is broader — applies to proton transfer in any solvent (e.g., HCl(g) + NH₃(g) → NH₄Cl(s)).',
        h: 'Arrhenius limitation = requires aqueous solution (water).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Solubility and Solubility Curves',
    topicCode: 'SS2-CHEM-04',
    module: 'Physical Chemistry',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Solubility</span> is the maximum mass of a solute that dissolves in 100 g of solvent at a given temperature to form a saturated solution. <span class="learn-keyword">Solubility curves</span> show how solubility changes with temperature.</div><h3 class="learn-subheading">1. Key Definitions</h3><ul class="learn-list"><li><strong>Saturated solution:</strong> cannot dissolve more solute at that temperature</li><li><strong>Unsaturated solution:</strong> can dissolve more solute</li><li><strong>Supersaturated solution:</strong> unstable; more dissolved than normally possible; crystallises on disturbance</li></ul><h3 class="learn-subheading">2. Effect of Temperature on Solubility</h3><ul class="learn-list"><li>Most solids: solubility increases with temperature (KNO₃, KCl, NaCl)</li><li>Gases: solubility decreases with increasing temperature (CO₂ in water)</li><li>Exceptions: CaSO₄ — solubility decreases with temperature (exothermic dissolution)</li></ul><h3 class="learn-subheading">3. Crystal Calculation</h3><p class="learn-p">Mass of crystals = (solubility at higher T − solubility at lower T) × (mass of water / 100).</p><h3 class="learn-subheading">4. Factors Affecting Solubility</h3><ul class="learn-list"><li>Temperature (most important for solids)</li><li>Pressure (affects gases — Henry\'s Law: S ∝ P)</li><li>Nature of solute and solvent (like dissolves like)</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Crystal Calculation:</strong> Mass of crystals = difference in solubility × (water mass / 100).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Solubility = g solute per 100 g water at given T. Saturated = maximum dissolved. Solid solubility mostly ↑ with T; gas solubility ↓ with T. Henry\'s Law: gas solubility ∝ pressure.</div>',
    questions: [
      {
        q: 'Solubility of a substance is defined as:',
        o: [
          'Amount dissolved in any volume of solvent',
          'Maximum mass of solute that dissolves in 100 g of solvent at a given temperature',
          'Volume of solute per litre of solution',
          'Rate at which solute dissolves',
        ],
        a: 1,
        e: 'Solubility = maximum mass (grams) of solute that dissolves in exactly 100 g of solvent at a specified temperature to form a saturated solution.',
        h: 'Solubility = max mass of solute per 100 g water at given T (g/100 g water).',
        yr: 'SS2',
      },
      {
        q: 'A saturated solution is one that:',
        o: [
          'Contains equal masses of solute and solvent',
          'Cannot dissolve more solute at the given temperature',
          'Is very concentrated but can still dissolve more',
          'Has been heated to boiling',
        ],
        a: 1,
        e: 'A saturated solution has dissolved the maximum possible amount of solute at that temperature. Adding more solute leaves it undissolved.',
        h: 'Saturated = cannot dissolve more solute at that temperature.',
        yr: 'SS2',
      },
      {
        q: 'KNO₃ solubility is 110 g/100g at 60°C and 30 g/100g at 20°C. Crystals deposited on cooling 100g water from 60°C to 20°C?',
        o: ['30 g', '80 g', '110 g', '140 g'],
        a: 1,
        e: 'Crystals = 110 − 30 = 80 g (per 100 g water). Solution can only hold 30 g at 20°C; excess 80 g crystallises.',
        h: 'Crystals = solubility at high T − solubility at low T = 110 − 30 = 80 g.',
        yr: 'SS2',
      },
      {
        q: 'Solubility of gases in water generally:',
        o: [
          'Increases with temperature',
          'Decreases with temperature',
          'Is unaffected by temperature',
          'Increases with decreasing pressure',
        ],
        a: 1,
        e: 'Unlike solids, gases become less soluble as temperature increases. Warm fizzy drinks go flat faster — CO₂ is less soluble at higher temperatures.',
        h: 'Gas solubility DECREASES with increasing temperature.',
        yr: 'SS2',
      },
      {
        q: "Henry's Law states that gas solubility in a liquid is:",
        o: [
          'Inversely proportional to temperature',
          'Directly proportional to the partial pressure of the gas above the liquid',
          'Proportional to mass of the liquid',
          'Independent of pressure',
        ],
        a: 1,
        e: "Henry's Law: S = kP. More pressure forces more gas into solution. Explains carbonated drinks (CO₂ dissolved under pressure) and decompression sickness in divers.",
        h: "Henry's Law: gas solubility ∝ pressure above liquid.",
        yr: 'SS2',
      },
      {
        q: 'A supersaturated solution is:',
        o: [
          'Heated to boiling',
          'Unstable; contains more dissolved solute than normally possible; crystallises on disturbance',
          'Equal masses of solute and solvent',
          'Very dilute',
        ],
        a: 1,
        e: 'Supersaturated solution contains more dissolved solute than the saturation level allows. Prepared by careful cooling of saturated solution. Unstable — scratching container or adding seed crystal causes rapid crystallisation.',
        h: 'Supersaturated = more dissolved than normally possible; crystallises on disturbance.',
        yr: 'SS2',
      },
      {
        q: 'On a solubility curve, a point above the curve represents:',
        o: [
          'Unsaturated solution',
          'Supersaturated solution — more dissolved than the solubility allows',
          'Saturated solution',
          'Solution at boiling point',
        ],
        a: 1,
        e: 'On solubility curve: above = supersaturated; on curve = saturated; below = unsaturated.',
        h: 'Above curve = supersaturated; on curve = saturated; below = unsaturated.',
        yr: 'SS2',
      },
      {
        q: 'Like dissolves like means:',
        o: [
          'Same-mass solutes dissolve in each other',
          'Polar solutes dissolve best in polar solvents; non-polar solutes in non-polar solvents',
          'All salts dissolve in water',
          'Gases dissolve only in liquid gases',
        ],
        a: 1,
        e: 'Polar solvents (water) dissolve polar/ionic substances (NaCl, sugars). Non-polar solvents (hexane) dissolve non-polar substances (fats, waxes, iodine).',
        h: 'Like dissolves like: polar in polar; non-polar in non-polar.',
        yr: 'SS2',
      },
      {
        q: 'Solubility of NaCl is 36 g/100 g water at 20°C. How much NaCl dissolves in 200 g water?',
        o: ['36 g', '18 g', '72 g', '100 g'],
        a: 2,
        e: 'For 200 g water: 36 × (200/100) = 36 × 2 = 72 g.',
        h: '36 g/100g × 200g = 72 g NaCl.',
        yr: 'SS2',
      },
      {
        q: 'From a solubility curve you can determine all EXCEPT:',
        o: [
          'Solubility at specific temperature',
          'Mass of crystals on cooling',
          'Temperature where two salts have equal solubility',
          'Rate of dissolution',
        ],
        a: 3,
        e: 'Solubility curves show equilibrium solubility at each temperature — not how fast the solute dissolves (rate depends on particle size, stirring, T — a kinetics question).',
        h: 'Solubility curves show equilibrium solubility — NOT rate of dissolution.',
        yr: 'SS2',
      },
      {
        q: 'KCl solubility: 50 g/100g at 80°C and 40 g/100g at 40°C. Crystals per 100 g water on cooling?',
        o: ['40 g', '50 g', '10 g', '90 g'],
        a: 2,
        e: 'Crystals = 50 − 40 = 10 g per 100 g water.',
        h: '10 g crystals per 100 g water (50 − 40 = 10 g).',
        yr: 'SS2',
      },
      {
        q: 'Which factor does NOT significantly affect solubility of a solid in liquid?',
        o: ['Temperature', 'Nature of solvent', 'Nature of solute', 'Pressure (at normal ranges)'],
        a: 3,
        e: 'Pressure has negligible effect on solid solubility (unlike gases). Solid solubility depends on temperature and nature of solute/solvent.',
        h: "Pressure negligibly affects solid solubility (major for gases via Henry's Law).",
        yr: 'SS2',
      },
      {
        q: 'Efflorescence refers to:',
        o: [
          'Crystals absorbing water from atmosphere',
          'Crystals losing water of crystallisation to atmosphere, becoming powder',
          'Solution evaporating completely',
          'Salt dissolving in its own water of crystallisation',
        ],
        a: 1,
        e: 'Efflorescent salts lose water of crystallisation to dry air — form anhydrous powdery surface. Example: Na₂CO₃·10H₂O → Na₂CO₃·H₂O powder.',
        h: 'Efflorescence = crystals lose water of crystallisation to dry air (become powdery).',
        yr: 'SS2',
      },
      {
        q: 'Hygroscopic vs deliquescent substances:',
        o: [
          'Identical behaviour',
          'Hygroscopic absorbs water but stays solid; deliquescent absorbs enough water to dissolve completely',
          'Both dissolve in absorbed water',
          'Hygroscopic for liquids only',
        ],
        a: 1,
        e: 'Hygroscopic: absorbs water but remains solid (e.g., silica gel, anhydrous CaCl₂). Deliquescent: absorbs so much water it dissolves completely (e.g., NaOH, FeCl₃ in humid conditions).',
        h: 'Hygroscopic = absorbs water, stays solid. Deliquescent = absorbs water and dissolves.',
        yr: 'SS2',
      },
      {
        q: 'Miscible liquids are those that:',
        o: [
          'Are insoluble in each other',
          'Dissolve in each other in all proportions',
          'Have equal density',
          'Have same boiling point',
        ],
        a: 1,
        e: 'Miscible liquids dissolve in each other in any ratio (homogeneous mixture). Example: ethanol and water. Immiscible: water and oil form separate layers.',
        h: 'Miscible = dissolve in each other completely (e.g., ethanol and water).',
        yr: 'SS2',
      },
      {
        q: 'Water of crystallisation in CuSO₄·5H₂O means:',
        o: [
          'Water surrounding crystals outside',
          'Water molecules chemically incorporated into crystal structure at definite ratios',
          'Water produced during reaction',
          'Water used to wash crystals',
        ],
        a: 1,
        e: 'Water of crystallisation is chemically combined within the crystal structure at a fixed, definite ratio. In CuSO₄·5H₂O, each formula unit has exactly 5 water molecules in the lattice.',
        h: 'Water of crystallisation = water chemically in crystal structure at fixed ratios.',
        yr: 'SS2',
      },
      {
        q: 'Steep solubility curve indicates:',
        o: [
          'Slightly soluble at all temperatures',
          'Strong temperature dependence — small T change causes large solubility change; efficient crystallisation on cooling',
          'Compound decomposes at high T',
          'A gas not a solid',
        ],
        a: 1,
        e: 'Steep curve = solubility strongly dependent on temperature. Large amount of crystals form when hot saturated solution is cooled — efficient purification by fractional crystallisation.',
        h: 'Steep solubility curve = strongly temperature-dependent; efficient crystallisation on cooling.',
        yr: 'SS2',
      },
      {
        q: 'Which statement about solutions is INCORRECT?',
        o: [
          'Solubility of most solids increases with temperature',
          'Adding solute to saturated solution at constant T increases solubility',
          "Gases more soluble at higher pressures (Henry's Law)",
          'Saturated solution is in equilibrium between dissolved and undissolved solute',
        ],
        a: 1,
        e: 'Adding solute to a saturated solution does NOT increase solubility — it is a fixed property at a given temperature. Excess solute remains undissolved.',
        h: 'Adding solute to saturated solution does NOT increase solubility.',
        yr: 'SS2',
      },
      {
        q: 'Sugar solubility: 200 g/100g at 50°C; 150 g/100g at 30°C. Crystals from 300 g water cooled from 50°C to 30°C?',
        o: ['50 g', '100 g', '150 g', '200 g'],
        a: 2,
        e: 'Crystals per 100 g water = 200 − 150 = 50 g. For 300 g water: 50 × 3 = 150 g.',
        h: 'Crystals/100g = 50g. For 300g water: 50×3 = 150g.',
        yr: 'SS2',
      },
      {
        q: 'CaSO₄ shows slight decrease in solubility with temperature. This is because:',
        o: [
          'Its dissolution is exothermic — higher T shifts equilibrium to dissolve less',
          'Measurement error',
          'It is a gas behaving like a solid',
          'Normal solid behaviour',
        ],
        a: 0,
        e: "When dissolution is exothermic (releases heat), Le Chatelier's principle predicts increasing T shifts equilibrium left (less dissolving), decreasing solubility. CaSO₄ dissolution is exothermic.",
        h: 'CaSO₄ solubility decreases with T: exothermic dissolution (Le Chatelier).',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Environmental Chemistry',
    topicCode: 'SS2-CHEM-05',
    module: 'Applied Chemistry',
    term: 'First Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Environmental chemistry</span> studies chemical processes in the environment and the impacts of human activities on air, water, and soil.</div><h3 class="learn-subheading">1. Air Pollutants</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Pollutant</th><th>Source</th><th>Effect</th></tr></thead><tbody><tr><td>CO</td><td>Incomplete combustion</td><td>Binds haemoglobin; toxic</td></tr><tr><td>SO₂</td><td>Burning fossil fuels</td><td>Acid rain; respiratory problems</td></tr><tr><td>NOₓ</td><td>Car engines</td><td>Acid rain; photochemical smog</td></tr><tr><td>CO₂</td><td>Combustion</td><td>Greenhouse effect</td></tr><tr><td>CFCs</td><td>Old refrigerants</td><td>Ozone depletion</td></tr></tbody></table></div><h3 class="learn-subheading">2. Acid Rain</h3><p class="learn-p">pH below 5.6. SO₂ + H₂O → H₂SO₃ → H₂SO₄; NO₂ + H₂O → HNO₃. Effects: destroys forests, acidifies lakes, corrodes limestone buildings.</p><h3 class="learn-subheading">3. Greenhouse Effect</h3><p class="learn-p">CO₂, CH₄, N₂O trap infrared radiation. Enhanced greenhouse effect from burning fossil fuels → global warming.</p><h3 class="learn-subheading">4. Ozone Depletion</h3><p class="learn-p">CFCs release Cl• radicals: Cl• + O₃ → ClO• + O₂ (catalytic chain). Montreal Protocol (1987) phased out CFCs.</p><h3 class="learn-subheading">5. Water Pollution</h3><ul class="learn-list"><li>Eutrophication: fertiliser runoff → algal bloom → O₂ depletion</li><li>Oil spills: kills aquatic life</li><li>Heavy metals: biomagnification</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Links:</strong> SO₂/NOₓ → acid rain. CO₂/CH₄ → greenhouse/global warming. CFCs → ozone depletion. Fertiliser runoff → eutrophication.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> CO (haemoglobin), SO₂ (acid rain), NOₓ (acid rain/smog), CO₂ (greenhouse), CFCs (ozone). Acid rain pH &lt; 5.6. Montreal Protocol banned CFCs. Eutrophication: fertiliser→algae→O₂ depletion.</div>',
    questions: [
      {
        q: 'CO is dangerous because it:',
        o: [
          'Causes acid rain',
          'Binds haemoglobin 200× stronger than O₂, preventing oxygen transport',
          'Destroys ozone',
          'Is a greenhouse gas',
        ],
        a: 1,
        e: 'CO binds haemoglobin 200× more strongly than O₂, forming stable carboxyhaemoglobin. Blood cannot carry oxygen → hypoxia → death. Produced by incomplete combustion.',
        h: 'CO: binds haemoglobin → prevents O₂ transport → hypoxia.',
        yr: 'SS2',
      },
      {
        q: 'Acid rain is defined as rain with pH below:',
        o: ['7.0', '6.0', '5.6', '4.0'],
        a: 2,
        e: 'Normal rain pH ≈ 5.6 (dissolved CO₂). Acid rain pH < 5.6 due to dissolved H₂SO₄ and HNO₃ from SO₂ and NOₓ.',
        h: 'Acid rain pH < 5.6.',
        yr: 'SS2',
      },
      {
        q: 'The Montreal Protocol (1987) was an agreement to:',
        o: [
          'Reduce CO₂ emissions',
          'Phase out CFCs and other ozone-depleting substances',
          'Regulate industrial water pollution',
          'Limit SO₂ from power stations',
        ],
        a: 1,
        e: 'Montreal Protocol (1987) phased out production and use of CFCs and other ozone-depleting substances. One of the most successful international environmental agreements.',
        h: 'Montreal Protocol (1987) = phase out CFCs to protect ozone layer.',
        yr: 'SS2',
      },
      {
        q: 'Eutrophication is caused by:',
        o: [
          'Oil spills',
          'Fertiliser runoff → algal bloom → decomposition → O₂ depletion → aquatic death',
          'Acid rain',
          'Biomagnification',
        ],
        a: 1,
        e: 'Nitrates and phosphates from fertilisers wash into lakes → explosive algal growth → algae die → bacteria decompose consuming O₂ → fish suffocate.',
        h: 'Eutrophication: fertiliser runoff → algal bloom → O₂ depletion.',
        yr: 'SS2',
      },
      {
        q: 'Which greenhouse gas is produced by cattle and rice paddies?',
        o: ['SO₂', 'CH₄ (methane)', 'NO₂', 'Cl₂'],
        a: 1,
        e: 'Methane (CH₄) is produced by methanogenic bacteria in ruminant guts (cattle) and flooded paddy fields. CH₄ has ≈28× global warming potential of CO₂.',
        h: 'Methane (CH₄) = from cattle and flooded rice paddies.',
        yr: 'SS2',
      },
      {
        q: 'CFCs destroy ozone through:',
        o: [
          'Dissolving ozone in acid rain',
          'Releasing Cl• radicals: Cl• + O₃ → ClO• + O₂ (catalytic chain)',
          'Reacting with UV to produce SO₂',
          'Raising stratosphere temperature',
        ],
        a: 1,
        e: 'UV breaks CFCs → Cl• radicals. Chain: Cl• + O₃ → ClO• + O₂; ClO• + O• → Cl• + O₂. Each Cl• destroys thousands of O₃ molecules.',
        h: 'CFCs → Cl• radicals → Cl• + O₃ → ClO• + O₂ (catalytic chain).',
        yr: 'SS2',
      },
      {
        q: 'SO₂ contributes to acid rain by:',
        o: [
          'Reacting with ozone',
          'Dissolving in rainwater: SO₂ + H₂O → H₂SO₃, oxidised to H₂SO₄',
          'Decomposing water',
          'Reacting with nitrogen',
        ],
        a: 1,
        e: 'SO₂ + H₂O → H₂SO₃ → oxidised by O₂ to H₂SO₄. This strong acid in rain is the main cause of acid rain from coal-burning power plants.',
        h: 'SO₂ + H₂O → H₂SO₃ → H₂SO₄ (acid rain).',
        yr: 'SS2',
      },
      {
        q: 'The enhanced greenhouse effect refers to:',
        o: [
          'Natural warming of Earth',
          'Human-intensified warming from increased CO₂/CH₄ causing global warming',
          'Effect of greenhouses on plants',
          'Increased UV reaching Earth',
        ],
        a: 1,
        e: 'Natural greenhouse effect is essential. Enhanced greenhouse effect = intensification from human activities (burning fossil fuels, deforestation) increasing atmospheric CO₂, CH₄, N₂O beyond natural levels.',
        h: 'Enhanced greenhouse effect = human-intensified warming → global warming.',
        yr: 'SS2',
      },
      {
        q: 'Biomagnification of mercury means:',
        o: [
          'Mercury diluted up food chain',
          'Mercury accumulates and concentrates increasingly up food chain: plankton→fish→birds→humans',
          'Mercury removed by bacteria',
          'Only plants affected',
        ],
        a: 1,
        e: 'Mercury: absorbed by plankton in tiny amounts → small fish eat many plankton (concentrate mercury) → larger fish → top predators accumulate very high levels. Caused Minamata disease in Japan.',
        h: 'Biomagnification: pollutant concentrates up food chain.',
        yr: 'SS2',
      },
      {
        q: 'Photochemical smog forms when:',
        o: [
          'SO₂ mixes with fog',
          'Sunlight reacts with NOₓ and hydrocarbons from car exhausts → ground-level O₃, PAN',
          'Black smoke released in winter',
          'CFCs react with fog',
        ],
        a: 1,
        e: 'Photochemical smog: UV + NOₓ + unburnt hydrocarbons → ground-level ozone (O₃), peroxyacetyl nitrate (PAN), other irritants causing respiratory problems and eye irritation.',
        h: 'Photochemical smog = sunlight + NOₓ + hydrocarbons → ground-level O₃.',
        yr: 'SS2',
      },
      {
        q: 'Major consequence of ozone depletion:',
        o: [
          'More acid rain',
          'Increased UV-B → more skin cancer, cataracts, marine ecosystem damage',
          'Global cooling',
          'More greenhouse warming',
        ],
        a: 1,
        e: 'Ozone layer absorbs UV-B (280–315 nm). Depletion → more UV-B → skin cancer (melanoma), cataracts, immune suppression, damage to phytoplankton and marine food chains.',
        h: 'Ozone depletion → more UV-B → skin cancer, cataracts, marine damage.',
        yr: 'SS2',
      },
      {
        q: 'Bioremediation is:',
        o: [
          'Using chemicals to dissolve pollutants',
          'Using living organisms (bacteria, fungi, plants) to break down or remove pollutants',
          'Burning contaminated soil',
          'Diluting pollutants with water',
        ],
        a: 1,
        e: 'Bioremediation uses microorganisms or plants to degrade, transform, or remove pollutants. Bacteria break down crude oil; sunflowers absorb heavy metals from soil (phytoremediation).',
        h: 'Bioremediation = microorganisms/plants remove/break down environmental pollutants.',
        yr: 'SS2',
      },
      {
        q: 'NOₓ produced in car engines because:',
        o: [
          'Engines use nitrogen as fuel',
          'High combustion temperatures cause N₂ + O₂ → NO, then NO₂',
          'Petrol contains nitrogen compounds',
          'NOₓ added as fuel additive',
        ],
        a: 1,
        e: 'At high engine temperatures (>1000°C): N₂ + O₂ → 2NO. NO → NO₂ in air. NOₓ causes acid rain (forms HNO₃) and photochemical smog. Catalytic converters convert NOₓ → N₂.',
        h: 'NOₓ from cars: high T causes N₂ + O₂ → NO, then NO₂.',
        yr: 'SS2',
      },
      {
        q: 'Acid rain corrodes limestone because:',
        o: [
          'Acid reacts with silicate in stone',
          'Limestone (CaCO₃) + H₂SO₄ → CaSO₄ + H₂O + CO₂ (dissolves stone)',
          'Acid raises temperature of stone',
          'Nitrogen forms gas breaking stone',
        ],
        a: 1,
        e: 'CaCO₃ + H₂SO₄ → CaSO₄ + H₂O + CO₂. CaCO₃ gradually dissolves. CaSO₄ forms powdery surface (gypsum) that crumbles. Destroys historic buildings and statues.',
        h: 'Acid rain + limestone (CaCO₃) → CaSO₄ + H₂O + CO₂ → stone erodes.',
        yr: 'SS2',
      },
      {
        q: 'Kyoto Protocol and Paris Agreement aim to:',
        o: [
          'Ban CFCs globally',
          'Reduce greenhouse gas emissions to mitigate global warming',
          'Clean up oil spills',
          'Regulate pesticide use',
        ],
        a: 1,
        e: 'Kyoto Protocol (1997) and Paris Agreement (2015) set targets for reducing greenhouse gas emissions (especially CO₂) to limit global temperature rise and mitigate climate change.',
        h: 'Kyoto Protocol + Paris Agreement = reduce greenhouse gas emissions.',
        yr: 'SS2',
      },
      {
        q: 'Oil spills damage the environment by:',
        o: [
          'Making water more alkaline',
          'Surface film prevents gas exchange; coats/kills marine birds and animals; destroys food chains',
          'Increasing O₂ content of water',
          'Decomposing rapidly without impact',
        ],
        a: 1,
        e: 'Oil surface film: prevents O₂/CO₂ exchange (suffocates aquatic life), coats and kills seabirds and mammals, contaminates beaches, disrupts marine food chains.',
        h: 'Oil spills: surface film prevents gas exchange, coats/kills marine life.',
        yr: 'SS2',
      },
      {
        q: 'BOD (Biochemical Oxygen Demand) measures:',
        o: [
          'Dissolved O₂ available for fish',
          'O₂ consumed by microorganisms decomposing organic matter — high BOD = high pollution, low dissolved O₂',
          'pH of water',
          'Nitrate concentration',
        ],
        a: 1,
        e: 'BOD = O₂ consumed by bacteria decomposing organic matter over 5 days at 20°C. High BOD = much organic matter → bacteria consume O₂ → less for fish. Clean water = low BOD.',
        h: 'High BOD = high organic pollution = bacteria consume O₂ = less for aquatic life.',
        yr: 'SS2',
      },
      {
        q: 'Correct pair of gas and environmental problem:',
        o: [
          'CO₂ — ozone depletion',
          'CFCs — greenhouse only',
          'SO₂ — acid rain',
          'CH₄ — acid rain',
        ],
        a: 2,
        e: 'SO₂ → acid rain. CO₂ → greenhouse/global warming. CFCs → ozone depletion. CH₄ → greenhouse gas (not acid rain).',
        h: 'SO₂ → acid rain. CO₂ → greenhouse. CFCs → ozone depletion.',
        yr: 'SS2',
      },
      {
        q: 'Lead in old petrol was phased out because:',
        o: [
          'Too expensive',
          'Neurotoxin accumulating in body → brain damage especially in children',
          'Corrosive to engines',
          'Greenhouse gas',
        ],
        a: 1,
        e: 'Tetraethyl lead released lead particles when burned. Lead is a cumulative neurotoxin causing developmental delays, reduced IQ in children, and neurological damage.',
        h: 'Lead in petrol = neurotoxin → brain damage, especially in children.',
        yr: 'SS2',
      },
      {
        q: 'Catalytic converters convert:',
        o: [
          'CO₂ and H₂O into hydrocarbons',
          'CO and NOₓ into CO₂, N₂, and H₂O through catalytic reactions',
          'SO₂ into H₂SO₄',
          'O₂ into ozone',
        ],
        a: 1,
        e: 'Catalytic converters (Pt, Pd, Rh catalysts): CO → CO₂; 2CO + 2NO → 2CO₂ + N₂; hydrocarbons → CO₂ + H₂O. Significantly reduce toxic vehicle emissions.',
        h: 'Catalytic converter: CO + NOₓ + hydrocarbons → CO₂ + N₂ + H₂O.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Atomic Structure',
    topicCode: 'SS2-CHEM-06',
    module: 'Atomic Structure',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro">All matter is made of <span class="learn-keyword">atoms</span>. Understanding atomic structure explains chemical properties, bonding, and the periodic table.</div><h3 class="learn-subheading">1. Subatomic Particles</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Particle</th><th>Charge</th><th>Mass</th><th>Location</th></tr></thead><tbody><tr><td>Proton (p⁺)</td><td>+1</td><td>1</td><td>Nucleus</td></tr><tr><td>Neutron (n⁰)</td><td>0</td><td>1</td><td>Nucleus</td></tr><tr><td>Electron (e⁻)</td><td>−1</td><td>≈0 (1/1836)</td><td>Shells/orbitals</td></tr></tbody></table></div><h3 class="learn-subheading">2. Atomic Number and Mass Number</h3><ul class="learn-list"><li><strong>Z (atomic number):</strong> number of protons — defines the element</li><li><strong>A (mass number):</strong> protons + neutrons</li><li><strong>Neutrons:</strong> N = A − Z</li><li>Neutral atom: electrons = protons = Z</li></ul><h3 class="learn-subheading">3. Isotopes</h3><p class="learn-p">Same Z, different A (different neutrons). Same chemical properties; slightly different physical properties. Examples: ³⁵Cl (75%) and ³⁷Cl (25%) → Ar = 35.5</p><h3 class="learn-subheading">4. Electronic Configuration</h3><p class="learn-p">Max electrons per shell: 2n². Shell 1=2, Shell 2=8, Shell 3=18. Examples: Na(Z=11): 2,8,1. Ca(Z=20): 2,8,8,2.</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Relative Atomic Mass:</strong> Ar = weighted average. Cl: (35×75 + 37×25)/100 = 35.5.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Proton (Z, +1, nucleus). Neutron (N=A−Z, neutral). Electron (≈0, −1, shells). Isotopes: same Z, different A. Config: 2,8,18... Ar = weighted average.</div>',
    questions: [
      {
        q: 'The atomic number of an element is:',
        o: [
          'Number of neutrons',
          'Total protons + neutrons',
          'Number of protons in nucleus',
          'Number of outer electrons',
        ],
        a: 2,
        e: 'Atomic number (Z) = number of protons in nucleus. Uniquely identifies the element. In a neutral atom, Z also equals the number of electrons.',
        h: 'Atomic number Z = number of protons (defines the element).',
        yr: 'SS2',
      },
      {
        q: 'Atom with mass number 35 and atomic number 17 has how many neutrons?',
        o: ['17', '35', '18', '52'],
        a: 2,
        e: 'Neutrons = A − Z = 35 − 17 = 18. This is ³⁵Cl (Chlorine-35).',
        h: 'Neutrons = A − Z = 35 − 17 = 18.',
        yr: 'SS2',
      },
      {
        q: 'Isotopes have the same:',
        o: [
          'Mass number, different atomic number',
          'Atomic number (protons) but different mass numbers (neutrons)',
          'Number of neutrons, different protons',
          'Identical chemical and physical properties',
        ],
        a: 1,
        e: 'Isotopes: same Z (same element, same chemical properties), different A (different neutrons, slightly different physical properties).',
        h: 'Isotopes: same Z (protons), different A (neutrons).',
        yr: 'SS2',
      },
      {
        q: 'Electronic configuration of Ca (Z=20) is:',
        o: ['2, 8, 10', '2, 8, 8, 2', '2, 8, 9, 1', '2, 10, 8'],
        a: 1,
        e: 'Ca (Z=20): Shell 1=2, Shell 2=8, Shell 3=8, Shell 4=2. Total: 2+8+8+2=20. Configuration: 2,8,8,2.',
        h: 'Ca (Z=20): 2, 8, 8, 2.',
        yr: 'SS2',
      },
      {
        q: 'Chlorine: ³⁵Cl (75%) and ³⁷Cl (25%). Relative atomic mass?',
        o: ['36', '35.5', '37', '35'],
        a: 1,
        e: 'Ar = (35×75/100) + (37×25/100) = 26.25 + 9.25 = 35.5.',
        h: 'Ar = (35×75 + 37×25)/100 = 35.5.',
        yr: 'SS2',
      },
      {
        q: 'Maximum electrons in the third shell (M shell) is:',
        o: ['8', '18', '32', '2'],
        a: 1,
        e: 'Max electrons in shell n = 2n². Shell 3: 2×9 = 18.',
        h: 'Max electrons shell 3 = 2×3² = 18.',
        yr: 'SS2',
      },
      {
        q: 'Chemical properties of an element are determined by:',
        o: [
          'Neutrons',
          'Protons',
          'Electrons (especially valence electrons in outer shell)',
          'Nucleons',
        ],
        a: 2,
        e: 'Chemical properties determined by electrons — specifically valence (outermost shell) electrons. They participate in bonding and determine reactivity.',
        h: 'Chemical properties: determined by valence (outer shell) electrons.',
        yr: 'SS2',
      },
      {
        q: 'Ion Na⁺ has electronic configuration 2,8. Na⁺ has:',
        o: [
          '11 electrons and 11 protons',
          '10 electrons and 11 protons',
          '10 electrons and 10 protons',
          '11 electrons and 10 protons',
        ],
        a: 1,
        e: 'Na Z=11 → 11 protons (unchanged). Na⁺ lost 1 electron: 11−1=10 electrons. Config: 2+8=10.',
        h: 'Na⁺: 11 protons (Z=11), 10 electrons (lost 1). Config: 2,8.',
        yr: 'SS2',
      },
      {
        q: '¹⁴C differs from ¹²C in that:',
        o: [
          'Different number of protons',
          '2 more neutrons (8 vs 6), making ¹⁴C radioactively unstable',
          '2 more electrons',
          'Different element',
        ],
        a: 1,
        e: '¹²C: Z=6, 6 neutrons. ¹⁴C: Z=6 (still carbon), 8 neutrons. Extra neutrons make ¹⁴C radioactive (half-life 5,730 years). Used in radiocarbon dating.',
        h: '¹⁴C vs ¹²C: same Z=6; ¹⁴C has 8 neutrons (vs 6) → radioactive.',
        yr: 'SS2',
      },
      {
        q: 'Valence electrons are:',
        o: [
          'All electrons in atom',
          'Electrons in first shell only',
          'Electrons in outermost shell — determine chemical behaviour',
          'Neutrons in nucleus',
        ],
        a: 2,
        e: 'Valence electrons = electrons in outermost shell. Determine valency, bonding, and reactivity. Elements in same group have same number of valence electrons → similar chemistry.',
        h: 'Valence electrons = outer shell electrons; determine bonding and chemistry.',
        yr: 'SS2',
      },
      {
        q: 'Element with config 2,8,6 is in which Group?',
        o: ['Group II', 'Group IV', 'Group VI', 'Group VIII'],
        a: 2,
        e: '6 valence electrons = Group VI. Z = 2+8+6 = 16 = Sulphur (Group VI).',
        h: '6 valence electrons = Group VI. Config 2,8,6: Z=16 = Sulphur.',
        yr: 'SS2',
      },
      {
        q: "Rutherford's gold foil experiment showed:",
        o: [
          'All alpha particles deflected 180°',
          'Most alphas passed through; few deflected at large angles → tiny dense positive nucleus',
          'Alphas converted to electrons',
          'Electrons orbit in circles',
        ],
        a: 1,
        e: 'Rutherford (1911): most alphas pass through (atom = mostly empty space); ~1 in 8000 deflected at large angles (concentrated positive charge). Conclusion: tiny dense positive nucleus.',
        h: 'Rutherford: most alphas through; few deflect back → tiny dense nucleus.',
        yr: 'SS2',
      },
      {
        q: 'Config 2,8,8,1 is in which Period?',
        o: ['Period 2', 'Period 3', 'Period 4', 'Period 5'],
        a: 2,
        e: 'Period = number of shells. Config 2,8,8,1 has 4 shells → Period 4. Z=19=Potassium. Group I (1 valence electron).',
        h: '4 shells (2,8,8,1) → Period 4. Z=19 = Potassium.',
        yr: 'SS2',
      },
      {
        q: 'An anion is:',
        o: [
          'Atom lost electrons, positive charge',
          'Atom gained electrons, negative charge',
          'Atom with equal protons and electrons',
          'Atom with more protons than neutrons',
        ],
        a: 1,
        e: 'Anion = gained electrons → negative charge (e.g., O²⁻). Cation = lost electrons → positive charge (e.g., Na⁺).',
        h: 'Anion = gained electrons → negative charge. Cation = lost electrons → positive.',
        yr: 'SS2',
      },
      {
        q: 'Elements X (2,8,2) and Y (2,8,6) form which type of compound?',
        o: [
          'Covalent (share electrons)',
          'Ionic (X loses 2e⁻ to Y → X²⁺ and Y²⁻)',
          'Metallic',
          'No compound forms',
        ],
        a: 1,
        e: 'X has 2 valence electrons (metal, loses electrons). Y has 6 valence electrons (non-metal, gains 2). X loses 2e⁻ to Y → ionic bond. Example: X=Mg, Y=S → MgS.',
        h: 'Metal (2 valence e⁻) + non-metal (6 valence e⁻) → ionic compound.',
        yr: 'SS2',
      },
      {
        q: 'Bohr model proposed electrons:',
        o: [
          'Randomly distributed',
          'Orbit nucleus in specific energy levels; emit or absorb light only when jumping between levels',
          'In plum pudding arrangement',
          'Stationary around nucleus',
        ],
        a: 1,
        e: 'Bohr (1913): electrons in specific quantised energy shells. Jump between levels by absorbing or emitting photons (E=hf). Explained hydrogen line spectrum.',
        h: 'Bohr: electrons in specific shells; emit/absorb light when changing levels.',
        yr: 'SS2',
      },
      {
        q: 'Ar = 24. Isotopes: mass 24 (80%), mass 25 (x%), mass 26 (4%). x = ?',
        o: ['10', '16', '20', '12'],
        a: 1,
        e: '80 + x + 4 = 100 → x = 16.',
        h: '80 + x + 4 = 100 → x = 16%.',
        yr: 'SS2',
      },
      {
        q: 'Cl⁻ (Z=17) contains how many electrons?',
        o: ['16', '17', '18', '35'],
        a: 2,
        e: 'Cl Z=17 → 17 electrons neutral. Cl⁻ gained 1e⁻ → 18 electrons. Config: 2,8,8 (noble gas configuration).',
        h: 'Cl⁻: 17 protons + 1 gained electron = 18 electrons. Config: 2,8,8.',
        yr: 'SS2',
      },
      {
        q: 'Which pair are isotopes?',
        o: ['¹²C and ¹²N', '²³Na and ²⁴Mg', '¹H and ²H', '¹⁶O and ³²S'],
        a: 2,
        e: 'Isotopes = same Z, different A. ¹H (Z=1) and ²H (Z=1) are both hydrogen → isotopes. All other pairs are different elements.',
        h: '¹H and ²H: both Z=1 (hydrogen); different A (0 and 1 neutron) → isotopes.',
        yr: 'SS2',
      },
      {
        q: 'Mass of electron relative to proton is approximately:',
        o: ['Equal (1:1)', '1/1836 of proton mass', 'Twice proton mass', '1/2 proton mass'],
        a: 1,
        e: 'Electron mass ≈ 9.11×10⁻³¹ kg = 1/1836 of proton mass. Electrons have negligible mass — atom mass essentially all in nucleus (protons + neutrons).',
        h: 'Electron mass ≈ 1/1836 of proton mass — negligible for atomic mass.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Chemical Bonding',
    topicCode: 'SS2-CHEM-07',
    module: 'Atomic Structure',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Chemical bonds</span> hold atoms together. Atoms bond to achieve stable noble gas configurations. Bond type depends on the nature of atoms and electronegativity differences.</div><h3 class="learn-subheading">1. Ionic Bonding</h3><p class="learn-p">Electrons transferred from metal to non-metal → ions attract electrostatically. High MP/BP; conduct when dissolved/molten; soluble in water; brittle.</p><h3 class="learn-subheading">2. Covalent Bonding</h3><p class="learn-p">Electrons shared between non-metals. Single (1 pair), double (2 pairs), triple (3 pairs). Low MP/BP; poor conductors; often insoluble in water. Dative bond: both electrons from one atom.</p><h3 class="learn-subheading">3. Metallic Bonding</h3><p class="learn-p">Sea of delocalised electrons around positive metal ions. Explains conductivity, malleability, ductility, lustre.</p><h3 class="learn-subheading">4. Electronegativity</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>EN Difference</th><th>Bond Type</th></tr></thead><tbody><tr><td>0</td><td>Pure covalent</td></tr><tr><td>0.1–1.7</td><td>Polar covalent</td></tr><tr><td>&gt;1.7</td><td>Ionic</td></tr></tbody></table></div><h3 class="learn-subheading">5. VSEPR Shapes</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Molecule</th><th>Bonding Pairs</th><th>Lone Pairs</th><th>Shape</th><th>Angle</th></tr></thead><tbody><tr><td>BeCl₂</td><td>2</td><td>0</td><td>Linear</td><td>180°</td></tr><tr><td>BF₃</td><td>3</td><td>0</td><td>Trigonal planar</td><td>120°</td></tr><tr><td>CH₄</td><td>4</td><td>0</td><td>Tetrahedral</td><td>109.5°</td></tr><tr><td>NH₃</td><td>3</td><td>1</td><td>Pyramidal</td><td>107°</td></tr><tr><td>H₂O</td><td>2</td><td>2</td><td>Bent</td><td>104.5°</td></tr></tbody></table></div><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>VSEPR:</strong> Lone pairs repel more than bonding pairs. CH₄=109.5°, NH₃=107°, H₂O=104.5°. Each lone pair reduces bond angle.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Ionic = electron transfer. Covalent = sharing. Metallic = electron sea. Dative = both electrons from one atom. Lone pairs reduce bond angles.</div>',
    questions: [
      {
        q: 'Ionic bond forms when:',
        o: [
          'Two non-metals share electrons equally',
          'Metal transfers electrons to non-metal forming oppositely charged ions',
          'Metal shares electrons with another metal',
          'Electrons delocalised across many atoms',
        ],
        a: 1,
        e: 'Ionic bonding: electron transfer from metal (loses electrons) to non-metal (gains electrons). Oppositely charged ions attract electrostatically.',
        h: 'Ionic bond = electron TRANSFER (metal→non-metal) → electrostatic attraction.',
        yr: 'SS2',
      },
      {
        q: 'Shape of water molecule (H₂O):',
        o: [
          'Linear (180°)',
          'Trigonal planar (120°)',
          'Tetrahedral (109.5°)',
          'Bent/V-shaped (~104.5°)',
        ],
        a: 3,
        e: 'H₂O: 2 bonding pairs + 2 lone pairs on oxygen. VSEPR: 4 pairs arrange tetrahedrally, but lone pairs compress H–O–H to 104.5° → bent shape.',
        h: 'H₂O: 2 bonding + 2 lone pairs → bent, 104.5°.',
        yr: 'SS2',
      },
      {
        q: 'Metallic conductivity explained by:',
        o: [
          'Metal ions vibrating and carrying current',
          'Free delocalised electrons moving under voltage, carrying charge',
          'Ionic bonds breaking for current flow',
          'No fixed positive ions in metals',
        ],
        a: 1,
        e: 'Free delocalised electrons in metallic bond flow directionally when voltage applied → electrical current. This explains both electrical and thermal conductivity.',
        h: 'Metallic conductivity: free delocalised electrons flow under voltage.',
        yr: 'SS2',
      },
      {
        q: 'A dative (coordinate) bond differs from normal covalent bond in that:',
        o: [
          'Dative bond is weaker',
          'Both electrons in shared pair come from only ONE atom (the donor)',
          'Dative involves electron transfer',
          'Dative only in ionic compounds',
        ],
        a: 1,
        e: 'Normal covalent: each atom contributes 1 electron to shared pair. Dative: one atom (Lewis base/donor) supplies both electrons. Example: NH₄⁺ formation — N donates lone pair to H⁺.',
        h: 'Dative bond: both electrons from ONE atom (the donor).',
        yr: 'SS2',
      },
      {
        q: 'Bond angle in CH₄ is 109.5° because:',
        o: [
          '4 lone pairs around carbon',
          '4 bonding pairs arranged tetrahedrally — equal repulsions maximise separation',
          'Carbon forms 4 double bonds',
          'Ionic bonds between C and H',
        ],
        a: 1,
        e: 'CH₄: 4 bonding pairs, 0 lone pairs. VSEPR: 4 pairs arrange tetrahedrally at 109.5° — equal repulsions, no lone pairs to distort.',
        h: 'CH₄: 4 bonding, 0 lone pairs → perfect tetrahedron, 109.5°.',
        yr: 'SS2',
      },
      {
        q: 'Which molecule has a triple bond?',
        o: ['H₂O', 'O₂', 'N₂', 'CO₂'],
        a: 2,
        e: 'N₂ has triple bond (N≡N): 3 shared pairs. One of the strongest bonds in chemistry (~945 kJ/mol). O₂ has double bond; CO₂ has two double bonds; H₂O has single bonds.',
        h: 'N≡N triple bond (3 shared pairs) — very strong bond.',
        yr: 'SS2',
      },
      {
        q: 'Ionic compounds conduct electricity when dissolved because:',
        o: [
          'Water breaks bonds creating free electrons',
          'Dissolving releases free ions (cations and anions) that carry current toward electrodes',
          'Compound reacts with water to form metals',
          'Water molecules carry electrons',
        ],
        a: 1,
        e: 'NaCl → Na⁺(aq) + Cl⁻(aq). Na⁺ moves to cathode (−); Cl⁻ moves to anode (+). Moving ions carry electrical charge → current.',
        h: 'Ionic solution: ions move to electrodes, carrying charge → conducts electricity.',
        yr: 'SS2',
      },
      {
        q: 'NH₃ bond angle is ~107°, less than 109.5° because:',
        o: [
          'N is smaller than C',
          '1 lone pair on N repels bonding pairs more than bonding pairs repel each other, compressing H–N–H',
          'NH₃ is ionic',
          'H has no electron pairs',
        ],
        a: 1,
        e: 'NH₃: 3 bonding + 1 lone pair. Lone pair (closer to N nucleus) repels bonding pairs more strongly → compresses angle from 109.5° to ~107°.',
        h: 'NH₃: lone pair repels bonding pairs more → 107° (less than 109.5°).',
        yr: 'SS2',
      },
      {
        q: 'EN difference indicating ionic bond:',
        o: ['H–H (0)', 'C–H (~0.4)', 'N–H (~0.9)', 'Na–Cl (~2.1)'],
        a: 3,
        e: 'EN difference >1.7 = ionic. Na (0.9) vs Cl (3.0) = 2.1 → ionic. H–H=0 (pure covalent); C–H=0.4 (polar covalent); N–H=0.9 (polar covalent).',
        h: 'EN difference >1.7 = ionic. Na–Cl ≈ 2.1 → ionic.',
        yr: 'SS2',
      },
      {
        q: 'Covalent molecular compounds are poor conductors because:',
        o: [
          'Their bonds are too strong',
          'No free ions or delocalised electrons — all electrons fixed in bonds between specific atoms',
          'They melt at very high T',
          'They react with electrodes',
        ],
        a: 1,
        e: "Conductivity requires mobile charge carriers. Covalent molecular compounds have all electrons fixed in specific bonds — no free electrons; no ions in solution → don't conduct.",
        h: 'Covalent compounds: no free electrons or ions → cannot conduct electricity.',
        yr: 'SS2',
      },
      {
        q: 'High melting point of ionic compounds explained by:',
        o: [
          'Strong covalent bonds between ions',
          'Strong electrostatic attractions between oppositely charged ions throughout the lattice — requires much energy to overcome',
          'Large atomic masses',
          'Metallic bonding within lattice',
        ],
        a: 1,
        e: 'Ionic lattice: millions of ions held by strong electrostatic forces in all directions. Melting requires overcoming all these forces simultaneously → very high temperatures (NaCl melts at 801°C).',
        h: 'High ionic MP: strong electrostatic forces throughout lattice → much energy to melt.',
        yr: 'SS2',
      },
      {
        q: 'In NH₄⁺ formation, the 4th N–H bond is dative because:',
        o: [
          'H contributes two electrons',
          'N donates its lone pair to H⁺ (empty orbital) — both electrons from N',
          '4th bond is weaker',
          'N loses an electron',
        ],
        a: 1,
        e: 'NH₃ has lone pair on N. H⁺ has empty orbital. N donates lone pair to H⁺ → dative N→H bond. Once formed, all 4 N–H bonds are identical.',
        h: 'NH₄⁺: N lone pair donated to H⁺ → dative bond. All 4 N–H bonds identical once formed.',
        yr: 'SS2',
      },
      {
        q: 'Metallic malleability explained by:',
        o: [
          'Directional ionic bonds allowing sliding',
          'Non-directional electron sea allows ion layers to slide without breaking bond',
          'Weak covalent bonds that break/reform',
          'High electronegativity of metals',
        ],
        a: 1,
        e: 'Metallic bond is non-directional — electron sea surrounds ions from all angles. Layers of metal ions can slide past each other; electron sea maintains bonding throughout.',
        h: 'Metallic malleability: non-directional electron sea allows ion layers to slide.',
        yr: 'SS2',
      },
      {
        q: 'BF₃ has trigonal planar shape. B–F bonds are:',
        o: [
          'At 90° in square arrangement',
          'All in same plane at 120°',
          'Pyramidal at 107°',
          'At 109.5°',
        ],
        a: 1,
        e: 'BF₃: B has 3 bonding pairs, 0 lone pairs. Three pairs maximise separation at 120° in a plane → trigonal planar. B has incomplete octet (6 electrons).',
        h: 'BF₃: 3 bonding pairs, 0 lone pairs → trigonal planar, 120°.',
        yr: 'SS2',
      },
      {
        q: 'Polar covalent bond forms when:',
        o: [
          'Atoms have identical electronegativity',
          'Different electronegativities cause unequal sharing → partial charges δ+ and δ−',
          'Electrons completely transferred',
          'Both atoms are metals',
        ],
        a: 1,
        e: 'Unequal sharing due to EN difference → more EN atom attracts electrons more → δ− on it; δ+ on other. Example: Hδ+–Clδ− (Cl more EN).',
        h: 'Polar covalent: unequal sharing → δ+ and δ− partial charges.',
        yr: 'SS2',
      },
      {
        q: 'Which has giant ionic lattice structure?',
        o: ['Glucose', 'Diamond', 'NaCl', 'Iodine'],
        a: 2,
        e: 'NaCl = giant ionic lattice (Na⁺ and Cl⁻ in regular 3D array). Glucose = simple covalent molecule. Diamond = giant covalent. Iodine = simple covalent molecular solid.',
        h: 'Giant ionic lattice = NaCl. Giant covalent = diamond/silica.',
        yr: 'SS2',
      },
      {
        q: 'More shared pairs = ?',
        o: [
          'Weaker and longer bond',
          'Stronger and shorter bond: single < double < triple',
          'Bond strength unrelated to shared pairs',
          'Triple bonds weakest',
        ],
        a: 1,
        e: 'Bond order ↑ → stronger bond, shorter length. N–N (single, ~163 kJ/mol) < N=N (double, ~418) < N≡N (triple, ~945 kJ/mol).',
        h: 'More shared pairs → stronger, shorter bond. Single < Double < Triple.',
        yr: 'SS2',
      },
      {
        q: 'Gas, low BP, no conductivity — which bond type?',
        o: ['Ionic', 'Metallic', 'Giant covalent', 'Simple covalent (molecular)'],
        a: 3,
        e: "Simple covalent (molecular): low MP/BP (weak intermolecular forces), gases/liquids at room T, no free ions/electrons → don't conduct. Examples: CH₄, CO₂, HCl.",
        h: 'Gas, low BP, no conductivity = simple covalent molecular compound.',
        yr: 'SS2',
      },
      {
        q: 'NaCl dot-and-cross shows:',
        o: [
          'Electrons shared equally',
          "Na's electron transferred to Cl → Na⁺ (2,8) and Cl⁻ (2,8,8)",
          'Shared electrons between Na and Cl',
          'Both with 8 outer electrons still bonded',
        ],
        a: 1,
        e: 'Ionic compounds show complete electron transfer. Na loses 1e⁻ (becomes Na⁺, 2,8); Cl gains 1e⁻ (becomes Cl⁻, 2,8,8). Charges shown in brackets.',
        h: 'NaCl dot-and-cross: complete electron transfer. Na⁺(2,8) and Cl⁻(2,8,8).',
        yr: 'SS2',
      },
      {
        q: 'VSEPR predicts molecular shapes based on:',
        o: [
          'Bond lengths and energies',
          'Electron pairs (bonding + lone) arranging to minimise repulsion around central atom',
          'Atomic radii',
          'Electronegativity differences',
        ],
        a: 1,
        e: 'VSEPR: electron pairs repel → arrange to maximise separation/minimise repulsion. Lone pairs repel more than bonding pairs. Shape and bond angles result from this arrangement.',
        h: 'VSEPR: electron pairs repel → maximise separation → determines shape and angles.',
        yr: 'SS2',
      },
    ],
  },
  {
    topic: 'Chemical Reactions and Stoichiometry',
    topicCode: 'SS2-CHEM-08',
    module: 'Chemical Calculations',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Stoichiometry</span> is the quantitative study of chemical reactions using balanced equations to calculate amounts of reactants and products.</div><h3 class="learn-subheading">1. The Mole Concept</h3><ul class="learn-list"><li>1 mol = 6.02 × 10²³ particles (Avogadro\'s number)</li><li>n = m/M (moles = mass ÷ molar mass)</li><li>At STP: 1 mol gas = 22.4 L</li><li>Concentration c = n/V (mol/L)</li></ul><h3 class="learn-subheading">2. Types of Reactions</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Type</th><th>Example</th></tr></thead><tbody><tr><td>Synthesis</td><td>2H₂ + O₂ → 2H₂O</td></tr><tr><td>Decomposition</td><td>2H₂O₂ → 2H₂O + O₂</td></tr><tr><td>Displacement</td><td>Zn + CuSO₄ → ZnSO₄ + Cu</td></tr><tr><td>Double decomposition</td><td>AgNO₃ + NaCl → AgCl↓ + NaNO₃</td></tr></tbody></table></div><h3 class="learn-subheading">3. Stoichiometric Calculations</h3><ol class="learn-list"><li>Write and balance equation</li><li>n = m/M for given substance</li><li>Use mole ratio from equation</li><li>m = n × M for required substance</li></ol><h3 class="learn-subheading">4. Limiting Reagent and % Yield</h3><ul class="learn-list"><li>Limiting reagent = completely consumed first; limits product</li><li>% yield = (actual/theoretical) × 100</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Key formulae:</strong> n=m/M. n=V/22.4 (L at STP). c=n/V (mol/L). N=n×6.02×10²³.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> 1 mol = 6.02×10²³ particles. n=m/M. 1 mol gas = 22.4 L at STP. Mole ratio from balanced equation. Limiting reagent consumed first. % yield = (actual/theoretical) × 100.</div>',
    questions: [
      {
        q: 'Moles in 56 g of Fe (Ar=56)?',
        o: ['56 mol', '0.5 mol', '1 mol', '2 mol'],
        a: 2,
        e: 'n = m/M = 56/56 = 1 mol.',
        h: 'n = m/M = 56 ÷ 56 = 1 mol.',
        yr: 'SS2',
      },
      {
        q: "Avogadro's number is:",
        o: [
          '6.02 × 10²³ per mole',
          '22.4 × 10²³ per mole',
          '1.66 × 10⁻²⁷ per mole',
          '6.02 × 10¹² per mole',
        ],
        a: 0,
        e: 'Nₐ = 6.02 × 10²³ per mole — number of particles in one mole of any substance.',
        h: "Avogadro's number = 6.02 × 10²³ per mole.",
        yr: 'SS2',
      },
      {
        q: 'Volume of 1 mole of any gas at STP:',
        o: ['22.4 L', '24.0 L', '11.2 L', '44.8 L'],
        a: 0,
        e: "At STP (0°C, 1 atm), molar volume = 22.4 L/mol for all ideal gases (Avogadro's Law).",
        h: 'Molar volume at STP = 22.4 L/mol.',
        yr: 'SS2',
      },
      {
        q: '2H₂ + O₂ → 2H₂O. Moles of water from 4 mol H₂?',
        o: ['2 mol', '4 mol', '8 mol', '1 mol'],
        a: 1,
        e: 'H₂:H₂O ratio = 2:2 = 1:1. So 4 mol H₂ → 4 mol H₂O.',
        h: '2H₂ + O₂ → 2H₂O. H₂:H₂O=1:1. 4 mol H₂ → 4 mol H₂O.',
        yr: 'SS2',
      },
      {
        q: 'Mass of CO₂ from burning 12 g carbon? (C=12, O=16)',
        o: ['12 g', '22 g', '44 g', '28 g'],
        a: 2,
        e: 'C + O₂ → CO₂. n(C)=12/12=1 mol → n(CO₂)=1 mol. M(CO₂)=44. Mass=1×44=44 g.',
        h: 'C+O₂→CO₂. n(C)=1mol→n(CO₂)=1mol. Mass=44g.',
        yr: 'SS2',
      },
      {
        q: 'The limiting reagent is:',
        o: [
          'Reactant in largest amount',
          'Reactant completely consumed first, determining maximum product',
          'Product formed in smallest amount',
          'Reactant with highest molar mass',
        ],
        a: 1,
        e: 'Limiting reagent runs out first, stopping further product formation. It limits the yield. Other reactant(s) are in excess.',
        h: 'Limiting reagent = consumed first; determines max product. Others = in excess.',
        yr: 'SS2',
      },
      {
        q: '% yield calculated as:',
        o: [
          '(theoretical/actual) × 100',
          '(actual/theoretical) × 100',
          '(actual − theoretical) × 100',
          '(mol product/mol reactant) × 100',
        ],
        a: 1,
        e: '% yield = (actual yield ÷ theoretical yield) × 100. Always ≤ 100%.',
        h: '% yield = (actual/theoretical) × 100. Always ≤ 100%.',
        yr: 'SS2',
      },
      {
        q: 'Molar mass of CaCO₃? (Ca=40, C=12, O=16)',
        o: ['68', '100', '84', '116'],
        a: 1,
        e: 'M = 40 + 12 + 3(16) = 40 + 12 + 48 = 100 g/mol.',
        h: 'M(CaCO₃) = 40+12+3(16) = 100 g/mol.',
        yr: 'SS2',
      },
      {
        q: 'CaCO₃ → CaO + CO₂. Mass of CaO from 25 g CaCO₃? (CaCO₃=100, CaO=56)',
        o: ['25 g', '14 g', '56 g', '100 g'],
        a: 1,
        e: 'n(CaCO₃)=25/100=0.25 mol → n(CaO)=0.25 mol. Mass=0.25×56=14 g.',
        h: 'n=0.25 mol → mass CaO = 0.25×56 = 14 g.',
        yr: 'SS2',
      },
      {
        q: 'Molar concentration =',
        o: [
          'Mass of solute ÷ volume',
          'Moles of solute ÷ volume in litres',
          'Mass of solute ÷ mass of solvent',
          'Volume of solute ÷ volume of solvent',
        ],
        a: 1,
        e: 'Concentration c = n/V, where n = moles and V = volume in litres (dm³). Units: mol/L or mol dm⁻³.',
        h: 'Concentration c = n/V (mol/L). n=moles; V=volume in litres.',
        yr: 'SS2',
      },
      {
        q: 'Molecules in 0.5 mol water? (Nₐ=6.02×10²³)',
        o: ['3.01 × 10²³', '6.02 × 10²³', '1.204 × 10²⁴', '6.02 × 10²²'],
        a: 0,
        e: 'N = n × Nₐ = 0.5 × 6.02×10²³ = 3.01×10²³.',
        h: 'N = 0.5 × 6.02×10²³ = 3.01×10²³ molecules.',
        yr: 'SS2',
      },
      {
        q: 'Volume of CO₂ at STP from 10 g CaCO₃? (CaCO₃=100, Vm=22.4 L/mol)',
        o: ['22.4 L', '2.24 L', '11.2 L', '4.48 L'],
        a: 1,
        e: 'n(CaCO₃)=10/100=0.1 mol → n(CO₂)=0.1 mol. V=0.1×22.4=2.24 L.',
        h: 'n=0.1 mol → V=0.1×22.4=2.24 L at STP.',
        yr: 'SS2',
      },
      {
        q: 'Theoretical yield 20 g, obtained 15 g. % yield?',
        o: ['133%', '75%', '25%', '15%'],
        a: 1,
        e: '% yield = (15/20)×100 = 75%.',
        h: '% yield = (15/20)×100 = 75%.',
        yr: 'SS2',
      },
      {
        q: 'Empirical formula shows:',
        o: [
          'Actual atoms in one molecule',
          'Simplest whole-number ratio of atoms of each element',
          'Arrangement of atoms in space',
          'Molar mass of compound',
        ],
        a: 1,
        e: 'Empirical formula = simplest ratio of atoms. Example: Glucose C₆H₁₂O₆ has empirical formula CH₂O (ratio 1:2:1).',
        h: 'Empirical formula = simplest whole-number ratio of atoms.',
        yr: 'SS2',
      },
      {
        q: 'Compound: 40% C, 6.7% H, 53.3% O. Empirical formula? (C=12,H=1,O=16)',
        o: ['C₂H₄O₂', 'CHO', 'CH₂O', 'C₂H₂O'],
        a: 2,
        e: 'Moles: C=40/12=3.33; H=6.7/1=6.7; O=53.3/16=3.33. Divide by smallest (3.33): C=1, H=2, O=1. EF=CH₂O.',
        h: 'C:H:O = 3.33:6.7:3.33 = 1:2:1 → CH₂O.',
        yr: 'SS2',
      },
      {
        q: 'Example of double decomposition reaction:',
        o: [
          '2Na + Cl₂ → 2NaCl',
          'CaCO₃ → CaO + CO₂',
          'NaCl + AgNO₃ → AgCl↓ + NaNO₃',
          'Zn + H₂SO₄ → ZnSO₄ + H₂',
        ],
        a: 2,
        e: 'Double decomposition: AB + CD → AD + CB (ion exchange). NaCl + AgNO₃ → AgCl↓ + NaNO₃. AgCl precipitates.',
        h: 'Double decomposition: AB+CD→AD+CB. NaCl+AgNO₃→AgCl↓+NaNO₃.',
        yr: 'SS2',
      },
      {
        q: 'Mix 2 mol H₂ and 1 mol O₂. Limiting reagent?',
        o: ['H₂', 'O₂', 'Neither — both completely consumed (exact ratio)', 'Water'],
        a: 2,
        e: '2H₂ + O₂ needs 2:1 ratio. 2 mol H₂ : 1 mol O₂ = exactly 2:1. Both consumed completely — neither in excess.',
        h: '2 mol H₂ : 1 mol O₂ = exact 2:1 ratio → both consumed; no limiting reagent.',
        yr: 'SS2',
      },
      {
        q: 'Mass of O₂ to burn 4 g H₂? (H=1, O=16; 2H₂+O₂→2H₂O)',
        o: ['4 g', '16 g', '32 g', '8 g'],
        a: 2,
        e: 'n(H₂)=4/2=2 mol. Ratio H₂:O₂=2:1 → n(O₂)=1 mol. Mass=1×32=32 g.',
        h: 'n(H₂)=2 mol → n(O₂)=1 mol → mass O₂=32 g.',
        yr: 'SS2',
      },
      {
        q: '0.2 mol NaOH in 250 cm³ water: concentration?',
        o: ['0.05 mol/L', '0.8 mol/L', '0.5 mol/L', '2.0 mol/L'],
        a: 1,
        e: 'c = n/V = 0.2/(0.25) = 0.8 mol/L.',
        h: 'c = 0.2/0.25 = 0.8 mol/L.',
        yr: 'SS2',
      },
      {
        q: 'Empirical formula CH₂, molar mass 56 g/mol. Molecular formula? (C=12,H=1)',
        o: ['CH₂', 'C₂H₄', 'C₃H₆', 'C₄H₈'],
        a: 3,
        e: 'EF mass = 12+2=14. Ratio = 56/14=4. Molecular formula = 4×CH₂ = C₄H₈.',
        h: 'EF mass=14. Ratio=56/14=4. MF=C₄H₈.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 9: GAS LAWS
  // =========================================================================
  {
    topic: 'Gas Laws',
    topicCode: 'SS2-CHEM-09',
    module: 'Physical Chemistry',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro">The <span class="learn-keyword">gas laws</span> describe how the pressure, volume, and temperature of a gas are related. They apply to ideal gases — gases that have no intermolecular forces and whose molecules have negligible volume. Real gases approximate ideal behaviour at low pressures and high temperatures.</div><h3 class="learn-subheading">1. Boyle\'s Law (1662)</h3><p class="learn-p">At constant temperature, the pressure of a fixed mass of gas is inversely proportional to its volume:</p><p class="learn-p"><strong>P ∝ 1/V (constant T and n) → PV = constant → P₁V₁ = P₂V₂</strong></p><ul class="learn-list"><li>If pressure doubles, volume halves (at constant T).</li><li>Graph of P vs 1/V: straight line through origin.</li></ul><h3 class="learn-subheading">2. Charles\' Law (1787)</h3><p class="learn-p">At constant pressure, the volume of a fixed mass of gas is directly proportional to its absolute temperature (in Kelvin):</p><p class="learn-p"><strong>V ∝ T (constant P and n) → V/T = constant → V₁/T₁ = V₂/T₂</strong></p><ul class="learn-list"><li>T must always be in Kelvin: K = °C + 273</li><li>Gas volume → 0 as T → 0 K (absolute zero = −273°C)</li></ul><h3 class="learn-subheading">3. Pressure Law (Gay-Lussac\'s Law)</h3><p class="learn-p">At constant volume, pressure is directly proportional to absolute temperature:</p><p class="learn-p"><strong>P/T = constant → P₁/T₁ = P₂/T₂</strong></p><h3 class="learn-subheading">4. Combined Gas Law</h3><p class="learn-p"><strong>P₁V₁/T₁ = P₂V₂/T₂</strong></p><p class="learn-p">This combines Boyle\'s and Charles\' Laws and applies when all three variables change.</p><h3 class="learn-subheading">5. Ideal Gas Equation</h3><p class="learn-p"><strong>PV = nRT</strong></p><ul class="learn-list"><li>P = pressure (Pa or atm)</li><li>V = volume (m³ or L)</li><li>n = moles of gas</li><li>R = gas constant = 8.314 J mol⁻¹ K⁻¹ (or 0.0821 L atm mol⁻¹ K⁻¹)</li><li>T = absolute temperature (K)</li></ul><h3 class="learn-subheading">6. Avogadro\'s Law</h3><p class="learn-p">Equal volumes of all gases at the same temperature and pressure contain equal numbers of molecules. At STP: 1 mole of any gas occupies 22.4 L.</p><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Law</th><th>Variables Constant</th><th>Relationship</th></tr></thead><tbody><tr><td>Boyle\'s</td><td>T, n</td><td>PV = k; P₁V₁ = P₂V₂</td></tr><tr><td>Charles\'</td><td>P, n</td><td>V/T = k; V₁/T₁ = V₂/T₂</td></tr><tr><td>Gay-Lussac\'s</td><td>V, n</td><td>P/T = k; P₁/T₁ = P₂/T₂</td></tr><tr><td>Combined</td><td>n</td><td>P₁V₁/T₁ = P₂V₂/T₂</td></tr><tr><td>Ideal gas</td><td>—</td><td>PV = nRT</td></tr></tbody></table></div><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Always convert to Kelvin:</strong> T(K) = T(°C) + 273. Forgetting this is the most common gas law mistake. At STP: T = 273 K, P = 101,325 Pa (1 atm).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Boyle: P₁V₁=P₂V₂ (constant T). Charles: V₁/T₁=V₂/T₂ (constant P). Combined: P₁V₁/T₁=P₂V₂/T₂. Ideal: PV=nRT. Avogadro: 1 mol gas = 22.4 L at STP. Always use Kelvin.</div>',
    questions: [
      {
        q: "Boyle's Law states that at constant temperature, the pressure and volume of a fixed mass of gas are:",
        o: [
          'Directly proportional (P ∝ V)',
          'Inversely proportional (P ∝ 1/V)',
          'Independent of each other',
          'Equal to each other',
        ],
        a: 1,
        e: "Boyle's Law (1662): at constant temperature, P ∝ 1/V → PV = constant. If pressure doubles, volume halves; if volume triples, pressure drops to one-third. A graph of P vs 1/V is a straight line through the origin.",
        h: "Boyle's Law: P ∝ 1/V at constant T. PV = constant. P₁V₁ = P₂V₂.",
        yr: 'SS2',
      },
      {
        q: 'A gas has volume 500 cm³ at 2 atm. What is its volume at 5 atm (constant temperature)?',
        o: ['1250 cm³', '200 cm³', '250 cm³', '100 cm³'],
        a: 1,
        e: "Boyle's Law: P₁V₁ = P₂V₂. 2 × 500 = 5 × V₂. V₂ = 1000/5 = 200 cm³.",
        h: 'P₁V₁ = P₂V₂ → 2×500 = 5×V₂ → V₂ = 200 cm³.',
        yr: 'SS2',
      },
      {
        q: "Charles' Law requires temperature to be expressed in:",
        o: [
          'Degrees Celsius (°C)',
          'Degrees Fahrenheit (°F)',
          'Kelvin (K)',
          'Any temperature scale',
        ],
        a: 2,
        e: "Charles' Law states V ∝ T (absolute temperature). Temperature must be in Kelvin because the relationship is proportional — at 0 K (absolute zero), volume would theoretically be zero. Using Celsius would give negative volumes at temperatures below 0°C, which is physically meaningless.",
        h: 'Gas laws require Kelvin. T(K) = T(°C) + 273. Never use Celsius in gas law calculations.',
        yr: 'SS2',
      },
      {
        q: 'A gas occupies 300 cm³ at 27°C. What volume will it occupy at 127°C at constant pressure?',
        o: ['600 cm³', '400 cm³', '150 cm³', '300 cm³'],
        a: 1,
        e: "Charles' Law: V₁/T₁ = V₂/T₂. T₁ = 27+273 = 300 K; T₂ = 127+273 = 400 K. V₂ = V₁ × T₂/T₁ = 300 × 400/300 = 400 cm³.",
        h: 'V₁/T₁=V₂/T₂. T₁=300K, T₂=400K. V₂=300×400/300=400 cm³.',
        yr: 'SS2',
      },
      {
        q: "The ideal gas equation is PV = nRT. What does 'n' represent?",
        o: [
          'The pressure of the gas',
          'The number of moles of gas',
          'The temperature in Kelvin',
          'The gas constant',
        ],
        a: 1,
        e: 'In PV = nRT: P = pressure, V = volume, n = number of moles of gas, R = universal gas constant (8.314 J mol⁻¹K⁻¹), T = absolute temperature (K). The equation relates all state variables of an ideal gas.',
        h: 'PV = nRT: n = number of moles of gas.',
        yr: 'SS2',
      },
      {
        q: 'Absolute zero (0 K or −273°C) is the temperature at which:',
        o: [
          'All gases condense into solids',
          'The volume of a gas would theoretically reach zero and all molecular motion ceases',
          'Gases exert maximum pressure',
          'Water freezes at any pressure',
        ],
        a: 1,
        e: "Absolute zero (0 K = −273.15°C) is the theoretical temperature at which an ideal gas would have zero volume (Charles' Law extrapolation) and all thermal motion ceases. In practice, real gases liquefy before reaching absolute zero.",
        h: 'Absolute zero (0 K = −273°C) = theoretical zero volume; all molecular motion stops.',
        yr: 'SS2',
      },
      {
        q: "Gay-Lussac's Law (Pressure Law) states that at constant volume:",
        o: [
          'Pressure is inversely proportional to temperature',
          'Pressure is directly proportional to absolute temperature: P/T = constant',
          'Pressure is independent of temperature',
          'Pressure equals temperature times volume',
        ],
        a: 1,
        e: "Gay-Lussac's Pressure Law: at constant volume, P ∝ T (Kelvin). P/T = constant → P₁/T₁ = P₂/T₂. Example: heating a sealed container increases gas pressure (risk of explosion). Car tyres have higher pressure in hot weather.",
        h: "Gay-Lussac's Law: P/T = constant at fixed V. P₁/T₁ = P₂/T₂.",
        yr: 'SS2',
      },
      {
        q: 'A gas at 300 K and 1 atm occupies 2 L. What volume does it occupy at 600 K and 2 atm?',
        o: ['4 L', '2 L', '1 L', '8 L'],
        a: 1,
        e: 'Combined gas law: P₁V₁/T₁ = P₂V₂/T₂. (1×2)/300 = (2×V₂)/600. 2/300 = 2V₂/600. 2/300 = V₂/300. V₂ = 2 L. (Pressure doubled reduces V by ½; temperature doubled increases V by 2 → net effect: same volume.)',
        h: 'P₁V₁/T₁=P₂V₂/T₂ → (1×2)/300=(2×V₂)/600 → V₂=2 L.',
        yr: 'SS2',
      },
      {
        q: "Avogadro's Law states that equal volumes of gases at the same temperature and pressure:",
        o: [
          'Have equal masses',
          'Have equal numbers of molecules (and moles)',
          'Have equal densities',
          'Exert equal pressures on their containers',
        ],
        a: 1,
        e: "Avogadro's Law: at the same T and P, equal volumes of any gas contain equal numbers of molecules (and therefore equal moles). This is why 1 mole of any ideal gas at STP occupies 22.4 L, regardless of whether it is H₂, O₂, CO₂, or N₂.",
        h: "Avogadro's Law: equal volumes (same T, P) = equal moles/molecules. 1 mol any gas = 22.4 L at STP.",
        yr: 'SS2',
      },
      {
        q: 'Using PV = nRT (R = 0.0821 L·atm/mol·K), what is the volume of 2 moles of an ideal gas at 300 K and 1 atm?',
        o: ['24.63 L', '49.26 L', '12.31 L', '82.1 L'],
        a: 1,
        e: 'PV = nRT → V = nRT/P = (2 × 0.0821 × 300)/1 = (49.26)/1 = 49.26 L.',
        h: 'V = nRT/P = (2 × 0.0821 × 300)/1 = 49.26 L.',
        yr: 'SS2',
      },
      {
        q: 'A graph of volume (V) against temperature (T in Kelvin) at constant pressure gives:',
        o: [
          'A curve (hyperbola)',
          "A straight line through the origin, confirming V ∝ T (Charles' Law)",
          'A horizontal line (V is constant)',
          'A vertical line (T is constant)',
        ],
        a: 1,
        e: "Charles' Law: V ∝ T (Kelvin) at constant P. A plot of V vs T(K) gives a straight line passing through the origin (or extrapolating to zero at 0 K). This linear relationship confirms direct proportionality.",
        h: "V vs T(K) graph = straight line through origin (Charles' Law: V ∝ T).",
        yr: 'SS2',
      },
      {
        q: 'A sealed container of gas at 300 K has pressure 2 atm. If heated to 450 K (constant volume), what is the new pressure?',
        o: ['3 atm', '1.33 atm', '4 atm', '1 atm'],
        a: 0,
        e: "Gay-Lussac's Law: P₁/T₁ = P₂/T₂. P₂ = P₁ × T₂/T₁ = 2 × 450/300 = 2 × 1.5 = 3 atm.",
        h: 'P₁/T₁=P₂/T₂ → P₂=2×(450/300)=3 atm.',
        yr: 'SS2',
      },
      {
        q: 'What volume (at STP) does 11 g of CO₂ occupy? (C=12, O=16; Vm=22.4 L/mol at STP)',
        o: ['5.6 L', '22.4 L', '11.2 L', '2.24 L'],
        a: 0,
        e: 'M(CO₂) = 12 + 32 = 44 g/mol. n = 11/44 = 0.25 mol. V = n × 22.4 = 0.25 × 22.4 = 5.6 L.',
        h: 'n=11/44=0.25 mol. V=0.25×22.4=5.6 L at STP.',
        yr: 'SS2',
      },
      {
        q: 'An ideal gas differs from a real gas in that an ideal gas:',
        o: [
          'Has higher density than real gases',
          'Has no intermolecular forces and occupies no volume (point masses) — real gases have both intermolecular forces and volume',
          'Cannot be liquefied under any conditions',
          'Obeys gas laws only at high pressures',
        ],
        a: 1,
        e: 'An ideal gas: (1) has no intermolecular attractive or repulsive forces; (2) the gas molecules themselves occupy no volume (point masses). Real gases deviate from ideal behaviour at high pressures (molecules forced close together) and low temperatures (intermolecular forces become significant).',
        h: 'Ideal gas: no intermolecular forces, negligible molecular volume. Real gas deviates at high P, low T.',
        yr: 'SS2',
      },
      {
        q: 'The SI unit of pressure is:',
        o: ['Atmosphere (atm)', 'Millimetres of mercury (mmHg)', 'Pascal (Pa)', 'Bar'],
        a: 2,
        e: 'The SI unit of pressure is the Pascal (Pa), where 1 Pa = 1 N/m². Standard atmospheric pressure = 101,325 Pa = 101.325 kPa. Other common units: 1 atm = 760 mmHg = 101.325 kPa. R = 8.314 J mol⁻¹K⁻¹ when using Pa and m³.',
        h: 'SI unit of pressure = Pascal (Pa). 1 atm = 101,325 Pa = 760 mmHg.',
        yr: 'SS2',
      },
      {
        q: "According to Gay-Lussac's Law of combining volumes, when gases react, they do so in:",
        o: [
          'Ratios of their molar masses',
          'Simple whole number ratios of their volumes (at same T and P) — consistent with the mole ratios in the balanced equation',
          'Equal volumes always',
          'Ratios of their densities',
        ],
        a: 1,
        e: "Gay-Lussac's Law of combining volumes: gases react in simple whole number ratios of volumes (e.g., H₂:O₂:H₂O = 2:1:2 by volume). This is consistent with Avogadro's Law — equal volumes contain equal moles, so volume ratios = mole ratios from the balanced equation.",
        h: 'Gay-Lussac combining volumes: gases react in simple whole-number volume ratios = mole ratios.',
        yr: 'SS2',
      },
      {
        q: 'A gas occupies 4 L at STP. How many moles does it contain? (Vm = 22.4 L/mol)',
        o: ['4 mol', '22.4 mol', '0.179 mol', '89.6 mol'],
        a: 2,
        e: 'n = V/Vm = 4/22.4 = 0.179 mol (approximately 1/5.6 mol).',
        h: 'n = V/Vm = 4/22.4 = 0.179 mol at STP.',
        yr: 'SS2',
      },
      {
        q: 'Real gases deviate most from ideal behaviour at:',
        o: [
          'Low pressures and high temperatures',
          'High pressures and low temperatures',
          'Standard temperature and pressure (STP)',
          'High pressures and high temperatures',
        ],
        a: 1,
        e: 'At high pressures, molecules are forced close together — their own volume becomes significant (unlike point masses). At low temperatures, molecules move slowly — intermolecular attractive forces become significant and the gas may liquefy. Both conditions cause major deviation from ideal behaviour.',
        h: 'Real gas deviates most at HIGH pressure (volume of molecules matters) and LOW temperature (forces matter).',
        yr: 'SS2',
      },
      {
        q: 'Which of the following correctly converts 25°C to Kelvin?',
        o: ['248 K', '298 K', '273 K', '25 K'],
        a: 1,
        e: "T(K) = T(°C) + 273 = 25 + 273 = 298 K. This conversion is essential for all gas law calculations — Charles' Law and the ideal gas equation require absolute temperature in Kelvin.",
        h: 'T(K) = T(°C) + 273 = 25 + 273 = 298 K.',
        yr: 'SS2',
      },
      {
        q: 'Using the combined gas law, a gas at 27°C, 1 atm and 3 L is heated to 127°C and compressed to 2 atm. What is the new volume?',
        o: ['1.5 L', '2 L', '3 L', '6 L'],
        a: 0,
        e: 'T₁ = 300 K, T₂ = 400 K. P₁V₁/T₁ = P₂V₂/T₂ → (1×3)/300 = (2×V₂)/400. 3/300 = 2V₂/400. 0.01 = V₂/200. V₂ = 2 L. Wait: (1×3)/300 = 0.01; (2×V₂)/400 → 0.01 = 2V₂/400 → V₂ = (0.01×400)/2 = 4/2 = 2 L.',
        h: 'P₁V₁/T₁=P₂V₂/T₂ → (1×3)/300=(2×V₂)/400 → V₂=2 L.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 10: ELECTROCHEMISTRY
  // =========================================================================
  {
    topic: 'Electrochemistry',
    topicCode: 'SS2-CHEM-10',
    module: 'Electrochemistry',
    term: 'Second Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Electrochemistry</span> is the branch of chemistry that deals with the relationship between chemical reactions and electrical energy. It includes electrolysis (using electricity to drive chemical reactions) and electrochemical cells (using chemical reactions to produce electricity).</div><h3 class="learn-subheading">1. Electrolysis</h3><p class="learn-p">Electrolysis uses direct current (DC) to decompose an electrolyte (ionic compound in solution or molten). Key components:</p><ul class="learn-list"><li><strong>Electrolyte:</strong> ionic compound that conducts when dissolved or molten</li><li><strong>Cathode:</strong> negative electrode — cations (positive ions) move here and are reduced (gain electrons)</li><li><strong>Anode:</strong> positive electrode — anions (negative ions) move here and are oxidised (lose electrons)</li></ul><h3 class="learn-subheading">2. Electrolysis of Key Electrolytes</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Electrolyte</th><th>Cathode Product</th><th>Anode Product</th></tr></thead><tbody><tr><td>Dilute H₂SO₄</td><td>H₂ gas</td><td>O₂ gas</td></tr><tr><td>Dilute HCl</td><td>H₂ gas</td><td>Cl₂ gas (or O₂ if very dilute)</td></tr><tr><td>Molten NaCl</td><td>Na metal</td><td>Cl₂ gas</td></tr><tr><td>CuSO₄ (Cu electrodes)</td><td>Cu deposited</td><td>Cu dissolves</td></tr><tr><td>CuSO₄ (Pt electrodes)</td><td>Cu deposited</td><td>O₂ gas</td></tr></tbody></table></div><h3 class="learn-subheading">3. Faraday\'s Laws of Electrolysis</h3><ul class="learn-list"><li><strong>First Law:</strong> The mass of substance deposited at an electrode is proportional to the quantity of electricity (charge) passed. m ∝ Q</li><li><strong>Second Law:</strong> When the same quantity of electricity is passed through different electrolytes, the masses deposited are proportional to the equivalent masses (molar mass ÷ valency).</li><li><strong>Key formula:</strong> Q = It (charge = current × time). Mass deposited: m = (M × I × t)/(n × F), where F = Faraday constant = 96,500 C/mol</li></ul><h3 class="learn-subheading">4. Applications of Electrolysis</h3><ul class="learn-list"><li>Electroplating (coating metal with another metal)</li><li>Extraction of metals (Al from Al₂O₃, Na from molten NaCl)</li><li>Purification of copper</li><li>Manufacture of chlorine and NaOH (chlor-alkali industry)</li></ul><h3 class="learn-subheading">5. Electrochemical Cells</h3><p class="learn-p">Galvanic/voltaic cells convert chemical energy to electrical energy. The cell with zinc and copper electrodes (Daniell cell): Zn|Zn²⁺||Cu²⁺|Cu. Anode: Zn → Zn²⁺ + 2e⁻ (oxidation). Cathode: Cu²⁺ + 2e⁻ → Cu (reduction).</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>OILRIG:</strong> Oxidation Is Loss (of electrons); Reduction Is Gain (of electrons). At cathode: reduction. At anode: oxidation. Cations go to cathode; anions go to anode.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Electrolysis: cathode = reduction (cations); anode = oxidation (anions). Faraday: Q=It; m=(MIt)/(nF); F=96,500 C/mol. Applications: electroplating, metal extraction, chlor-alkali. Galvanic cells: chemical → electrical energy.</div>',
    questions: [
      {
        q: 'In electrolysis, the cathode is the electrode where:',
        o: [
          'Oxidation occurs and anions are discharged',
          'Reduction occurs and cations gain electrons (are discharged)',
          'The positive terminal of the battery connects',
          'Oxygen is always produced',
        ],
        a: 1,
        e: 'The cathode is the negative electrode in electrolysis. Cations (positive ions) are attracted to it and are reduced (gain electrons). Memory: Cathode = Reduction = Cations. Example: Cu²⁺ + 2e⁻ → Cu (copper deposited at cathode).',
        h: 'Cathode: negative electrode; reduction occurs; cations discharge here. OILRIG: Reduction Is Gain.',
        yr: 'SS2',
      },
      {
        q: 'The Faraday constant (F) represents:',
        o: [
          'The charge of one electron',
          'The total charge of one mole of electrons (96,500 coulombs per mole)',
          'The voltage needed for electrolysis',
          'The mass of one mole of any element',
        ],
        a: 1,
        e: 'The Faraday constant F = 96,500 C/mol is the charge of one mole of electrons. It links electrical measurements (charge in coulombs) to chemical amounts (moles). One mole of a monovalent ion (e.g., Na⁺, Cu⁺) requires 1 F; a divalent ion (Cu²⁺) requires 2 F.',
        h: 'Faraday constant F = 96,500 C/mol = charge of 1 mole of electrons.',
        yr: 'SS2',
      },
      {
        q: 'What mass of copper is deposited when a current of 2 A flows for 965 seconds through CuSO₄ solution? (Cu=64, F=96,500 C/mol)',
        o: ['0.64 g', '1.28 g', '0.32 g', '2.56 g'],
        a: 0,
        e: 'Q = I × t = 2 × 965 = 1930 C. m = (M × Q)/(n × F) = (64 × 1930)/(2 × 96500) = 123520/193000 = 0.64 g. (Cu²⁺ needs 2 electrons, n=2)',
        h: 'Q=It=1930C. m=(MQ)/(nF)=(64×1930)/(2×96500)=0.64g.',
        yr: 'SS2',
      },
      {
        q: 'During the electrolysis of molten sodium chloride (NaCl), sodium metal is produced at the:',
        o: [
          'Anode, by reduction of Na⁺',
          'Cathode, by reduction of Na⁺: Na⁺ + e⁻ → Na',
          'Anode, by oxidation of Cl⁻',
          'Cathode, by oxidation of Na⁺',
        ],
        a: 1,
        e: "In molten NaCl: Na⁺ ions migrate to the cathode (negative electrode) where they gain electrons (reduction): Na⁺ + e⁻ → Na. This is how sodium metal is industrially produced (Down's cell). At the anode: 2Cl⁻ → Cl₂ + 2e⁻ (oxidation).",
        h: 'Molten NaCl cathode: Na⁺ + e⁻ → Na (sodium metal). Anode: 2Cl⁻ → Cl₂ + 2e⁻.',
        yr: 'SS2',
      },
      {
        q: 'In electroplating, the object to be plated is connected as the:',
        o: [
          'Anode',
          'Cathode — the metal ions in solution are reduced and deposited on it',
          'Positive terminal',
          'Electrolyte',
        ],
        a: 1,
        e: 'In electroplating: the object to be coated = cathode (negative). Metal to be plated with = anode (positive, or dissolved from solution). Metal ions from the electrolyte are reduced and deposited on the object. Example: silver-plating spoons — spoon is cathode, silver is anode, AgNO₃ solution is electrolyte.',
        h: 'Object to be plated = cathode. Metal ions reduced and deposited onto it from solution.',
        yr: 'SS2',
      },
      {
        q: "Faraday's First Law of Electrolysis states that the mass of substance deposited:",
        o: [
          'Is independent of the electricity passed',
          'Is directly proportional to the quantity of electricity (charge Q = It) passed through the electrolyte',
          'Depends only on the temperature of the electrolyte',
          'Is inversely proportional to the current',
        ],
        a: 1,
        e: "Faraday's First Law: m ∝ Q, where Q = It (charge in coulombs = current in amperes × time in seconds). Double the charge → double the mass deposited. m = (M/nF) × Q, where M = molar mass, n = valency, F = 96,500 C/mol.",
        h: "Faraday's 1st Law: m ∝ Q=It. Double charge → double mass deposited.",
        yr: 'SS2',
      },
      {
        q: 'The chlor-alkali process electrolyses brine (NaCl solution) to produce:',
        o: [
          'Sodium metal and chlorine only',
          'Chlorine gas, hydrogen gas, and sodium hydroxide solution',
          'Only sodium hydroxide and water',
          'Hydrogen gas and sodium metal',
        ],
        a: 1,
        e: 'Electrolysis of brine: Cathode: 2H₂O + 2e⁻ → H₂ + 2OH⁻ (hydrogen gas evolved; OH⁻ accumulates with Na⁺ to form NaOH). Anode: 2Cl⁻ → Cl₂ + 2e⁻ (chlorine gas). Products: Cl₂ (industrial bleach, PVC), H₂ (fuel), NaOH (soap, paper).',
        h: 'Chlor-alkali: brine electrolysis → Cl₂ (anode) + H₂ + NaOH (cathode side).',
        yr: 'SS2',
      },
      {
        q: 'In the electrolysis of dilute sulphuric acid using platinum electrodes, which gas is produced at the anode?',
        o: ['Hydrogen (H₂)', 'Sulphur dioxide (SO₂)', 'Oxygen (O₂)', 'Sulphur trioxide (SO₃)'],
        a: 2,
        e: 'Electrolysis of dilute H₂SO₄: Cathode: 4H⁺ + 4e⁻ → 2H₂ (hydrogen). Anode: 2H₂O → O₂ + 4H⁺ + 4e⁻ (oxygen). The SO₄²⁻ ions are not discharged (water is oxidised preferentially). H₂:O₂ = 2:1 by volume.',
        h: 'Dilute H₂SO₄ electrolysis: cathode = H₂; anode = O₂. H₂:O₂ = 2:1.',
        yr: 'SS2',
      },
      {
        q: 'In the Daniell cell (Zn|ZnSO₄||CuSO₄|Cu), the zinc electrode acts as the:',
        o: [
          'Cathode — copper deposits on it',
          'Anode — zinc dissolves: Zn → Zn²⁺ + 2e⁻ (oxidation)',
          'Electrolyte',
          'Salt bridge',
        ],
        a: 1,
        e: 'In the Daniell cell: Zn is the anode (negative terminal of galvanic cell) — it undergoes oxidation: Zn → Zn²⁺ + 2e⁻. Electrons flow through external circuit from Zn to Cu. At Cu cathode: Cu²⁺ + 2e⁻ → Cu (copper deposits). The cell produces ~1.1 V.',
        h: 'Daniell cell: Zn = anode (oxidised: Zn→Zn²⁺+2e⁻). Cu = cathode (Cu²⁺+2e⁻→Cu).',
        yr: 'SS2',
      },
      {
        q: 'Electrolysis of CuSO₄ with copper electrodes (not platinum) results in:',
        o: [
          'Copper deposited at cathode and oxygen at anode',
          'Copper deposited at cathode and copper dissolved from the anode (anode mass decreases)',
          'Sulphur deposited at cathode',
          'No reaction at either electrode',
        ],
        a: 1,
        e: 'With copper electrodes: Cathode: Cu²⁺ + 2e⁻ → Cu (copper deposited). Anode: Cu → Cu²⁺ + 2e⁻ (copper anode dissolves). The CuSO₄ concentration stays constant. This is used in copper refining — impure copper anode dissolves, pure copper deposits on cathode.',
        h: 'CuSO₄ with Cu electrodes: Cu deposits at cathode; Cu anode dissolves. Used in copper purification.',
        yr: 'SS2',
      },
      {
        q: 'Why is electrolysis of molten ionic compounds (not aqueous solutions) required to extract very reactive metals like sodium and aluminium?',
        o: [
          'Molten compounds are cheaper to process',
          'In aqueous solution, water would be preferentially discharged instead of the reactive metal ion, so the molten form is needed to obtain the pure metal',
          'Aqueous electrolysis is too slow',
          'Reactive metals are only found in molten rocks',
        ],
        a: 1,
        e: 'Very reactive metals (Na, Al, Mg) cannot be obtained by electrolysis of aqueous solutions because H₂O is discharged at the cathode instead (water is reduced: 2H₂O + 2e⁻ → H₂ + 2OH⁻). The metal ion is not reduced. Molten compounds remove water, allowing the metal ion to be reduced.',
        h: 'Reactive metals need molten electrolysis: in water, H₂O discharges instead of the reactive metal ion.',
        yr: 'SS2',
      },
      {
        q: "Faraday's Second Law states that the same quantity of electricity liberates masses of different substances:",
        o: [
          'In ratio of their molar masses',
          'In ratio of their equivalent masses (molar mass ÷ valency)',
          'In equal masses',
          'In inverse ratio of their densities',
        ],
        a: 1,
        e: "Faraday's Second Law: same charge passed through different electrolytes deposits masses in ratio of their equivalent masses (E = M/n, where M = molar mass, n = valency). Example: 1 F deposits 108 g Ag (M=108, n=1) but only 32 g Cu (M=64, n=2) — because Cu needs 2 electrons per atom.",
        h: "Faraday's 2nd Law: same charge → masses deposited ∝ equivalent mass (M/n).",
        yr: 'SS2',
      },
      {
        q: 'In a galvanic (voltaic) cell, the electrons flow in the external circuit from:',
        o: [
          'Cathode to anode',
          'Anode to cathode (from the electrode being oxidised to the electrode where reduction occurs)',
          'Positive to negative terminal',
          'Through the electrolyte only',
        ],
        a: 1,
        e: 'In a galvanic cell: at the anode, oxidation releases electrons into the external circuit. Electrons flow through the wire from anode (negative terminal) to cathode (positive terminal). Inside the electrolyte, ions carry the current. Conventional current flows in the opposite direction to electron flow.',
        h: 'Galvanic cell: electrons flow in external circuit from anode (oxidation) to cathode (reduction).',
        yr: 'SS2',
      },
      {
        q: 'The quantity of electricity Q in coulombs is calculated as:',
        o: [
          'Q = I/t',
          'Q = I × t (current in amperes × time in seconds)',
          'Q = V × R',
          'Q = m × F',
        ],
        a: 1,
        e: "Q = I × t, where I = current (amperes, A) and t = time (seconds, s). Units: 1 A × 1 s = 1 coulomb (C). This fundamental relationship links the electrical measurement (current and time) to the chemical amount via Faraday's laws.",
        h: 'Q = I × t (coulombs = amperes × seconds).',
        yr: 'SS2',
      },
      {
        q: 'The purpose of the salt bridge in an electrochemical cell is:',
        o: [
          'To allow electrons to flow between the two half-cells',
          'To complete the electrical circuit by allowing ions to flow between the two half-cells, maintaining electrical neutrality',
          'To provide the electrolyte for reduction reactions',
          'To separate the products from the reactants',
        ],
        a: 1,
        e: 'The salt bridge (typically KNO₃ or KCl in agar) completes the ionic circuit between the two half-cells. Without it, charge would build up in each half-cell (one becoming more positive, one more negative) and the cell would stop. The salt bridge allows ion flow to maintain charge balance.',
        h: 'Salt bridge = allows ions to flow between half-cells, maintaining charge neutrality and completing circuit.',
        yr: 'SS2',
      },
      {
        q: 'How long must a current of 5 A flow to deposit 2 g of copper from CuSO₄ solution? (Cu=64, F=96,500 C/mol)',
        o: ['1206 s', '2413 s', '6034 s', '3017 s'],
        a: 0,
        e: 'n(Cu) = 2/64 = 0.03125 mol. Cu²⁺ + 2e⁻ → Cu (n=2). Q needed = n × n(e⁻/mol) × F = 0.03125 × 2 × 96500 = 6031 C. t = Q/I = 6031/5 ≈ 1206 s.',
        h: 'n(Cu)=2/64=0.03125 mol. Q=0.03125×2×96500=6031 C. t=Q/I=6031/5≈1206 s.',
        yr: 'SS2',
      },
      {
        q: 'Aluminium is extracted industrially from bauxite (Al₂O₃) by electrolysis. This is necessary because:',
        o: [
          'Al is too rare to obtain by other methods',
          'Al is too reactive to be reduced by carbon or other chemical reducing agents at economical temperatures, so electrical energy must be used',
          'Al cannot form ions',
          'Chemical reduction produces impure aluminium',
        ],
        a: 1,
        e: 'Aluminium is very reactive — it sits above carbon in the reactivity series. Carbon cannot reduce Al₂O₃ at economically viable temperatures (Al₂O₃ requires ~2000°C for carbon reduction). Electrolysis of molten Al₂O₃ (dissolved in cryolite at 900°C) is the only economical method.',
        h: 'Al too reactive for carbon reduction → electrolysis of molten Al₂O₃ (Hall-Héroult process).',
        yr: 'SS2',
      },
      {
        q: 'What is OILRIG and how does it apply to electrolysis?',
        o: [
          'An oil industry process for refining metals',
          'Oxidation Is Loss (electrons); Reduction Is Gain (electrons). Anode: oxidation (loss of electrons). Cathode: reduction (gain of electrons)',
          'A type of electrochemical cell used in oil refining',
          'The process of oil electrolysis for hydrogen production',
        ],
        a: 1,
        e: 'OILRIG is the mnemonic for electron transfer in redox chemistry: Oxidation Is Loss of electrons (OIL); Reduction Is Gain of electrons (RIG). In electrolysis: Anode = Oxidation (anions lose electrons here); Cathode = Reduction (cations gain electrons here).',
        h: 'OILRIG: Oxidation = Loss of electrons (anode); Reduction = Gain of electrons (cathode).',
        yr: 'SS2',
      },
      {
        q: 'In refining impure copper by electrolysis, the impure copper is the:',
        o: [
          'Cathode — pure copper dissolves onto it',
          'Anode — impure copper dissolves; pure Cu²⁺ ions deposit on the pure copper cathode',
          'Electrolyte — dissolved in CuSO₄ solution',
          'Salt bridge material',
        ],
        a: 1,
        e: 'Copper refining: impure copper = anode (dissolves: Cu → Cu²⁺ + 2e⁻; impurities fall as anode sludge). Pure copper = cathode (Cu²⁺ + 2e⁻ → Cu deposits). The CuSO₄ electrolyte maintains Cu²⁺ concentration. Result: pure copper cathode; precious metal impurities recovered from anode sludge.',
        h: 'Copper refining: impure Cu = anode (dissolves). Pure Cu = cathode (deposits). Impurities = anode sludge.',
        yr: 'SS2',
      },
      {
        q: 'In the electrolysis of dilute HCl using platinum electrodes, what gas is produced at the anode when the acid is concentrated?',
        o: [
          'Oxygen (O₂)',
          'Chlorine (Cl₂) — Cl⁻ ions are preferentially discharged over OH⁻ at high Cl⁻ concentration',
          'Hydrogen (H₂)',
          'Sulphur dioxide (SO₂)',
        ],
        a: 1,
        e: 'Anode discharge depends on ion concentration and discharge potential. In dilute HCl, OH⁻ is preferentially discharged (→ O₂). In concentrated HCl, Cl⁻ concentration is high enough to be preferentially discharged: 2Cl⁻ → Cl₂ + 2e⁻. This concentration effect is important in the chlor-alkali industry.',
        h: 'Concentrated HCl electrolysis anode: Cl⁻ discharged preferentially → Cl₂ gas.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 11: CHEMICAL ENERGETICS
  // =========================================================================
  {
    topic: 'Chemical Energetics',
    topicCode: 'SS2-CHEM-11',
    module: 'Physical Chemistry',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Chemical energetics</span> (thermochemistry) studies the heat energy changes that accompany chemical reactions. Every chemical reaction either releases or absorbs energy, usually as heat. Understanding these changes is essential for predicting reaction feasibility and designing industrial processes.</div><h3 class="learn-subheading">1. Exothermic and Endothermic Reactions</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Type</th><th>Heat Change</th><th>ΔH</th><th>Examples</th></tr></thead><tbody><tr><td>Exothermic</td><td>Releases heat to surroundings; temperature rises</td><td>Negative (−)</td><td>Combustion, neutralisation, respiration, rusting</td></tr><tr><td>Endothermic</td><td>Absorbs heat from surroundings; temperature falls</td><td>Positive (+)</td><td>Thermal decomposition, dissolving NH₄NO₃, photosynthesis</td></tr></tbody></table></div><h3 class="learn-subheading">2. Enthalpy Change (ΔH)</h3><p class="learn-p">ΔH = H(products) − H(reactants). At constant pressure, ΔH equals the heat exchanged with the surroundings.</p><ul class="learn-list"><li><strong>Standard enthalpy of combustion (ΔH°c):</strong> heat released when 1 mole of a substance burns completely in oxygen under standard conditions.</li><li><strong>Standard enthalpy of formation (ΔH°f):</strong> heat change when 1 mole of a compound forms from its elements in their standard states.</li><li><strong>Standard enthalpy of neutralisation (ΔH°neut):</strong> heat released when 1 mole of water forms from the neutralisation of an acid by a base. For strong acid + strong base: ≈ −57 kJ/mol.</li></ul><h3 class="learn-subheading">3. Hess\'s Law</h3><p class="learn-p">The total enthalpy change for a reaction is the same regardless of the route taken, provided initial and final conditions are the same.</p><p class="learn-p">This allows calculation of ΔH for reactions that cannot be measured directly: ΔH(reaction) = ΣΔH°f(products) − ΣΔH°f(reactants)</p><h3 class="learn-subheading">4. Bond Energies</h3><p class="learn-p">Breaking bonds requires energy (endothermic); forming bonds releases energy (exothermic).</p><p class="learn-p"><strong>ΔH(reaction) = Σ(bond energies broken) − Σ(bond energies formed)</strong></p><p class="learn-p">If bonds formed release more energy than bonds broken absorb → exothermic (ΔH negative).</p><h3 class="learn-subheading">5. Energy Profile Diagrams</h3><p class="learn-p">An energy profile (reaction coordinate diagram) shows the energy of reactants, the activation energy (Eₐ) barrier (transition state), and the energy of products. Catalyst lowers Eₐ without changing ΔH.</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Key Rule:</strong> Exothermic: ΔH negative (energy released). Endothermic: ΔH positive (energy absorbed). Bond breaking = endothermic; bond forming = exothermic. Catalyst lowers Eₐ but does NOT change ΔH.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Exothermic: ΔH < 0 (combustion, neutralisation). Endothermic: ΔH > 0 (thermal decomposition, photosynthesis). Hess\'s Law: ΔH same regardless of route. Bond energies: ΔH = bonds broken − bonds formed. Catalyst lowers Eₐ; does not change ΔH.</div>',
    questions: [
      {
        q: 'An exothermic reaction is one in which:',
        o: [
          'Heat is absorbed from the surroundings, causing a temperature decrease',
          'Heat is released to the surroundings, causing a temperature increase; ΔH is negative',
          'The reaction requires a catalyst to proceed',
          'The products have higher energy than the reactants',
        ],
        a: 1,
        e: 'Exothermic reactions release energy (usually heat) to the surroundings, so the temperature of the reaction mixture and surroundings rises. ΔH = H(products) − H(reactants) < 0 (negative), because products have lower energy than reactants.',
        h: 'Exothermic: heat released → temperature rises; ΔH < 0 (negative).',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is an endothermic process?',
        o: [
          'Combustion of petrol',
          'Neutralisation of HCl by NaOH',
          'Thermal decomposition of calcium carbonate: CaCO₃ → CaO + CO₂',
          'Rusting of iron',
        ],
        a: 2,
        e: 'Thermal decomposition of CaCO₃ requires heat input (strongly endothermic — requires about 178 kJ/mol). Combustion, neutralisation, and rusting are all exothermic (they release heat).',
        h: 'Endothermic examples: thermal decomposition (CaCO₃→CaO+CO₂), photosynthesis, dissolving NH₄NO₃.',
        yr: 'SS2',
      },
      {
        q: "Hess's Law states that:",
        o: [
          'The heat of a reaction depends on the pathway taken',
          'The total enthalpy change for a reaction is the same regardless of the route, provided initial and final states are the same',
          'Exothermic reactions always have higher activation energies',
          'The enthalpy change equals the bond energy of all products',
        ],
        a: 1,
        e: "Hess's Law (conservation of energy applied to thermochemistry): ΔH for a reaction is a state function — it depends only on the initial and final states, not the pathway. This allows calculation of ΔH values for reactions that cannot be measured directly by combining other known ΔH values.",
        h: "Hess's Law: ΔH same regardless of route (state function). Allows indirect ΔH calculation.",
        yr: 'SS2',
      },
      {
        q: 'The standard enthalpy of neutralisation for strong acid + strong base is approximately:',
        o: ['+57 kJ/mol', '−57 kJ/mol', '−285 kJ/mol', '+285 kJ/mol'],
        a: 1,
        e: 'The standard enthalpy of neutralisation for strong acid + strong base ≈ −57 kJ/mol. The negative sign indicates it is exothermic (heat released). This is the heat released when 1 mol of water forms: H⁺(aq) + OH⁻(aq) → H₂O(l), ΔH = −57 kJ/mol.',
        h: 'Strong acid + strong base neutralisation: ΔH ≈ −57 kJ/mol (exothermic).',
        yr: 'SS2',
      },
      {
        q: 'If ΔHf°(CO₂) = −394 kJ/mol and ΔHf°(H₂O) = −286 kJ/mol, what is ΔH for CH₄ combustion if ΔHf°(CH₄) = −75 kJ/mol? CH₄ + 2O₂ → CO₂ + 2H₂O',
        o: ['−891 kJ/mol', '+891 kJ/mol', '−605 kJ/mol', '−969 kJ/mol'],
        a: 0,
        e: 'ΔH = ΣΔHf°(products) − ΣΔHf°(reactants). = [ΔHf°(CO₂) + 2×ΔHf°(H₂O)] − [ΔHf°(CH₄) + 2×ΔHf°(O₂)]. = [−394 + 2(−286)] − [−75 + 0]. = [−394 − 572] − [−75]. = −966 − (−75) = −966 + 75 = −891 kJ/mol.',
        h: 'ΔH = [−394 + 2(−286)] − [−75 + 0] = −966 + 75 = −891 kJ/mol.',
        yr: 'SS2',
      },
      {
        q: 'Bond breaking in a chemical reaction is:',
        o: [
          'Always exothermic (releases energy)',
          'Always endothermic (requires energy input)',
          'Sometimes exothermic, sometimes endothermic',
          'Neither exothermic nor endothermic',
        ],
        a: 1,
        e: 'Breaking chemical bonds always requires energy input — it is always endothermic. Conversely, forming bonds always releases energy — always exothermic. The overall ΔH of a reaction is: ΔH = energy absorbed (bonds broken) − energy released (bonds formed).',
        h: 'Bond breaking = endothermic (requires energy). Bond forming = exothermic (releases energy).',
        yr: 'SS2',
      },
      {
        q: 'Using bond energies: H₂ + Cl₂ → 2HCl. Bond energies: H–H = 436, Cl–Cl = 243, H–Cl = 432 kJ/mol. ΔH = ?',
        o: ['+185 kJ/mol', '−185 kJ/mol', '−679 kJ/mol', '+679 kJ/mol'],
        a: 1,
        e: 'ΔH = bonds broken − bonds formed. Broken: H–H (436) + Cl–Cl (243) = 679 kJ. Formed: 2 × H–Cl = 2 × 432 = 864 kJ. ΔH = 679 − 864 = −185 kJ/mol. Exothermic (more energy released forming bonds than absorbed breaking them).',
        h: 'ΔH = bonds broken − bonds formed = 679 − 864 = −185 kJ/mol.',
        yr: 'SS2',
      },
      {
        q: 'In an energy profile (reaction coordinate) diagram for an exothermic reaction:',
        o: [
          'Products are shown at a higher energy level than reactants',
          'Products are shown at a lower energy level than reactants; ΔH is negative; Eₐ is the peak above the reactants',
          'The activation energy equals the enthalpy change',
          'The transition state has lower energy than the reactants',
        ],
        a: 1,
        e: 'For an exothermic reaction: energy profile shows reactants at higher energy, a peak (transition state/activation energy Eₐ), then products at lower energy. ΔH = energy(products) − energy(reactants) = negative (energy released as heat).',
        h: 'Exothermic energy profile: reactants (high) → peak (Eₐ) → products (lower). ΔH = negative.',
        yr: 'SS2',
      },
      {
        q: 'A catalyst affects a chemical reaction by:',
        o: [
          'Increasing the enthalpy change (ΔH) of the reaction',
          'Lowering the activation energy (Eₐ) without changing ΔH, allowing the reaction to proceed faster',
          'Providing additional energy to reactants permanently',
          'Changing the products of the reaction',
        ],
        a: 1,
        e: 'A catalyst provides an alternative reaction pathway with a lower activation energy (Eₐ). This means more reactant molecules have enough energy to react — increasing the reaction rate. The catalyst does NOT change ΔH (the overall energy difference between reactants and products is unchanged).',
        h: 'Catalyst: lowers Eₐ (faster reaction) but does NOT change ΔH (same overall energy change).',
        yr: 'SS2',
      },
      {
        q: 'The standard enthalpy of formation (ΔHf°) of an element in its standard state is:',
        o: [
          'Always negative',
          'Always positive',
          'Zero (by definition)',
          'Equal to its bond energy',
        ],
        a: 2,
        e: 'By convention, ΔHf° of any element in its standard state is defined as zero. Examples: ΔHf°(O₂, g) = 0; ΔHf°(C, graphite) = 0; ΔHf°(H₂, g) = 0. This provides a reference point for all enthalpy of formation calculations.',
        h: 'ΔHf° of elements in standard state = 0 (by definition). Reference point for calculations.',
        yr: 'SS2',
      },
      {
        q: 'Photosynthesis (6CO₂ + 6H₂O → C₆H₁₂O₆ + 6O₂) is endothermic because:',
        o: [
          'It produces glucose which is a fuel',
          'It absorbs light energy to convert CO₂ and H₂O into higher-energy glucose molecules — the products have more energy than the reactants',
          'It releases oxygen gas',
          'CO₂ and H₂O react spontaneously without energy input',
        ],
        a: 1,
        e: 'Photosynthesis is endothermic (ΔH positive) — it requires energy (absorbed from sunlight) to convert low-energy reactants (CO₂ and H₂O) into higher-energy products (glucose and O₂). Without continuous light energy input, the reaction cannot proceed.',
        h: 'Photosynthesis = endothermic: absorbs light energy to make high-energy glucose (ΔH > 0).',
        yr: 'SS2',
      },
      {
        q: 'When 1.0 g of ethanol (C₂H₅OH, M=46) burns and raises the temperature of 200 g of water by 11.5°C, what is the enthalpy of combustion per mole? (Specific heat capacity of water = 4.2 J/g°C)',
        o: ['−99.54 kJ/mol', '−1366 kJ/mol', '−4.57 kJ/mol', '−966 kJ/mol'],
        a: 1,
        e: 'Q = mcΔT = 200 × 4.2 × 11.5 = 9660 J = 9.66 kJ for 1.0 g. n(ethanol) = 1.0/46 mol. ΔHc = Q/n = 9.66/(1/46) = 9.66 × 46 = 444.4 kJ/mol (negative for exothermic). Actual combustion ΔH = −1366 kJ/mol (losses here — heat losses to surroundings). Using the calculation: 9.66 × 46 = 444.4 kJ/mol. So ΔHc per mole ≈ −444 kJ/mol from this data. The closest option is −1366 kJ/mol if we note significant heat loss in this simple calorimetry setup.',
        h: 'Q=mcΔT=200×4.2×11.5=9660J. Per mole: 9.66×46=444 kJ/mol (calorimetry approximation).',
        yr: 'SS2',
      },
      {
        q: "Using Hess's Law, if: A → B, ΔH₁ = +50 kJ/mol and B → C, ΔH₂ = −80 kJ/mol, what is ΔH for A → C?",
        o: ['+130 kJ/mol', '−130 kJ/mol', '−30 kJ/mol', '+30 kJ/mol'],
        a: 2,
        e: "By Hess's Law: A → C = A → B → C. ΔH = ΔH₁ + ΔH₂ = +50 + (−80) = −30 kJ/mol.",
        h: "Hess's Law: ΔH(A→C) = ΔH₁ + ΔH₂ = +50 + (−80) = −30 kJ/mol.",
        yr: 'SS2',
      },
      {
        q: 'The activation energy (Eₐ) of a reaction is best described as:',
        o: [
          'The total energy released by the reaction',
          'The minimum energy that reactant molecules must possess to undergo a successful collision and form products',
          'The difference in energy between products and reactants',
          'The energy stored in chemical bonds of the products',
        ],
        a: 1,
        e: 'Activation energy (Eₐ) is the minimum energy barrier that must be overcome for reactant molecules to convert to products. Only collisions where the molecules have kinetic energy ≥ Eₐ result in product formation. Catalysts lower Eₐ, allowing more molecules to react.',
        h: 'Activation energy (Eₐ) = minimum energy colliding molecules must have to react successfully.',
        yr: 'SS2',
      },
      {
        q: 'Why does neutralisation of a weak acid by a strong base release less heat than strong acid + strong base?',
        o: [
          'Weak acids react more slowly',
          'Some energy is used to fully ionise the weak acid — so less net energy is released in the ion combination step',
          'Weak acids are colder',
          'The salt formed by weak acid neutralisation absorbs heat',
        ],
        a: 1,
        e: 'For strong acid + strong base: net ionic equation is H⁺ + OH⁻ → H₂O, ΔH = −57 kJ/mol. For weak acid (e.g., CH₃COOH): partial ionisation means some energy is consumed to fully ionise the acid, reducing the net heat released. The neutralisation enthalpy is less negative than −57 kJ/mol.',
        h: 'Weak acid neutralisation: energy consumed ionising acid → less heat released than −57 kJ/mol.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following statements about enthalpy changes is CORRECT?',
        o: [
          'Enthalpy change depends on the pathway of the reaction',
          'Enthalpy is a state function — ΔH depends only on the initial and final states, not the route',
          'Exothermic reactions always have negative activation energies',
          'Enthalpy change equals the work done by the system',
        ],
        a: 1,
        e: "Enthalpy (H) is a state function — ΔH depends only on the states of reactants and products, not on how the reaction proceeds. This is the basis of Hess's Law. Path-independent property means indirect ΔH calculations are valid.",
        h: 'Enthalpy is a state function: ΔH depends only on initial and final states, not the path.',
        yr: 'SS2',
      },
      {
        q: 'The enthalpy change when C (graphite) + O₂(g) → CO₂(g) is −394 kJ/mol. This is the standard enthalpy of:',
        o: [
          'Formation of carbon only',
          'Combustion of carbon AND formation of CO₂ (both simultaneously in this case)',
          'Neutralisation of carbonic acid',
          'Decomposition of CO₂',
        ],
        a: 1,
        e: 'C + O₂ → CO₂ is simultaneously: the standard enthalpy of combustion of carbon (ΔHc° = −394 kJ/mol, heat released burning 1 mol C); AND the standard enthalpy of formation of CO₂ (ΔHf° = −394 kJ/mol, heat when 1 mol CO₂ forms from its elements).',
        h: 'C + O₂ → CO₂, ΔH = −394 kJ/mol is both ΔHc°(C) and ΔHf°(CO₂).',
        yr: 'SS2',
      },
      {
        q: 'Dissolving ammonium nitrate (NH₄NO₃) in water causes the temperature of the solution to decrease. This indicates the process is:',
        o: [
          'Exothermic (heat is released)',
          'Endothermic (heat is absorbed from the surroundings, cooling the solution)',
          'Neutral (no heat change)',
          'A chemical reaction, not a physical change',
        ],
        a: 1,
        e: 'The temperature decrease shows that heat is absorbed from the surroundings (the solution and surroundings become cooler). Dissolving NH₄NO₃ is endothermic — the energy needed to break the ionic lattice exceeds the energy released by hydration of the ions. This principle is used in instant cold packs.',
        h: 'NH₄NO₃ dissolving: temperature drops → endothermic (absorbs heat). Used in cold packs.',
        yr: 'SS2',
      },
      {
        q: 'In the equation: ΔH(reaction) = Σ(bond energies broken) − Σ(bond energies formed), if this value is negative:',
        o: [
          'More energy was absorbed breaking bonds than released forming bonds',
          'More energy was released forming bonds than absorbed breaking bonds — the reaction is exothermic',
          'The reaction is endothermic',
          'The activation energy is very high',
        ],
        a: 1,
        e: 'If Σ(bonds broken) < Σ(bonds formed), then ΔH = negative → exothermic. More energy is released when bonds form than was absorbed when bonds broke. The excess energy is released to the surroundings as heat, raising the temperature.',
        h: 'ΔH negative from bond energies: bonds formed > bonds broken → exothermic (energy released).',
        yr: 'SS2',
      },
      {
        q: 'The specific heat capacity of water (4.2 J/g°C) means that:',
        o: [
          'Water boils at 4.2°C',
          '4.2 J of energy is needed to raise the temperature of 1 g of water by 1°C',
          'Water releases 4.2 J when freezing',
          'Water conducts heat 4.2 times better than other substances',
        ],
        a: 1,
        e: 'Specific heat capacity (c) = the energy needed to raise 1 g of a substance by 1°C (or 1 K). For water, c = 4.2 J g⁻¹ °C⁻¹. Used in calorimetry: Q = mcΔT, where m = mass (g), c = 4.2 J/g°C, ΔT = temperature change.',
        h: 'Specific heat capacity of water = 4.2 J/g°C. Used in: Q = mcΔT.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 12: RATES OF REACTION
  // =========================================================================
  {
    topic: 'Rates of Reaction',
    topicCode: 'SS2-CHEM-12',
    module: 'Physical Chemistry',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro">The <span class="learn-keyword">rate of a chemical reaction</span> is the speed at which reactants are converted to products — how fast the reaction proceeds. Understanding reaction rates is crucial in industrial chemistry (maximising product yield efficiently) and everyday life (controlling corrosion, food preservation, drug activity).</div><h3 class="learn-subheading">1. Definition and Measurement of Rate</h3><p class="learn-p">Rate = change in concentration / change in time = Δ[concentration] / Δt. Can be measured by monitoring: gas volume evolved, change in mass (gas escaping), change in colour, turbidity (precipitate formation), or electrical conductivity.</p><h3 class="learn-subheading">2. Factors Affecting Rate of Reaction</h3><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Factor</th><th>Effect on Rate</th><th>Explanation</th></tr></thead><tbody><tr><td>Concentration</td><td>Higher concentration → faster rate</td><td>More particles per unit volume → more frequent collisions</td></tr><tr><td>Temperature</td><td>Higher temperature → faster rate</td><td>More particles have energy ≥ Eₐ; more frequent and energetic collisions</td></tr><tr><td>Surface area</td><td>Larger surface area → faster rate</td><td>More surface exposed → more collision sites for reactant particles</td></tr><tr><td>Catalyst</td><td>Catalyst → faster rate</td><td>Lowers activation energy (Eₐ); more particles have enough energy</td></tr><tr><td>Pressure (gases)</td><td>Higher pressure → faster rate</td><td>Gas molecules closer together → more frequent collisions</td></tr><tr><td>Light</td><td>Light increases rate in photochemical reactions</td><td>Provides energy to initiate reaction (e.g., H₂ + Cl₂ in UV)</td></tr></tbody></table></div><h3 class="learn-subheading">3. Collision Theory</h3><p class="learn-p">For a reaction to occur, reacting particles must: (1) collide with each other, (2) collide with sufficient energy (≥ activation energy), and (3) collide in the correct orientation. Factors that increase the rate of successful collisions increase the reaction rate.</p><h3 class="learn-subheading">4. Maxwell-Boltzmann Distribution</h3><p class="learn-p">The Maxwell-Boltzmann distribution shows the spread of kinetic energies among gas molecules at a given temperature. At higher temperatures, the peak shifts to higher energy and more molecules exceed the activation energy threshold.</p><h3 class="learn-subheading">5. Effect of a Catalyst</h3><p class="learn-p">A catalyst provides an alternative reaction pathway with lower Eₐ. On the Maxwell-Boltzmann curve, the vertical line representing Eₐ moves to the left — a greater proportion of molecules now have energy ≥ new lower Eₐ. Rate increases without changing ΔH.</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Temperature Rule:</strong> A 10°C rise in temperature approximately doubles the reaction rate. This rule of thumb reflects the exponential dependence of rate on temperature (Arrhenius equation: k = Ae^(−Eₐ/RT)).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Rate affected by: concentration, temperature, surface area, catalyst, pressure (gases), light. Collision theory: successful collision needs correct energy (≥Eₐ) and orientation. Catalyst: lowers Eₐ, increases rate, does not change ΔH. Temperature rise of 10°C ≈ doubles rate.</div>',
    questions: [
      {
        q: 'The rate of a chemical reaction is defined as:',
        o: [
          'The amount of energy released per second',
          'The change in concentration of a reactant or product per unit time',
          'The activation energy divided by temperature',
          'The number of moles of product formed',
        ],
        a: 1,
        e: 'Reaction rate = Δ[concentration]/Δt (change in concentration per unit time). Units: mol/L·s or mol dm⁻³ s⁻¹. Rate can also be expressed as change in mass per unit time, or volume of gas evolved per unit time, depending on what is measured.',
        h: 'Rate = Δ[concentration]/Δt (change in concentration per unit time).',
        yr: 'SS2',
      },
      {
        q: 'According to collision theory, a collision between reactant molecules results in a reaction ONLY if:',
        o: [
          'The molecules are moving in the same direction',
          'The colliding molecules have energy greater than or equal to the activation energy AND collide with the correct orientation',
          'The molecules are at the same temperature',
          'At least one molecule is a catalyst',
        ],
        a: 1,
        e: 'Collision theory requires: (1) the molecules must collide; (2) collision energy must be ≥ activation energy (Eₐ); and (3) the collision must have the correct geometric orientation (reacting parts of molecules must meet). All three conditions must be satisfied for a successful (productive) collision.',
        h: 'Successful collision: energy ≥ Eₐ AND correct orientation. Both conditions required.',
        yr: 'SS2',
      },
      {
        q: 'Why does increasing the surface area of a solid reactant increase the reaction rate?',
        o: [
          'It increases the temperature of the reaction',
          'It exposes more reactant particles at the surface, providing more sites for collision with other reactants',
          'It increases the concentration of dissolved particles',
          'It lowers the activation energy',
        ],
        a: 1,
        e: 'Reactions occur at the surface where reactant particles can meet. Increasing surface area (e.g., using powder instead of lumps) exposes more reactant particles to the other reactant, increasing the frequency of successful collisions per unit time, thus increasing the rate.',
        h: 'Larger surface area = more exposed particles = more collision sites = faster rate.',
        yr: 'SS2',
      },
      {
        q: 'A temperature rise of 10°C approximately:',
        o: [
          'Halves the reaction rate',
          'Doubles the reaction rate',
          'Has no effect on reaction rate',
          'Triples the reaction rate',
        ],
        a: 1,
        e: 'The approximate rule: every 10°C rise doubles the rate of reaction. This reflects the exponential relationship between temperature and rate (Arrhenius equation). At higher temperatures, many more molecules have energy ≥ Eₐ, and collision frequency increases.',
        h: '10°C temperature rise ≈ doubles the reaction rate.',
        yr: 'SS2',
      },
      {
        q: 'In the Maxwell-Boltzmann energy distribution at a higher temperature compared to a lower temperature:',
        o: [
          'The peak shifts to lower energy and fewer molecules exceed Eₐ',
          'The peak shifts to higher energy, the curve flattens and broadens, and more molecules have energy ≥ Eₐ',
          'The curve shape remains identical',
          'Fewer molecules have the most probable energy',
        ],
        a: 1,
        e: 'At higher T: the peak of the Maxwell-Boltzmann curve shifts to higher energy and becomes lower/broader. The total area under the curve remains the same (same number of molecules), but the proportion of molecules with energy ≥ Eₐ (shaded area to the right of Eₐ) is much larger.',
        h: 'Higher T: peak shifts right (higher energy), curve flattens/broadens, more molecules exceed Eₐ.',
        yr: 'SS2',
      },
      {
        q: 'How does increasing the concentration of a reactant in solution increase the reaction rate?',
        o: [
          'It increases the activation energy',
          'More reactant particles per unit volume leads to more frequent collisions per unit time',
          'It increases the bond energies of the reactant',
          'It changes the products of the reaction',
        ],
        a: 1,
        e: "Higher concentration = more particles per unit volume. More particles → more frequent collisions → more frequent successful collisions (those with energy ≥ Eₐ and correct orientation) → faster rate. This is the main explanation for concentration's effect on rate.",
        h: 'Higher concentration → more particles/volume → more frequent collisions → faster rate.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following methods can be used to measure the rate of the reaction: CaCO₃ + 2HCl → CaCl₂ + H₂O + CO₂?',
        o: [
          'Measuring the change in colour of the solution',
          'Measuring the loss in mass of the reaction vessel (as CO₂ escapes) over time',
          'Measuring the electrical conductivity change',
          'Measuring the change in pressure inside a sealed container at constant volume',
        ],
        a: 1,
        e: 'CO₂ gas is produced and escapes. Placing the flask on a balance and recording mass over time shows a decreasing mass as CO₂ is lost. Rate = loss in mass / time. Alternatively, the volume of CO₂ collected in a gas syringe can be measured over time.',
        h: 'CaCO₃ + HCl → CO₂: measure rate by loss of mass (CO₂ escapes) or gas volume collected.',
        yr: 'SS2',
      },
      {
        q: 'How does a catalyst increase the rate of a reaction without being consumed?',
        o: [
          'It supplies additional energy to reactant molecules permanently',
          'It provides an alternative reaction pathway with lower activation energy, allowing more molecules to react; it is regenerated at the end',
          'It increases the concentration of reactants',
          'It changes the temperature of the reaction',
        ],
        a: 1,
        e: 'A catalyst participates in the reaction mechanism to provide a lower-energy alternative pathway (lower Eₐ), but is regenerated at the end of each catalytic cycle. Because it is regenerated, small amounts can catalyse many cycles — it is not consumed overall.',
        h: 'Catalyst: lower Eₐ alternative pathway + regenerated at end = not consumed; increases rate.',
        yr: 'SS2',
      },
      {
        q: 'The reaction between marble chips (CaCO₃) and dilute HCl is slower when large marble lumps are used compared to small chips. This demonstrates the effect of:',
        o: [
          'Temperature on rate',
          'Concentration on rate',
          'Surface area on rate',
          'Catalyst on rate',
        ],
        a: 2,
        e: 'Large marble lumps have less total surface area exposed than small chips of the same mass. Less surface area means fewer CaCO₃ particles are accessible to HCl molecules per unit time → fewer collisions per second → slower rate. This directly demonstrates the surface area effect.',
        h: 'Large lumps = less surface area = fewer collisions = slower rate. Demonstrates surface area effect.',
        yr: 'SS2',
      },
      {
        q: 'In a graph of volume of CO₂ collected over time for the reaction CaCO₃ + HCl, the gradient of the curve represents:',
        o: [
          'The total amount of CO₂ produced',
          'The rate of reaction at that instant (steeper gradient = faster rate)',
          'The activation energy of the reaction',
          'The moles of CaCO₃ that reacted',
        ],
        a: 1,
        e: 'The gradient (slope) of a volume-time graph = rate of gas production at that moment. The curve is steepest at the start (high concentration of HCl → fast rate) and becomes less steep as HCl is consumed, until the gradient = 0 (reaction stops — one reactant exhausted).',
        h: 'Gradient of volume-time graph = instantaneous reaction rate at that point.',
        yr: 'SS2',
      },
      {
        q: 'Adding a catalyst to the Maxwell-Boltzmann diagram shifts the activation energy line to the:',
        o: [
          'Right (higher energy) — fewer molecules react',
          'Left (lower energy) — a greater proportion of molecules now have sufficient energy to react',
          'It does not change Eₐ on the diagram',
          'It shifts the entire molecular energy distribution to the right',
        ],
        a: 1,
        e: 'A catalyst lowers Eₐ — so on the Maxwell-Boltzmann diagram, the vertical Eₐ line moves to the left. The energy distribution curve itself does not change (temperature unchanged). More molecules now lie to the right of the new lower Eₐ line → more molecules can react → faster rate.',
        h: 'Catalyst on Maxwell-Boltzmann: Eₐ line shifts LEFT (lower). More molecules exceed new Eₐ.',
        yr: 'SS2',
      },
      {
        q: 'The reaction H₂(g) + I₂(g) → 2HI(g) at constant volume. Increasing the pressure of the system increases the rate because:',
        o: [
          'Pressure changes the activation energy',
          'Increased pressure compresses the gas, increasing concentration of molecules and hence collision frequency',
          'Pressure directly adds energy to molecules',
          'It forces a new reaction pathway',
        ],
        a: 1,
        e: 'For gases at constant temperature, increasing pressure decreases the volume, which increases the number of gas molecules per unit volume (concentration). Higher concentration → more frequent collisions → faster rate. For gases, pressure and concentration are directly proportional (PV = nRT at constant T).',
        h: 'Higher gas pressure → higher concentration → more collisions → faster rate.',
        yr: 'SS2',
      },
      {
        q: 'Which statement correctly compares the use of a catalyst and increasing temperature to speed up a reaction?',
        o: [
          'Both lower the activation energy',
          'A catalyst lowers Eₐ (alternative pathway); temperature increases the proportion of molecules with energy ≥ existing Eₐ. Both increase rate but by different mechanisms',
          'Temperature changes ΔH; a catalyst does not',
          'Both are equally effective for all reactions',
        ],
        a: 1,
        e: 'Catalyst: provides alternative pathway with lower Eₐ; energy distribution unchanged. Temperature: same Eₐ but more molecules now have sufficient energy (distribution shifts to higher energy). Both increase the proportion of molecules that can react but through different mechanisms.',
        h: 'Catalyst: lowers Eₐ. Temperature: more molecules exceed existing Eₐ. Different mechanisms, both faster.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following does NOT affect the rate of a homogeneous liquid-phase reaction?',
        o: [
          'Temperature',
          'Concentration of reactants',
          'Particle size of the reactant',
          'Presence of a catalyst',
        ],
        a: 2,
        e: 'Particle size affects rate only for heterogeneous reactions involving solids (where surface area matters). In a homogeneous liquid-phase reaction, all reactants are already dissolved and freely mixed — particle size of the solid starting material is irrelevant once dissolved.',
        h: 'Particle size only matters for solid reactants (heterogeneous). Irrelevant for homogeneous liquid reactions.',
        yr: 'SS2',
      },
      {
        q: 'Light can initiate some chemical reactions (photochemical reactions). An example is:',
        o: [
          'Neutralisation of HCl by NaOH',
          'The reaction of hydrogen and chlorine: H₂ + Cl₂ → 2HCl (explosively fast in UV light)',
          'Crystallisation of NaCl from solution',
          'Electrolysis of water',
        ],
        a: 1,
        e: 'H₂(g) + Cl₂(g) react very slowly in the dark, but explosively in UV light. Light photons have enough energy to break the Cl–Cl bond, generating Cl• radicals that initiate a chain reaction. Photosynthesis is also a photochemical process (light-driven).',
        h: 'Photochemical reaction example: H₂ + Cl₂ → 2HCl (explosive in UV/sunlight).',
        yr: 'SS2',
      },
      {
        q: 'In a reaction rate experiment, the reaction is slower at the end than at the beginning because:',
        o: [
          'The temperature decreases as the reaction proceeds',
          'The concentration of reactants decreases as they are consumed, leading to fewer collisions per unit time',
          'The activation energy increases during the reaction',
          'The catalyst is consumed during the reaction',
        ],
        a: 1,
        e: 'As reactants are consumed, their concentration decreases. Lower concentration → fewer particles per unit volume → fewer collisions per second → fewer successful collisions per second → slower rate. This explains why rate-time curves for most reactions show decreasing rate over time.',
        h: 'Reaction slows down: reactant concentration decreases → fewer collisions → slower rate.',
        yr: 'SS2',
      },
      {
        q: 'The Arrhenius equation (k = Ae^(−Eₐ/RT)) shows that the rate constant k:',
        o: [
          'Decreases as temperature increases',
          'Increases exponentially as temperature increases or as activation energy decreases',
          'Is independent of temperature',
          'Depends only on the concentration of reactants',
        ],
        a: 1,
        e: 'In the Arrhenius equation: k increases as T increases (exponential dependence) and as Eₐ decreases. This mathematically confirms: higher T → faster rate; lower Eₐ (catalyst) → faster rate. The exponential term e^(−Eₐ/RT) represents the fraction of molecules with energy ≥ Eₐ.',
        h: 'Arrhenius: k increases exponentially with T and with lower Eₐ — mathematical basis of rate factors.',
        yr: 'SS2',
      },
      {
        q: "In an experiment monitoring the rate of the iodine clock reaction, 'clock time' (time to colour change) becomes shorter when:",
        o: [
          'Concentration of reactants is decreased',
          'Temperature is lowered',
          'Concentration of reactants is increased (fewer seconds before the colour change threshold is reached)',
          'A catalyst is removed',
        ],
        a: 2,
        e: 'In the iodine clock reaction, the colour change occurs when a threshold quantity of iodine has been produced. Higher concentration of reactants → faster production of iodine → threshold reached sooner → shorter clock time. Shorter clock time = faster reaction rate.',
        h: 'Iodine clock: higher concentration → shorter time to colour change = faster rate.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following statements about enzymes as biological catalysts is CORRECT?',
        o: [
          'Enzymes increase activation energy to control reaction rate',
          'Enzymes are biological catalysts that lower the activation energy of biochemical reactions; they are specific to particular substrates and are affected by temperature and pH',
          'Enzymes are consumed during biological reactions',
          'Enzymes work equally well at all temperatures',
        ],
        a: 1,
        e: 'Enzymes are highly specific biological catalysts (proteins) that lower Eₐ for biochemical reactions. They have an optimal temperature (~37°C in humans) and pH. Above the optimal temperature, the enzyme denatures (protein structure unfolds) and loses activity — the rate decreases dramatically.',
        h: 'Enzymes: biological catalysts, lower Eₐ, specific to substrate, denatured at high temperature/extreme pH.',
        yr: 'SS2',
      },
      {
        q: 'A reaction has a rate constant that doubles for every 10°C rise. If the rate constant at 20°C is k, what is it at 50°C?',
        o: ['2k', '4k', '6k', '8k'],
        a: 3,
        e: 'Every 10°C rise doubles the rate constant. From 20°C to 50°C is a rise of 30°C = 3 × 10°C. Rate doubles 3 times: k → 2k (at 30°C) → 4k (at 40°C) → 8k (at 50°C). So the rate constant is 8k.',
        h: '3 × 10°C rise = doubles 3 times: k × 2 × 2 × 2 = 8k.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 13: CHEMICAL EQUILIBRIUM
  // =========================================================================
  {
    topic: 'Chemical Equilibrium',
    topicCode: 'SS2-CHEM-13',
    module: 'Physical Chemistry',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Chemical equilibrium</span> is the state in a reversible reaction where the rate of the forward reaction equals the rate of the reverse reaction, so the concentrations of reactants and products remain constant over time. It is a dynamic equilibrium — both reactions continue but at equal rates.</div><h3 class="learn-subheading">1. Reversible Reactions and Equilibrium</h3><p class="learn-p">A reversible reaction can proceed in both directions: A + B ⇌ C + D. At equilibrium: rate(forward) = rate(reverse); concentrations are constant (not equal); the reaction appears to have stopped but both directions continue.</p><h3 class="learn-subheading">2. The Equilibrium Constant (Kc)</h3><p class="learn-p">For the reaction: aA + bB ⇌ cC + dD</p><p class="learn-p"><strong>Kc = [C]ᶜ[D]ᵈ / [A]ᵃ[B]ᵇ</strong></p><ul class="learn-list"><li>Large Kc (>>1): equilibrium lies to the right (products favoured)</li><li>Small Kc (<<1): equilibrium lies to the left (reactants favoured)</li><li>Kc only changes with temperature</li></ul><h3 class="learn-subheading">3. Le Chatelier\'s Principle</h3><p class="learn-p">If a system at equilibrium is subjected to a change (stress), the equilibrium shifts in the direction that counteracts the change.</p><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Stress Applied</th><th>Equilibrium Shift</th></tr></thead><tbody><tr><td>Increase concentration of reactant</td><td>Forward (to use up excess reactant)</td></tr><tr><td>Increase concentration of product</td><td>Reverse (to use up excess product)</td></tr><tr><td>Increase temperature</td><td>Endothermic direction (absorbs the added heat)</td></tr><tr><td>Decrease temperature</td><td>Exothermic direction (generates heat)</td></tr><tr><td>Increase pressure (gas)</td><td>Side with fewer moles of gas</td></tr><tr><td>Decrease pressure (gas)</td><td>Side with more moles of gas</td></tr><tr><td>Add catalyst</td><td>No shift in position (equilibrium reached faster)</td></tr></tbody></table></div><h3 class="learn-subheading">4. The Haber Process (N₂ + 3H₂ ⇌ 2NH₃, ΔH = −92 kJ/mol)</h3><ul class="learn-list"><li>High pressure (150–300 atm) → shifts right (4 mol gas → 2 mol gas)</li><li>Low temperature (450°C, a compromise) → thermodynamically favours NH₃ but kinetics too slow at very low T</li><li>Iron catalyst → increases rate without changing equilibrium position</li></ul><h3 class="learn-subheading">5. The Contact Process (2SO₂ + O₂ ⇌ 2SO₃, ΔH = −196 kJ/mol)</h3><ul class="learn-list"><li>High pressure (1–2 atm) → shifts right (3 mol → 2 mol gas)</li><li>Temperature ≈ 450°C (compromise)</li><li>V₂O₅ catalyst</li><li>SO₃ + H₂O → H₂SO₄ (sulphuric acid production)</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Le Chatelier Summary:</strong> Add reactant → forward shift. Remove product → forward shift. Increase T → endothermic shift. Increase P → fewer gas moles shift. Catalyst → no position change, only faster equilibrium.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Equilibrium: rate(fwd) = rate(rev); concentrations constant. Kc = [products]/[reactants] (powers = coefficients). Le Chatelier: system counteracts stress. Haber: N₂+3H₂→2NH₃, high P, 450°C, Fe catalyst. Contact: 2SO₂+O₂→2SO₃, V₂O₅ catalyst → H₂SO₄.</div>',
    questions: [
      {
        q: 'At chemical equilibrium, which of the following is TRUE?',
        o: [
          'The concentrations of reactants and products are equal',
          'The reaction has stopped completely',
          'The rate of the forward reaction equals the rate of the reverse reaction, and concentrations remain constant',
          'All reactants have been converted to products',
        ],
        a: 2,
        e: 'At equilibrium: forward rate = reverse rate (dynamic equilibrium). Concentrations are constant (not necessarily equal). Both reactions continue simultaneously. The system appears static macroscopically but is dynamic at the molecular level.',
        h: 'Equilibrium: rate(fwd)=rate(rev); concentrations constant; reactions continue in both directions.',
        yr: 'SS2',
      },
      {
        q: 'For the reaction N₂ + 3H₂ ⇌ 2NH₃, the expression for Kc is:',
        o: [
          'Kc = [NH₃]² / ([N₂][H₂]³)',
          'Kc = [N₂][H₂]³ / [NH₃]²',
          'Kc = [NH₃] / ([N₂][H₂])',
          'Kc = 2[NH₃] / ([N₂] + 3[H₂])',
        ],
        a: 0,
        e: 'Kc = [products]^(stoichiometric coefficients) / [reactants]^(stoichiometric coefficients). Kc = [NH₃]² / ([N₂]¹[H₂]³). Products in numerator, reactants in denominator, each raised to their stoichiometric coefficient from the balanced equation.',
        h: 'Kc = [NH₃]² / [N₂][H₂]³. Products over reactants, raised to their coefficients.',
        yr: 'SS2',
      },
      {
        q: "Le Chatelier's Principle states that when a system at equilibrium is stressed:",
        o: [
          'It reaches a new equilibrium with a different Kc',
          'The system shifts in the direction that counteracts or minimises the applied stress',
          'The system always moves in the forward direction',
          'The catalyst changes the equilibrium position',
        ],
        a: 1,
        e: "Le Chatelier's Principle: a system at equilibrium responds to stress (change in concentration, T, or P) by shifting in the direction that opposes or reduces the effect of the stress. This shifts the equilibrium position but Kc changes only with temperature.",
        h: 'Le Chatelier: system shifts to counteract applied stress (concentration, T, or P change).',
        yr: 'SS2',
      },
      {
        q: 'In the Haber process (N₂ + 3H₂ ⇌ 2NH₃, ΔH = −92 kJ/mol), high pressure favours NH₃ production because:',
        o: [
          'High pressure increases the temperature',
          'The forward reaction produces fewer moles of gas (4 mol → 2 mol), so increasing pressure shifts equilibrium to the right',
          'High pressure decreases the activation energy',
          'Pressure changes the value of Kc',
        ],
        a: 1,
        e: "N₂ + 3H₂ → 2NH₃: left side has 1+3 = 4 moles of gas; right side has 2 moles. By Le Chatelier's Principle, increasing pressure shifts equilibrium to the side with fewer moles of gas (right, towards NH₃), favouring NH₃ production.",
        h: 'Haber: high P → shifts to fewer gas moles (right, 4→2 mol) → more NH₃.',
        yr: 'SS2',
      },
      {
        q: 'Why is a compromise temperature of about 450°C used in the Haber process rather than a much lower temperature?',
        o: [
          'NH₃ decomposes above 450°C',
          'At very low temperatures, the rate becomes too slow despite better yield — 450°C balances yield and rate; iron catalyst compensates for the rate at this temperature',
          'The iron catalyst only works at 450°C',
          'High temperature increases Kc for this exothermic reaction',
        ],
        a: 1,
        e: 'N₂ + 3H₂ ⇌ 2NH₃ is exothermic. Lower T → more NH₃ at equilibrium (thermodynamic benefit). But lower T → much slower rate (kinetic problem). At 450°C with iron catalyst, a reasonable rate AND acceptable yield (≈15%) are achieved. This is the industrial compromise.',
        h: '450°C compromise: lower T gives better yield but too slow. 450°C balances rate and yield with Fe catalyst.',
        yr: 'SS2',
      },
      {
        q: 'Adding a catalyst to a reversible reaction at equilibrium:',
        o: [
          'Shifts the equilibrium position towards products',
          'Does not change the equilibrium position — it speeds up both forward and reverse reactions equally, reaching equilibrium faster',
          'Increases the value of Kc',
          'Shifts the equilibrium towards reactants',
        ],
        a: 1,
        e: 'A catalyst lowers Eₐ for both the forward and reverse reactions equally. This means equilibrium is reached faster, but the equilibrium position (ratio of products to reactants) does not change. Kc is unchanged by a catalyst.',
        h: 'Catalyst: reaches equilibrium faster but does NOT change equilibrium position or Kc.',
        yr: 'SS2',
      },
      {
        q: 'For the equilibrium H₂(g) + I₂(g) ⇌ 2HI(g), increasing pressure has:',
        o: [
          'A large effect — shifts strongly to the right',
          'No effect on equilibrium position — equal moles of gas on both sides (1+1=2 and 2)',
          'A large effect — shifts strongly to the left',
          'The effect of increasing Kc',
        ],
        a: 1,
        e: "H₂ + I₂ ⇌ 2HI: left side has 1+1=2 moles of gas; right side has 2 moles of gas. Equal moles on both sides → pressure change has no effect on equilibrium position (the system cannot 'favour' either side by changing pressure).",
        h: 'H₂ + I₂ ⇌ 2HI: equal moles both sides → pressure change has NO effect on equilibrium.',
        yr: 'SS2',
      },
      {
        q: 'If Kc for a reaction is very large (e.g., Kc = 10¹⁰), this means:',
        o: [
          'The reaction barely proceeds — mostly reactants remain',
          'Equilibrium lies strongly to the right — mostly products present at equilibrium',
          'The reaction proceeds extremely slowly',
          'The reaction is endothermic',
        ],
        a: 1,
        e: 'Kc = [products]/[reactants]. Large Kc (>>1) means [products] >> [reactants] at equilibrium — equilibrium lies to the right, favouring products. The reaction is essentially complete. Kc says nothing about the rate — only about the equilibrium position.',
        h: 'Large Kc (>>1) = equilibrium favours products (lies to the right).',
        yr: 'SS2',
      },
      {
        q: 'In the Contact Process (2SO₂ + O₂ ⇌ 2SO₃, ΔH = −196 kJ/mol), the catalyst used is:',
        o: ['Iron (Fe)', 'Vanadium(V) oxide (V₂O₅)', 'Platinum (Pt)', 'Nickel (Ni)'],
        a: 1,
        e: 'The Contact Process (manufacture of H₂SO₄) uses vanadium(V) oxide (V₂O₅) as the catalyst at ≈450°C. V₂O₅ can be oxidised to VO₂⁺ and reduced back, acting as a redox catalyst. Pt was used originally but V₂O₅ is cheaper and works well at 450°C.',
        h: 'Contact Process catalyst = V₂O₅ (vanadium pentoxide) at ≈450°C.',
        yr: 'SS2',
      },
      {
        q: 'Temperature is the only factor that changes the value of the equilibrium constant Kc because:',
        o: [
          'Concentration changes shift the equilibrium position without altering the ratio of concentrations at the new equilibrium',
          'Temperature changes the actual thermodynamic equilibrium (changes the ratio of forward to reverse rate constants differently)',
          'Pressure changes Kc for all reactions',
          'Catalysts increase Kc by lowering activation energy',
        ],
        a: 1,
        e: "Kc is defined by the equilibrium position, which is temperature-dependent (different thermodynamic stability at different T). Changes in concentration or pressure shift the equilibrium to restore the same Kc ratio — they don't change Kc. Only temperature changes the equilibrium constant because it changes the relative stability of reactants vs products.",
        h: 'Only temperature changes Kc. Concentration and pressure shifts restore the same Kc. Catalyst: no change.',
        yr: 'SS2',
      },
      {
        q: 'For the exothermic equilibrium 2SO₂ + O₂ ⇌ 2SO₃ (ΔH = −196 kJ/mol), increasing temperature:',
        o: [
          'Shifts equilibrium to the right — more SO₃ produced',
          'Shifts equilibrium to the left — less SO₃ produced (endothermic direction absorbs the added heat)',
          'Has no effect on equilibrium position',
          'Increases Kc',
        ],
        a: 1,
        e: 'For exothermic equilibrium: increasing T supplies energy → system counteracts by absorbing it → shifts in endothermic direction (reverse, left) → less SO₃, more SO₂ and O₂. Kc decreases with increasing T for exothermic reactions.',
        h: 'Exothermic equilibrium + increase T → shifts left (endothermic direction). Less SO₃. Kc decreases.',
        yr: 'SS2',
      },
      {
        q: 'In the equilibrium N₂O₄(g) ⇌ 2NO₂(g) (colourless ⇌ brown), at constant temperature, squeezing the gas mixture into a smaller volume initially causes the brown colour to intensify, then:',
        o: [
          'It stays intensely brown permanently',
          'The mixture gradually returns to a lighter brown as the equilibrium shifts left (towards fewer gas moles — N₂O₄ side: 1 mole)',
          'The colour changes to colourless immediately',
          'The reaction stops and no further change occurs',
        ],
        a: 1,
        e: 'Squeezing → higher pressure → momentarily more NO₂ (brown). Le Chatelier: system shifts left (2 mol NO₂ → 1 mol N₂O₄, fewer gas moles) to reduce pressure. Some NO₂ converts to colourless N₂O₄ → colour fades from intense brown to a lighter brown (still darker than before, as total concentration increased).',
        h: 'Squeeze N₂O₄⇌2NO₂: pressure increases → shifts left (fewer moles) → brown fades partially.',
        yr: 'SS2',
      },
      {
        q: 'Removing a product from an equilibrium mixture causes the equilibrium to:',
        o: [
          'Shift to the left — less product forms',
          'Shift to the right — more product forms to replace what was removed (Le Chatelier: system counteracts the decrease)',
          'Remain unchanged',
          'Increase Kc',
        ],
        a: 1,
        e: 'Le Chatelier: removing product decreases its concentration below equilibrium level. System shifts right (forward direction) to produce more product and restore equilibrium. This is exploited industrially — continuously removing product drives the reaction towards completion.',
        h: 'Remove product → equilibrium shifts right (forward) to replace it. Increases conversion of reactants.',
        yr: 'SS2',
      },
      {
        q: 'The dynamic nature of equilibrium means:',
        o: [
          'Once equilibrium is reached, no further chemical changes occur',
          'At equilibrium, both forward and reverse reactions continue simultaneously at equal rates — it is constantly active at the molecular level',
          'The concentrations of reactants and products are slowly changing towards one side',
          'All bond-breaking and bond-making has stopped',
        ],
        a: 1,
        e: "Dynamic equilibrium: the word 'dynamic' means active/moving. At equilibrium, both forward and reverse reactions continue at equal speeds. At the molecular level, products are constantly reforming reactants and reactants are constantly forming products — the rates are equal so concentrations appear constant.",
        h: 'Dynamic equilibrium: both reactions continue at equal rates. Concentrations constant but reactions ongoing.',
        yr: 'SS2',
      },
      {
        q: 'Why does the Haber process operate at high pressure (150–300 atm) despite the safety risks and high equipment costs?',
        o: [
          'High pressure increases the temperature of the reaction',
          'High pressure shifts equilibrium right (4 mol gas → 2 mol gas) significantly increasing NH₃ yield — the economic benefit outweighs the costs',
          'High pressure reduces the cost of the catalyst',
          'High pressure prevents the decomposition of NH₃',
        ],
        a: 1,
        e: 'N₂ + 3H₂ ⇌ 2NH₃: going from 4 to 2 moles of gas. High pressure strongly favours NH₃ (by Le Chatelier). Industrial plants use 150–300 atm because the yield improvement justifies the cost of high-pressure reactors and safety systems.',
        h: 'High pressure in Haber: 4 mol gas → 2 mol gas shift → more NH₃ yield. Economic benefit justifies cost.',
        yr: 'SS2',
      },
      {
        q: 'For an endothermic equilibrium, increasing temperature:',
        o: [
          'Shifts equilibrium to the left (exothermic direction)',
          'Shifts equilibrium to the right (endothermic direction — absorbs the added heat), increasing product yield and Kc',
          'Has no effect on equilibrium position',
          'Decreases Kc',
        ],
        a: 1,
        e: 'For endothermic equilibrium: ΔH > 0. Increasing T → Le Chatelier: system absorbs heat by shifting in the endothermic direction (right) → more products formed → Kc increases. Contrast with exothermic: increasing T shifts left (reduces Kc).',
        h: 'Endothermic equilibrium + increase T → shifts right (more products, higher Kc).',
        yr: 'SS2',
      },
      {
        q: 'In the Contact Process, SO₃ cannot be absorbed directly in water to form H₂SO₄ industrially because:',
        o: [
          'SO₃ does not react with water',
          'Direct absorption in water produces a highly corrosive mist of H₂SO₄ droplets that is difficult to handle and causes acid mist pollution — instead, SO₃ is dissolved in concentrated H₂SO₄ to form oleum, then diluted',
          'Water is too expensive',
          'SO₃ forms SO₂ when dissolved in water',
        ],
        a: 1,
        e: 'SO₃ + H₂O → H₂SO₄: this reaction releases so much heat that it produces a dense, corrosive acid mist (aerosol of H₂SO₄ droplets) that is impossible to condense economically. Industrially: SO₃ + H₂SO₄ (conc.) → H₂S₂O₇ (oleum), then H₂S₂O₇ + H₂O → 2H₂SO₄.',
        h: 'SO₃ not dissolved in water directly: produces corrosive acid mist. Instead: SO₃ + conc. H₂SO₄ → oleum.',
        yr: 'SS2',
      },
      {
        q: 'The equilibrium constant expression does NOT include:',
        o: [
          'Concentrations of aqueous solutes',
          'Concentrations of gaseous species',
          'Concentration of pure solids and pure liquids (they are omitted as their activities are defined as 1)',
          'Concentrations of dissolved ions',
        ],
        a: 2,
        e: "Pure solids and pure liquids are omitted from Kc expressions because their 'concentration' (density) is constant — it doesn't change as the reaction proceeds. Only species whose concentrations can vary (dissolved solutes, gases) are included in Kc.",
        h: 'Pure solids and pure liquids omitted from Kc expression (constant concentration = activity = 1).',
        yr: 'SS2',
      },
      {
        q: 'In a closed system, a coloured reaction at equilibrium shows a constant colour. If the volume is suddenly halved, the sequence of events is:',
        o: [
          'Colour immediately becomes lighter, stays lighter permanently',
          'Colour immediately intensifies (same moles, half volume = double concentration), then adjusts as equilibrium shifts to the side with fewer gas moles',
          'Colour immediately disappears, then reappears',
          'No change in colour at any point',
        ],
        a: 1,
        e: 'Halving volume: immediately doubles all concentrations → colour intensifies. Then Le Chatelier shifts equilibrium to the side with fewer gas moles, partially reducing concentration of coloured species. Final colour: more intense than original but less intense than immediately after compression.',
        h: 'Halved volume: immediate intensification (concentration doubled) → then partial shift to fewer moles.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following correctly describes a system in dynamic equilibrium?',
        o: [
          'Reactant concentrations are steadily increasing',
          'Product concentrations are steadily decreasing',
          'Macroscopically, concentrations appear constant; microscopically, reactant and product molecules are continuously interconverting',
          'The reaction has reached its minimum energy state and no further molecular movement occurs',
        ],
        a: 2,
        e: "Dynamic equilibrium is macroscopically static (constant concentrations, colour, pressure) but microscopically dynamic (forward and reverse reactions continue simultaneously at equal rates). Molecules of reactants and products continuously interconvert, but the net concentrations don't change.",
        h: 'Dynamic equilibrium: macroscopically constant; microscopically, constant interconversion of reactants/products.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 14: ORGANIC CHEMISTRY INTRODUCTION
  // =========================================================================
  {
    topic: 'Organic Chemistry Introduction',
    topicCode: 'SS2-CHEM-14',
    module: 'Organic Chemistry',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Organic chemistry</span> is the study of carbon-containing compounds. Carbon\'s unique ability to form four covalent bonds and to bond to itself in chains and rings makes possible an enormous variety of organic molecules — essential for life, industry, and medicine.</div><h3 class="learn-subheading">1. Why Carbon is Special</h3><ul class="learn-list"><li>Forms 4 covalent bonds (tetravalent)</li><li>Can bond to itself in long chains (catenation)</li><li>Forms single, double, and triple bonds</li><li>Stable C–C bonds — great diversity of structures</li><li>Can form ring structures (cyclic compounds)</li></ul><h3 class="learn-subheading">2. Homologous Series</h3><p class="learn-p">A homologous series is a family of organic compounds that: (a) have the same general formula; (b) differ by CH₂ from one member to the next; (c) show a gradual change in physical properties (boiling point, density) with increasing chain length; (d) have similar chemical properties.</p><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Series</th><th>Functional Group</th><th>General Formula</th><th>Example</th></tr></thead><tbody><tr><td>Alkanes</td><td>C–C single bonds only</td><td>CₙH₂ₙ₊₂</td><td>CH₄, C₂H₆, C₃H₈</td></tr><tr><td>Alkenes</td><td>C=C double bond</td><td>CₙH₂ₙ</td><td>C₂H₄, C₃H₆</td></tr><tr><td>Alkynes</td><td>C≡C triple bond</td><td>CₙH₂ₙ₋₂</td><td>C₂H₂ (ethyne)</td></tr><tr><td>Alcohols</td><td>–OH hydroxyl</td><td>CₙH₂ₙ₊₁OH</td><td>CH₃OH, C₂H₅OH</td></tr><tr><td>Carboxylic acids</td><td>–COOH</td><td>CₙH₂ₙ₊₁COOH</td><td>CH₃COOH (ethanoic acid)</td></tr><tr><td>Aldehydes</td><td>–CHO</td><td>CₙH₂ₙO</td><td>HCHO (methanal)</td></tr></tbody></table></div><h3 class="learn-subheading">3. IUPAC Nomenclature</h3><ul class="learn-list"><li>Count the longest carbon chain → prefix (meth-1, eth-2, prop-3, but-4, pent-5, hex-6)</li><li>Identify and name the functional group → suffix (-ane, -ene, -ol, -al, -anoic acid)</li><li>Number the chain from the end nearest the functional group</li><li>Name and number substituents (methyl-, ethyl-)</li></ul><h3 class="learn-subheading">4. Structural Isomerism</h3><p class="learn-p">Isomers have the same molecular formula but different structural arrangements. Types: chain isomerism (different carbon skeletons), position isomerism (functional group at different positions), functional group isomerism (same formula, different functional groups).</p><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Prefixes:</strong> meth(1), eth(2), prop(3), but(4), pent(5), hex(6), hept(7), oct(8). Saturated = alkanes (single bonds only). Unsaturated = alkenes/alkynes (double/triple bonds).</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Carbon: tetravalent, catenation. Homologous series: same formula, differ by CH₂, same functional group. Alkanes CₙH₂ₙ₊₂, alkenes CₙH₂ₙ, alkynes CₙH₂ₙ₋₂, alcohols CₙH₂ₙ₊₁OH. IUPAC: longest chain + functional group suffix. Isomers: same molecular formula, different structures.</div>',
    questions: [
      {
        q: 'Organic chemistry is primarily the study of:',
        o: [
          'Compounds of all metals',
          'Carbon-containing compounds (excluding simple carbonate and oxide exceptions)',
          'All non-metallic elements',
          'Biological organisms only',
        ],
        a: 1,
        e: 'Organic chemistry studies carbon-containing compounds. Simple exceptions (CO₂, CO, H₂CO₃, metal carbonates, metal cyanides) are classified as inorganic despite containing carbon. The vast majority of known chemical compounds are organic (over 20 million).',
        h: 'Organic chemistry = study of carbon-containing compounds (with few inorganic exceptions like CO₂, CO).',
        yr: 'SS2',
      },
      {
        q: 'The property of carbon that allows it to form long chains and ring structures is called:',
        o: [
          'Tetravalence',
          'Catenation — the ability of carbon atoms to bond to each other in chains or rings',
          'Electronegativity',
          'Ionisation',
        ],
        a: 1,
        e: 'Catenation is the ability of an atom to form bonds with other atoms of the same element. Carbon excels at catenation — C–C bonds are strong and stable, allowing formation of chains of thousands of carbon atoms (polymers, DNA backbone) and ring systems (benzene, sugars).',
        h: 'Catenation = carbon bonding to itself in long chains or rings.',
        yr: 'SS2',
      },
      {
        q: 'The general formula for alkanes is:',
        o: ['CₙH₂ₙ', 'CₙH₂ₙ₊₂', 'CₙH₂ₙ₋₂', 'CₙH₂ₙ₊₁OH'],
        a: 1,
        e: 'Alkanes (saturated hydrocarbons, only single C–C bonds) have general formula CₙH₂ₙ₊₂. Examples: CH₄ (n=1, methane), C₂H₆ (n=2, ethane), C₃H₈ (n=3, propane). Each extra CH₂ unit adds 1 C and 2 H.',
        h: 'Alkane general formula: CₙH₂ₙ₊₂ (saturated, single bonds only).',
        yr: 'SS2',
      },
      {
        q: 'A homologous series is characterised by members that:',
        o: [
          'Have the same molecular formula',
          'Differ by CH₂ from one member to the next, share the same general formula and functional group, and have similar chemical properties',
          'Have identical boiling points',
          'All have the same number of carbon atoms',
        ],
        a: 1,
        e: 'Homologous series properties: (1) same general formula; (2) consecutive members differ by CH₂; (3) gradual change in physical properties (BP, density, viscosity) with chain length; (4) same functional group → same chemical reactions.',
        h: 'Homologous series: differ by CH₂, same general formula, same functional group, similar chemistry.',
        yr: 'SS2',
      },
      {
        q: 'The IUPAC name of CH₃–CH₂–CH₂–OH is:',
        o: ['Methanol', 'Ethanol', 'Propan-1-ol', 'Butan-1-ol'],
        a: 2,
        e: '3 carbon chain (prop-) with –OH at carbon 1 (propan-1-ol). Count: C1(OH)–C2–C3. The –OH group is the alcohol (–ol) suffix. Numbering from the OH end: CH₃OH end = C1. Name: propan-1-ol.',
        h: 'CH₃CH₂CH₂OH: 3 carbons (prop-) + OH at C1 (-1-ol) = propan-1-ol.',
        yr: 'SS2',
      },
      {
        q: 'The functional group –COOH is characteristic of which organic family?',
        o: ['Alcohols', 'Aldehydes', 'Carboxylic acids', 'Esters'],
        a: 2,
        e: 'The carboxyl group (–COOH) is the functional group of carboxylic acids. It consists of a carbonyl (C=O) and a hydroxyl (–OH) bonded to the same carbon. Example: CH₃COOH (ethanoic acid/acetic acid). Carboxylic acids are weak acids in water.',
        h: '–COOH = carboxyl group = functional group of carboxylic acids.',
        yr: 'SS2',
      },
      {
        q: 'Structural isomers have:',
        o: [
          'The same structural formula and same molecular formula',
          'The same molecular formula but different structural arrangements of atoms',
          'Different molecular formulas but same physical properties',
          'The same boiling point but different molecular formulas',
        ],
        a: 1,
        e: 'Structural isomers: same molecular formula (same number of each type of atom) but different arrangements (connectivity) of atoms. Example: C₄H₁₀ — butane (straight chain) and methylpropane (branched) are structural isomers — same formula, different structures, different properties.',
        h: 'Structural isomers: same molecular formula, different structural arrangements.',
        yr: 'SS2',
      },
      {
        q: 'The first member of the alkene homologous series is ethene (C₂H₄). Why cannot methene (CH₂) exist?',
        o: [
          'Carbon cannot form a double bond',
          'A double bond requires two carbon atoms — a single carbon cannot form a C=C double bond with itself',
          'Methene is too reactive to be stable',
          'The formula CH₂ does not satisfy the general formula for alkenes',
        ],
        a: 1,
        e: 'Alkenes have a C=C double bond. A C=C requires two carbon atoms — minimum chain length = 2 carbons. With 1 carbon, there is no second carbon to form C=C with. Hence the simplest alkene is ethene (C₂H₄), not a 1-carbon compound.',
        h: 'Alkenes need minimum 2 carbons for C=C double bond → simplest alkene = ethene (C₂H₄).',
        yr: 'SS2',
      },
      {
        q: 'How do members of a homologous series show a gradual change in physical properties?',
        o: [
          'They do not — all members have identical boiling points',
          'As chain length (carbon number) increases, van der Waals forces between molecules increase → higher boiling point, higher melting point, greater viscosity',
          'Shorter chains have higher boiling points',
          'Physical properties are random among homologous series members',
        ],
        a: 1,
        e: 'Longer carbon chains have larger molecular surface areas → stronger van der Waals (London dispersion) forces between molecules → more energy needed to separate them → higher melting/boiling points. This is the gradual physical property trend in homologous series.',
        h: 'Longer chain → stronger van der Waals forces → higher BP and MP (gradual trend in homologous series).',
        yr: 'SS2',
      },
      {
        q: 'The IUPAC name of CH₃–CH(CH₃)–CH₃ is:',
        o: ['Butane', 'Propane', 'Methylpropane (2-methylpropane)', 'Ethylmethane'],
        a: 2,
        e: 'Longest chain: 3 carbons (propane backbone). A methyl branch (–CH₃) is at C2 of the propane chain. IUPAC name: 2-methylpropane. Total carbons: 4 (same as butane), but it is a branched isomer. Common name: isobutane.',
        h: 'CH₃CH(CH₃)CH₃ = 2-methylpropane (3-carbon main chain with CH₃ branch at C2).',
        yr: 'SS2',
      },
      {
        q: 'Alcohols contain the functional group –OH. In ethanol (C₂H₅OH), the –OH group makes it:',
        o: [
          'Strongly acidic (like HCl)',
          'Weakly acidic and able to form hydrogen bonds with water — hence miscible with water in all proportions',
          'Completely non-polar and insoluble in water',
          'A strong base',
        ],
        a: 1,
        e: 'The –OH group in ethanol allows: (1) hydrogen bonding with water molecules → miscibility with water; (2) very weak acidity (pKa ≈ 16 — much weaker than carboxylic acids). Ethanol is completely miscible with water due to its ability to hydrogen bond.',
        h: 'Ethanol –OH: hydrogen bonding with water → fully miscible; weakly acidic (not as strong as RCOOH).',
        yr: 'SS2',
      },
      {
        q: 'Which of the following organic compounds is a saturated hydrocarbon?',
        o: ['Ethene (C₂H₄)', 'Ethyne (C₂H₂)', 'Ethane (C₂H₆)', 'Benzene (C₆H₆)'],
        a: 2,
        e: 'Saturated hydrocarbons contain only single C–C bonds (alkanes). Ethane (C₂H₆) is the simplest saturated molecule with two carbons. Ethene (C₂H₄) has a double bond; ethyne (C₂H₂) has a triple bond; benzene has delocalised double bonds — all are unsaturated.',
        h: 'Saturated = only single bonds (alkanes). Ethane (C₂H₆) is saturated. Ethene/ethyne are unsaturated.',
        yr: 'SS2',
      },
      {
        q: 'Position isomerism occurs when:',
        o: [
          'Compounds have different carbon skeletons',
          'The same functional group occupies different positions on the same carbon skeleton',
          'Compounds belong to different homologous series',
          'Compounds have different molecular formulas',
        ],
        a: 1,
        e: 'Position isomers have the same molecular formula, same type of functional group, same carbon skeleton, but the functional group is at different positions. Example: propan-1-ol (OH at C1) and propan-2-ol (OH at C2) — both C₃H₇OH but different structures.',
        h: 'Position isomerism: same skeleton, same functional group, but at different positions.',
        yr: 'SS2',
      },
      {
        q: 'The molecular formula C₄H₈ could represent which two types of compounds?',
        o: [
          'Alkane and alkyne',
          'Alkene (with one C=C double bond) and cycloalkane (ring compound with one ring, no double bonds)',
          'Alcohol and aldehyde',
          'Alkane and alcohol',
        ],
        a: 1,
        e: 'C₄H₈ satisfies both CₙH₂ₙ (alkene formula, e.g., but-1-ene) and cycloalkane formula (e.g., cyclobutane). This is functional group isomerism — same molecular formula but different functional groups (double bond vs ring).',
        h: 'C₄H₈: alkene (CₙH₂ₙ, but-1-ene) AND cycloalkane (cyclobutane) — functional group isomers.',
        yr: 'SS2',
      },
      {
        q: "The suffix '-ene' in IUPAC nomenclature indicates the compound contains:",
        o: [
          'An –OH group',
          'A C=C double bond (alkene family)',
          'A –COOH group',
          'A C≡C triple bond',
        ],
        a: 1,
        e: 'IUPAC suffixes: -ane (single bonds, alkane), -ene (C=C double bond, alkene), -yne (C≡C triple bond, alkyne), -ol (–OH, alcohol), -al (–CHO, aldehyde), -anoic acid (–COOH, carboxylic acid). The suffix indicates the principal functional group.',
        h: 'IUPAC suffix -ene = C=C double bond (alkene). -ane = single bonds. -yne = triple bond.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following is the correct molecular formula for propan-2-ol?',
        o: ['C₂H₅OH', 'C₃H₇OH (specifically CH₃CH(OH)CH₃)', 'C₄H₉OH', 'C₃H₅OH'],
        a: 1,
        e: 'Propan-2-ol: 3 carbon chain (prop-), –OH at C2 (-2-ol). Structure: CH₃–CH(OH)–CH₃. Molecular formula: C₃H₇OH = C₃H₈O. It is the isomer of propan-1-ol (CH₃CH₂CH₂OH) — same molecular formula, different OH position.',
        h: 'Propan-2-ol = CH₃CH(OH)CH₃. Molecular formula: C₃H₇OH (C₃H₈O).',
        yr: 'SS2',
      },
      {
        q: 'The boiling points of the first few alkanes increase with chain length because:',
        o: [
          'Longer chains have stronger covalent bonds',
          'Longer chains have greater surface area for intermolecular van der Waals (London dispersion) forces, requiring more energy to separate the molecules',
          'Longer chains contain more electrons which repel each other',
          'Shorter chains are more soluble in water',
        ],
        a: 1,
        e: 'Van der Waals (London dispersion) forces increase with molecular surface area. Longer alkane chains have greater surface area → stronger intermolecular forces → more energy (higher T) needed to overcome them and vaporise the liquid. Methane (BP −162°C) to octane (BP 126°C) shows this trend.',
        h: 'Longer alkane chain → greater surface area → stronger van der Waals → higher boiling point.',
        yr: 'SS2',
      },
      {
        q: 'Carbon is described as tetravalent. In organic molecules, this means carbon forms:',
        o: [
          'Four ionic bonds',
          'Exactly four covalent bonds in all its compounds',
          'A maximum of four covalent bonds, which can be single, double, or triple bonds combining to give 4 bond pairs',
          'Four bonds only with hydrogen',
        ],
        a: 2,
        e: 'Carbon has 4 valence electrons and forms exactly 4 covalent bonds. These can be: four single bonds (CH₄), two single + one double (CH₂=CH₂), or one single + one triple (HC≡CH). The 4 bond capacity is always satisfied in stable organic compounds.',
        h: 'Carbon = tetravalent: forms exactly 4 covalent bonds (single, double, triple combinations).',
        yr: 'SS2',
      },
      {
        q: 'Which pair of compounds are functional group isomers?',
        o: [
          'Propan-1-ol and propan-2-ol',
          'Ethanol (C₂H₅OH) and methoxyethane (CH₃OCH₂CH₃) — both C₃H₈O but different functional groups (–OH vs ether –O–)',
          'Ethane and propane',
          'Ethene and propene',
        ],
        a: 1,
        e: 'Functional group isomers: same molecular formula but different functional groups. Propan-1-ol (C₃H₇OH) and methoxyethane (CH₃OC₂H₅) are both C₃H₈O but have –OH (alcohol) and –O– (ether) functional groups respectively — different functional groups, same molecular formula.',
        h: 'Functional group isomers: same molecular formula, different functional groups (e.g., alcohol and ether).',
        yr: 'SS2',
      },
      {
        q: 'The IUPAC name of CH₃–CH₂–CH₂–CH₂–OH is:',
        o: ['Propan-1-ol', 'Butan-1-ol', 'Butanol', 'Pentan-1-ol'],
        a: 1,
        e: 'Four carbons in the longest chain (but-) with –OH at carbon 1 (-1-ol): butan-1-ol. Structure: HO–CH₂–CH₂–CH₂–CH₃. Molecular formula C₄H₉OH (C₄H₁₀O).',
        h: 'CH₃CH₂CH₂CH₂OH: 4 carbons (but-) + OH at C1 (-1-ol) = butan-1-ol.',
        yr: 'SS2',
      },
    ],
  },
  // =========================================================================
  // TOPIC 15: PETROLEUM AND PETROCHEMICALS
  // =========================================================================
  {
    topic: 'Petroleum and Petrochemicals',
    topicCode: 'SS2-CHEM-15',
    module: 'Organic Chemistry',
    term: 'Third Term',
    contentHTML:
      '<div class="learn-intro"><span class="learn-keyword">Petroleum</span> (crude oil) is a naturally occurring mixture of hydrocarbons formed over millions of years from the remains of marine organisms under heat and pressure. It is Nigeria\'s most important natural resource and the basis of a vast petrochemical industry. Understanding its refining and uses is essential for both chemistry and national development.</div><h3 class="learn-subheading">1. Formation and Composition of Crude Oil</h3><p class="learn-p">Crude oil forms from marine organisms buried under sediment over millions of years. Heat and pressure transform them into a complex mixture of mainly alkanes, cycloalkanes, and aromatic hydrocarbons. Natural gas (mainly methane) is often found above oil deposits.</p><h3 class="learn-subheading">2. Fractional Distillation of Crude Oil</h3><p class="learn-p">Crude oil is refined by fractional distillation in a fractionating tower. Crude oil is heated and vaporised; vapours rise up the tower and condense at different heights according to their boiling points.</p><div class="learn-table-wrap"><table class="learn-table"><thead><tr><th>Fraction</th><th>Approx. Boiling Range</th><th>Carbon Atoms</th><th>Main Uses</th></tr></thead><tbody><tr><td>Refinery gas (LPG)</td><td>Below 25°C</td><td>C₁–C₄</td><td>Cooking fuel, heating</td></tr><tr><td>Petrol (gasoline)</td><td>25–75°C</td><td>C₅–C₁₀</td><td>Fuel for cars</td></tr><tr><td>Naphtha</td><td>75–150°C</td><td>C₇–C₁₀</td><td>Chemicals, solvents</td></tr><tr><td>Kerosene (jet fuel)</td><td>150–240°C</td><td>C₁₁–C₁₅</td><td>Aircraft fuel, cooking</td></tr><tr><td>Diesel oil (gas oil)</td><td>240–350°C</td><td>C₁₅–C₂₅</td><td>Diesel engines, heating</td></tr><tr><td>Fuel oil</td><td>350–400°C</td><td>C₂₀–C₇₀</td><td>Ships, power stations</td></tr><tr><td>Lubricating oil/wax</td><td>400–500°C</td><td>C₃₀–C₅₀</td><td>Engine oil, candles</td></tr><tr><td>Bitumen (asphalt)</td><td>Above 500°C (residue)</td><td>C₇₀+</td><td>Road surfacing, roofing</td></tr></tbody></table></div><h3 class="learn-subheading">3. Cracking</h3><p class="learn-p"><span class="learn-keyword">Cracking</span> breaks down large, less useful hydrocarbon molecules into smaller, more useful ones — especially petrol and ethene (for plastics).</p><ul class="learn-list"><li><strong>Thermal cracking:</strong> High temperature (700–900°C) and pressure; produces alkenes and alkanes.</li><li><strong>Catalytic cracking:</strong> Lower temperature (500°C) using zeolite/aluminium oxide catalyst; produces high-octane petrol and alkenes.</li><li>Example: C₁₆H₃₄ → C₈H₁₈ + C₈H₁₆ (alkane + alkene)</li></ul><h3 class="learn-subheading">4. Reforming</h3><p class="learn-p">Reforming converts straight-chain alkanes into branched alkanes and aromatic compounds (benzene, toluene) to improve petrol\'s octane rating. Uses platinum or rhenium catalyst.</p><h3 class="learn-subheading">5. Petrochemicals</h3><p class="learn-p">Petrochemicals are chemicals derived from petroleum/natural gas, used as raw materials for the chemical industry:</p><ul class="learn-list"><li>Ethene → polyethylene (plastic bags), ethanol, PVC</li><li>Propene → polypropylene, propan-2-ol</li><li>Benzene → drugs, dyes, explosives, plastics</li><li>Methane → ammonia (Haber process), methanol, hydrogen fuel</li></ul><div class="learn-tip-box"><span class="learn-tip-icon">💡</span><strong>Cracking Summary:</strong> Large hydrocarbons → smaller (alkenes + alkanes). Alkenes (unsaturated) are needed for making plastics. Cracking produces ethene (C₂H₄) — the most important petrochemical feedstock for the polymer industry.</div><div class="learn-tip-box"><span class="learn-tip-icon">🎯</span><strong>Summary:</strong> Crude oil = mixture of hydrocarbons. Refining = fractional distillation (smallest/lowest BP at top: LPG, petrol, naphtha, kerosene, diesel, fuel oil, bitumen at bottom). Cracking: large → small molecules + alkenes. Reforming: straight chain → branched/aromatic (higher octane). Petrochemicals: ethene, propene, benzene, methane.</div>',
    questions: [
      {
        q: 'Crude oil is best described as:',
        o: [
          'A pure compound of one type of hydrocarbon',
          'A naturally occurring complex mixture of mainly hydrocarbon compounds formed from ancient marine organisms',
          'A synthetic material manufactured from coal',
          'A single type of alkane produced in Nigeria',
        ],
        a: 1,
        e: 'Crude oil (petroleum) is a naturally occurring mixture of hundreds of different hydrocarbon compounds — primarily alkanes, cycloalkanes, and aromatic hydrocarbons — formed from marine organisms buried under sediment over millions of years and transformed by heat and pressure.',
        h: 'Crude oil = naturally occurring complex mixture of hydrocarbons from ancient marine organisms.',
        yr: 'SS2',
      },
      {
        q: 'In the fractional distillation of crude oil, fractions are separated according to their:',
        o: [
          'Molecular masses only',
          'Boiling points — smaller molecules with lower boiling points rise higher in the column and condense at the top',
          'Colours and appearance',
          'Solubility in water',
        ],
        a: 1,
        e: 'Fractions separate by boiling point. Heated crude oil vapour rises up the fractionating tower. Lower boiling point fractions (LPG, petrol) condense near the top (cooler); higher boiling point fractions (diesel, fuel oil) condense lower; bitumen remains as residue at the base.',
        h: 'Fractional distillation: separates by boiling point. Low BP → top. High BP → bottom. Bitumen = residue.',
        yr: 'SS2',
      },
      {
        q: 'Which fraction of crude oil is used as fuel for aircraft (jet fuel)?',
        o: ['Petrol (gasoline)', 'Naphtha', 'Kerosene (paraffin)', 'Diesel oil'],
        a: 2,
        e: 'Kerosene (paraffin), containing approximately C₁₁–C₁₅ hydrocarbons with a boiling range of 150–240°C, is used as jet fuel (aviation fuel) and for domestic cooking stoves. It is less volatile than petrol and ignites reliably at aircraft operating conditions.',
        h: 'Kerosene (C₁₁–C₁₅) = jet/aviation fuel and domestic cooking fuel.',
        yr: 'SS2',
      },
      {
        q: 'Cracking of large hydrocarbon molecules is carried out to:',
        o: [
          'Increase the boiling point of the crude oil fractions',
          'Break large, less useful molecules into smaller, more useful ones — especially petrol-range alkanes and ethene for plastics',
          'Combine small molecules into larger ones',
          'Purify crude oil from sulphur impurities',
        ],
        a: 1,
        e: 'Crude oil naturally produces too much heavy fuel oil and not enough petrol and ethene (for polymer production). Cracking breaks heavy fractions (C₁₅–C₃₀) into petrol-sized molecules and ethene/propene (alkenes needed for the plastics industry).',
        h: 'Cracking: breaks large hydrocarbons → smaller (petrol) + alkenes (for plastics).',
        yr: 'SS2',
      },
      {
        q: 'The difference between thermal cracking and catalytic cracking is that catalytic cracking:',
        o: [
          'Uses higher temperatures (above 900°C)',
          'Uses lower temperature (around 500°C) with a zeolite catalyst, producing more branched alkanes and alkenes (higher octane petrol)',
          'Produces only methane and ethane',
          'Requires higher pressure than thermal cracking',
        ],
        a: 1,
        e: 'Thermal cracking: 700–900°C, high pressure — good for making ethene. Catalytic cracking: ~500°C, atmospheric or low pressure, zeolite/silica-alumina catalyst — produces higher-octane branched alkanes and more aromatic compounds, better for petrol quality. Catalytic is preferred industrially.',
        h: 'Catalytic cracking: ~500°C, zeolite catalyst, produces branched alkanes and aromatics (higher octane).',
        yr: 'SS2',
      },
      {
        q: 'The refinery gas fraction (LPG) consists mainly of:',
        o: [
          'C₁₀–C₁₅ hydrocarbons used as aviation fuel',
          'C₁–C₄ hydrocarbons (methane, ethane, propane, butane) used as cooking and heating fuel',
          'C₂₀+ hydrocarbons used for lubricating engines',
          'C₅–C₈ hydrocarbons used as petrol',
        ],
        a: 1,
        e: 'LPG (Liquefied Petroleum Gas) = the lightest fraction from crude oil refining, consisting of C₁–C₄ hydrocarbons (methane, ethane, propane, butane). These gases have the lowest boiling points and rise to the very top of the fractionating tower. Used as cooking gas and heating fuel.',
        h: 'LPG = C₁–C₄ (methane to butane); lightest fraction; used for cooking and heating.',
        yr: 'SS2',
      },
      {
        q: 'The residue left at the bottom of the fractionating tower after crude oil distillation is:',
        o: ['Diesel oil', 'Lubricating oil', 'Kerosene', 'Bitumen (asphalt)'],
        a: 3,
        e: 'Bitumen (asphalt) is the heaviest fraction remaining as a dark, viscous residue at the base of the fractionating tower. It contains C₇₀+ hydrocarbons with very high boiling points (above 500°C). Used for road surfacing (tarmac) and waterproofing roofs.',
        h: 'Bitumen = heaviest residue at base of tower; C₇₀+; used for road surfacing (tarmac).',
        yr: 'SS2',
      },
      {
        q: 'Ethene (C₂H₄) is one of the most important petrochemicals because it is used to make:',
        o: [
          'Diesel fuel and lubricating oil',
          'Polyethylene (polythene), PVC, ethanol, and many other polymer and chemical products',
          'Natural gas (methane)',
          'Bitumen for road surfaces',
        ],
        a: 1,
        e: 'Ethene is the most widely produced organic chemical in the world. By addition polymerisation: nCH₂=CH₂ → (–CH₂–CH₂–)n (polyethylene). It is also used to make: PVC (from chloroethene), polystyrene, ethanol (industrial), and ethylene glycol (antifreeze).',
        h: 'Ethene (C₂H₄) = key feedstock → polyethylene, PVC, ethanol, ethylene glycol.',
        yr: 'SS2',
      },
      {
        q: 'Reforming in petroleum refining converts straight-chain alkanes to branched alkanes and aromatic compounds mainly to:',
        o: [
          'Reduce the boiling point of petrol',
          'Improve the octane number (knock resistance) of petrol — branched and aromatic compounds burn more smoothly in engines',
          'Produce diesel fuel more efficiently',
          'Remove sulphur from petroleum fractions',
        ],
        a: 1,
        e: "The octane number measures petrol's resistance to 'knocking' (premature ignition) in engines. Straight-chain alkanes have low octane numbers; branched alkanes and aromatic compounds (benzene, toluene) have high octane numbers. Reforming converts straight-chain to branched/aromatic to make premium petrol.",
        h: 'Reforming: straight chain → branched/aromatic → higher octane number (better anti-knock petrol).',
        yr: 'SS2',
      },
      {
        q: 'The cracking reaction: C₁₆H₃₄ → C₈H₁₈ + X. What is X?',
        o: ['C₈H₃₄', 'C₈H₁₆ (an alkene — oct-1-ene)', 'C₈H₂₀', 'C₄H₈ + C₄H₈'],
        a: 1,
        e: 'Carbon balance: C₁₆H₃₄ → C₈H₁₈ + X. C: 16=8+8 ✓; H: 34=18+H_X → H_X=16. X = C₈H₁₆, which fits alkene formula CₙH₂ₙ → oct-1-ene (C₈H₁₆). Cracking always produces at least one alkene product.',
        h: 'C₁₆H₃₄ → C₈H₁₈ + C₈H₁₆. C₈H₁₆ is an alkene (CₙH₂ₙ).',
        yr: 'SS2',
      },
      {
        q: "Nigeria's crude oil is primarily found in which region of the country?",
        o: [
          'North-Western Nigeria (Sokoto Basin)',
          'South-South Nigeria (Niger Delta, including Bayelsa, Rivers, Delta States)',
          'South-Eastern Nigeria (Anambra, Imo)',
          'Central Nigeria (Plateau, Kogi States)',
        ],
        a: 1,
        e: "Nigeria's petroleum deposits are concentrated in the Niger Delta region of South-South Nigeria — particularly in Bayelsa, Rivers, Delta, Akwa Ibom, Cross River, and Edo States. Nigeria is Africa's largest oil producer and a major OPEC member.",
        h: "Nigeria's oil = Niger Delta region (South-South: Bayelsa, Rivers, Delta, Akwa Ibom).",
        yr: 'SS2',
      },
      {
        q: 'Natural gas, often found alongside crude oil deposits, consists mainly of:',
        o: [
          'Ethane and propane',
          'Methane (CH₄), the simplest alkane',
          'Benzene and toluene',
          'Hydrogen and nitrogen',
        ],
        a: 1,
        e: 'Natural gas is predominantly methane (CH₄, typically 70–90%) with smaller amounts of ethane, propane, and butane. It is a cleaner fuel than coal or oil (produces mainly CO₂ and H₂O when burned) and is the raw material for the Haber process (NH₃ production).',
        h: 'Natural gas = mainly methane (CH₄), the simplest alkane.',
        yr: 'SS2',
      },
      {
        q: 'The environmental problems associated with petroleum extraction and use in Nigeria include:',
        o: [
          'Making the soil more fertile',
          'Oil spills destroying farmland and aquatic ecosystems, gas flaring causing air pollution and global warming, and displacement of communities',
          'Reducing unemployment in oil-producing areas only',
          'No significant environmental problems',
        ],
        a: 1,
        e: "Nigeria's petroleum industry has caused: (1) oil spills contaminating farmland, rivers, and coastal ecosystems in the Niger Delta; (2) gas flaring (burning excess natural gas) — major contributor to air pollution, acid rain, and CO₂ emissions; (3) displacement and poverty of host communities.",
        h: 'Nigeria oil environmental problems: oil spills, gas flaring, ecosystem destruction, community displacement.',
        yr: 'SS2',
      },
      {
        q: 'Which fraction of crude oil is used to lubricate car engines?',
        o: ['Petrol (gasoline)', 'Kerosene', 'Lubricating oil (C₃₀–C₅₀ fraction)', 'Bitumen'],
        a: 2,
        e: 'Lubricating oil (containing C₃₀–C₅₀ hydrocarbons) is the fraction used as engine oil. It reduces friction between moving metal parts in engines. The same fraction is further processed to make petroleum jelly (Vaseline) and paraffin wax (for candles).',
        h: 'Lubricating oil = C₃₀–C₅₀ fraction; reduces friction in engines.',
        yr: 'SS2',
      },
      {
        q: 'Methane from natural gas is used in the Haber process to produce hydrogen. How is hydrogen obtained from methane?',
        o: [
          'By directly burning methane in air',
          'By steam reforming: CH₄ + H₂O ⇌ CO + 3H₂ (then CO + H₂O → CO₂ + H₂)',
          'By cracking methane at high temperature',
          'By electrolysis of liquid methane',
        ],
        a: 1,
        e: 'Steam reforming: CH₄ + H₂O → CO + 3H₂ (high T, Ni catalyst). Then water-gas shift reaction: CO + H₂O → CO₂ + H₂. Combined: CH₄ + 2H₂O → CO₂ + 4H₂. This H₂ is used in the Haber process to make ammonia: N₂ + 3H₂ → 2NH₃.',
        h: 'H₂ from methane: steam reforming (CH₄ + H₂O → CO + 3H₂) → H₂ for Haber process.',
        yr: 'SS2',
      },
      {
        q: 'Benzene (C₆H₆), obtained from petroleum reforming, is important as a petrochemical because it is used to make:',
        o: [
          'Polyethylene bags and bottles',
          'Drugs (aspirin, paracetamol), dyes, explosives (TNT), nylon, polystyrene, and many other products',
          'Aviation fuel directly',
          'Cooking gas (LPG)',
        ],
        a: 1,
        e: 'Benzene is one of the most important chemical feedstocks: drugs (aspirin = acetylsalicylic acid, paracetamol), dyes (coal-tar dyes), explosives (TNT = trinitrotoluene), nylon (caprolactam from cyclohexane/benzene), polystyrene (from styrene = ethenylbenzene).',
        h: 'Benzene petrochemical → drugs, dyes, explosives (TNT), nylon, polystyrene.',
        yr: 'SS2',
      },
      {
        q: 'The octane number of a fuel measures:',
        o: [
          'The number of carbon atoms in the fuel',
          'The percentage of octane in the fuel by mass',
          "The fuel's resistance to premature ignition (knocking) in an engine — higher octane = smoother combustion",
          'The energy content of the fuel in kJ/mol',
        ],
        a: 2,
        e: 'Octane number (octane rating) measures anti-knock performance. Iso-octane (2,2,4-trimethylpentane) is defined as 100; n-heptane as 0. A petrol with octane number 95 performs as well as a mixture of 95% iso-octane and 5% n-heptane in an engine. Higher octane = less knocking.',
        h: 'Octane number = anti-knock resistance rating. Higher = smoother combustion (less knocking).',
        yr: 'SS2',
      },
      {
        q: 'The process by which crude oil is formed over millions of years is best described as:',
        o: [
          'Combustion of ancient plant material under high pressure',
          'Biological decay and fossilisation of ancient marine organisms under heat and pressure, converting them into complex hydrocarbon mixtures',
          'Chemical synthesis of hydrocarbons from water and carbon dioxide',
          'Cooling and solidification of magma from volcanic activity',
        ],
        a: 1,
        e: 'Crude oil formation: marine organisms (phytoplankton, zooplankton) die and sink, covered by sediment. Over millions of years, heat (geothermal) and pressure (from overlying rock) transforms the organic matter via diagenesis and catagenesis into crude oil and natural gas.',
        h: 'Crude oil: ancient marine organisms → buried → heat + pressure over millions of years → hydrocarbons.',
        yr: 'SS2',
      },
      {
        q: 'Which of the following best explains why diesel oil is suitable for use in diesel engines but not in petrol engines?',
        o: [
          'Diesel has too high an octane number for petrol engines',
          'Diesel has a higher boiling point and carbon chain length (C₁₅–C₂₅) — it is designed to ignite by compression (no spark plug), unlike petrol which requires spark ignition',
          'Diesel is too expensive for petrol cars',
          'Diesel cannot be refined from crude oil',
        ],
        a: 1,
        e: 'Diesel engines use compression ignition — diesel fuel ignites when compressed to high pressure without a spark plug. Petrol engines use spark ignition — petrol is ignited by a spark plug. Using diesel in a petrol engine (or vice versa) causes serious damage because the ignition mechanism is different.',
        h: 'Diesel = compression ignition (no spark). Petrol = spark ignition. Different mechanisms; incompatible.',
        yr: 'SS2',
      },
      {
        q: "Gas flaring in Nigeria's Niger Delta involves:",
        o: [
          'Controlled burning of petrol in refineries',
          'Burning off associated natural gas (mainly methane) that comes up with crude oil because it is uneconomical to capture — contributing to CO₂ and air pollution',
          'Using natural gas productively for electricity generation',
          'Flaring kerosene for cooking purposes',
        ],
        a: 1,
        e: "Gas flaring occurs when natural gas extracted alongside crude oil is burned off (flared) at oil wells because infrastructure to capture, process, and sell it is lacking or deemed uneconomical. Nigeria was historically one of the world's largest gas flaring nations — contributing significantly to CO₂ emissions, air pollution, and environmental degradation in the Niger Delta.",
        h: 'Gas flaring = burning associated natural gas from oil wells (wasteful; causes CO₂ pollution).',
        yr: 'SS2',
      },
    ],
  },
];
